/* PDFCommunity - PDF core (v68), shared by the page and the background worker.
   The build (build.mjs) puts this file in front of app.js, and also into
   pc-worker.js, so the page and the worker run exactly the same code.
   It may only use what exists in both places: no document/window, no DOM
   canvas (pcNewCanvas gives an OffscreenCanvas in the worker). The few
   things it needs from its host are defined by app.js on the page and by
   pc-worker.js in the worker: pcT, pcUserError, pcCheckCancel, pcIsCancel,
   pcLogUnexpected. */
/* (v150) Polish plural forms in a message: "{0#plik|pliki|plików}" is the word for the number in
   {0}: one | few | many by Intl.PluralRules('pl') (1 plik, 2 pliki, 5 plików, 12 plików, 22 pliki).
   Only Polish texts use this form; any other text comes back unchanged. pcFill (app.js) and pcT
   (pc-worker.js) run it before filling in {0}, {1}, ... */
const PC_PLURAL_RE = /\{(\d+)#([^{}|]*)\|([^{}|]*)\|([^{}|]*)\}/g;
let pcPluralRules = null;
function pcPluralForms(tpl, values){
  if(tpl.indexOf('#') < 0) return tpl;
  return tpl.replace(PC_PLURAL_RE, (m, i, one, few, many) => {
    const n = Number(String(values[+i]).replace(/[\s\u00a0\u202f]/g, ''));
    if(!pcPluralRules) pcPluralRules = new Intl.PluralRules('pl');
    const form = Number.isFinite(n) ? pcPluralRules.select(n) : 'many';
    return form === 'one' ? one : form === 'few' ? few : many;
  });
}
/* ---------- small crypto kit (MD5, RC4, AES via WebCrypto, SHA-2) ---------- */
function pcMd5(data){
  const K = new Int32Array(64), S = [7,12,17,22,7,12,17,22,7,12,17,22,7,12,17,22,5,9,14,20,5,9,14,20,5,9,14,20,5,9,14,20,4,11,16,23,4,11,16,23,4,11,16,23,4,11,16,23,6,10,15,21,6,10,15,21,6,10,15,21,6,10,15,21];
  for(let i = 0; i < 64; i++) K[i] = Math.floor(Math.abs(Math.sin(i + 1)) * 4294967296) | 0;
  const len = data.length, padLen = ((len + 72) >>> 6) << 6;
  const buf = new Uint8Array(padLen);
  buf.set(data); buf[len] = 0x80;
  const bits = len * 8;
  for(let i = 0; i < 4; i++) buf[padLen - 8 + i] = (bits >>> (8 * i)) & 0xff;
  const hi = Math.floor(len / 0x20000000);
  for(let i = 0; i < 4; i++) buf[padLen - 4 + i] = (hi >>> (8 * i)) & 0xff;
  let a0 = 0x67452301, b0 = 0xefcdab89 | 0, c0 = 0x98badcfe | 0, d0 = 0x10325476;
  const M = new Int32Array(16);
  for(let off = 0; off < padLen; off += 64){
    for(let j = 0; j < 16; j++) M[j] = buf[off+j*4] | (buf[off+j*4+1] << 8) | (buf[off+j*4+2] << 16) | (buf[off+j*4+3] << 24);
    let A = a0, B = b0, C = c0, D = d0;
    for(let i = 0; i < 64; i++){
      let F, g;
      if(i < 16){ F = (B & C) | (~B & D); g = i; }
      else if(i < 32){ F = (D & B) | (~D & C); g = (5 * i + 1) % 16; }
      else if(i < 48){ F = B ^ C ^ D; g = (3 * i + 5) % 16; }
      else { F = C ^ (B | ~D); g = (7 * i) % 16; }
      const tmp = D; D = C; C = B;
      const x = (A + F + K[i] + M[g]) | 0;
      B = (B + ((x << S[i]) | (x >>> (32 - S[i])))) | 0;
      A = tmp;
    }
    a0 = (a0 + A) | 0; b0 = (b0 + B) | 0; c0 = (c0 + C) | 0; d0 = (d0 + D) | 0;
  }
  const out = new Uint8Array(16);
  [a0, b0, c0, d0].forEach((v, i) => { for(let k = 0; k < 4; k++) out[i*4+k] = (v >>> (8 * k)) & 0xff; });
  return out;
}
function pcRc4(key, data){
  const s = new Uint8Array(256);
  for(let i = 0; i < 256; i++) s[i] = i;
  for(let i = 0, j = 0; i < 256; i++){ j = (j + s[i] + key[i % key.length]) & 255; const t = s[i]; s[i] = s[j]; s[j] = t; }
  const out = new Uint8Array(data.length);
  for(let k = 0, i = 0, j = 0; k < data.length; k++){
    i = (i + 1) & 255; j = (j + s[i]) & 255;
    const t = s[i]; s[i] = s[j]; s[j] = t;
    out[k] = data[k] ^ s[(s[i] + s[j]) & 255];
  }
  return out;
}
function pcConcat(){
  let n = 0; for(const a of arguments) n += a.length;
  const out = new Uint8Array(n); let o = 0;
  for(const a of arguments){ out.set(a, o); o += a.length; }
  return out;
}
const pcSubtle = (typeof crypto !== 'undefined' && crypto.subtle) ? crypto.subtle : null;
async function pcAesKey(key, usage){ return pcSubtle.importKey('raw', key, { name: 'AES-CBC' }, false, [usage]); }
async function pcAesEncryptNoPad(key, iv, data){
  const k = await pcAesKey(key, 'encrypt');
  const out = new Uint8Array(await pcSubtle.encrypt({ name: 'AES-CBC', iv }, k, data));
  return out.subarray(0, data.length);
}
/* AES-CBC decryption with PKCS#7 padding; when the padding is invalid (some
   producers pad differently) the data is decrypted without padding removal. */
async function pcAesDecrypt(key, iv, data, noPad){
  if(!data.length) return new Uint8Array(0);
  if(data.length % 16) data = data.subarray(0, data.length - (data.length % 16));
  if(!data.length) return new Uint8Array(0);
  const kd = await pcAesKey(key, 'decrypt');
  if(!noPad){
    try{ return new Uint8Array(await pcSubtle.decrypt({ name: 'AES-CBC', iv }, kd, data)); }catch(e){ /* fall through */ }
  }
  // Append one extra block that decrypts to a full padding block, so
  // WebCrypto's padding check always passes and the real data comes back whole.
  const last = data.subarray(data.length - 16);
  const ke = await pcAesKey(key, 'encrypt');
  const pad = new Uint8Array(16).fill(16);
  const extra = new Uint8Array(await pcSubtle.encrypt({ name: 'AES-CBC', iv: last }, ke, pad)).subarray(0, 16);
  return new Uint8Array(await pcSubtle.decrypt({ name: 'AES-CBC', iv }, kd, pcConcat(data, extra)));
}
async function pcSha(bits, data){ return new Uint8Array(await pcSubtle.digest('SHA-' + bits, data)); }

const PC_PAD = new Uint8Array([0x28,0xBF,0x4E,0x5E,0x4E,0x75,0x8A,0x41,0x64,0x00,0x4E,0x56,0xFF,0xFA,0x01,0x08,0x2E,0x2E,0x00,0xB6,0xD0,0x68,0x3E,0x80,0x2F,0x0C,0xA9,0xFE,0x64,0x53,0x69,0x7A]);
function pcPasswordError(){ const e = new Error('password protected'); e.name = 'PasswordException'; return e; }
function pcBytesOf(o){
  const { PDFString, PDFHexString } = PDFLib;
  if(o instanceof PDFString || o instanceof PDFHexString) return o.asBytes();
  return new Uint8Array(0);
}
function pcEq(a, b, n){ for(let i = 0; i < n; i++) if(a[i] !== b[i]) return false; return true; }
/* ISO 32000-2 algorithm 2.B (R6).
   (v60) The loop stops after round N (counted from 1) when N >= 64 and the
   last byte of E <= N - 32. Up to v59 the round was counted from 0, one too
   low: for some passwords (the empty one of many AES-256 files with only an
   owner password among them) the hash came out wrong and the file was
   refused as "password-protected". */
const PC_R6_MIN_ROUNDS = 64, PC_R6_ROUND_OFFSET = 32, PC_R6_REPEAT = 64;
async function pcHashR6(pw, salt, udata){
  let K = await pcSha(256, pcConcat(pw, salt, udata));
  for(let round = 1; ; round++){
    const one = pcConcat(pw, K, udata);
    const K1 = new Uint8Array(one.length * PC_R6_REPEAT);
    for(let r = 0; r < PC_R6_REPEAT; r++) K1.set(one, r * one.length);
    const E = await pcAesEncryptNoPad(K.subarray(0, 16), K.subarray(16, 32), K1);
    let sum = 0; for(let j = 0; j < 16; j++) sum += E[j];
    const h = [256, 384, 512][sum % 3];
    K = await pcSha(h, E);
    if(round >= PC_R6_MIN_ROUNDS && E[E.length - 1] <= round - PC_R6_ROUND_OFFSET) break;
  }
  return K.subarray(0, 32);
}
/* (v60) A typed password as bytes. R5/R6 use UTF-8 (after NFKC, the core of
   the SASLprep step the standard asks for), at most 127 bytes; R2-R4 use
   PDFDocEncoding (ISO 32000-2 annex D), at most 32 bytes: Latin-1 plus the
   typographic characters below (0x80-0xA0). A character it lacks cannot be
   part of an R2-R4 password; it becomes "?", so such a password simply
   doesn't match. */
const PC_PW_MAX_R6 = 127, PC_PW_MAX_R4 = 32;
const PC_V4_KEY_BITS = 128; // key length of a V4 file whose /Encrypt has no /Length
const PC_PDFDOC_HIGH = {
  0x2022: 0x80, 0x2020: 0x81, 0x2021: 0x82, 0x2026: 0x83, 0x2014: 0x84, 0x2013: 0x85, 0x0192: 0x86, 0x2044: 0x87,
  0x2039: 0x88, 0x203A: 0x89, 0x2212: 0x8A, 0x2030: 0x8B, 0x201E: 0x8C, 0x201C: 0x8D, 0x201D: 0x8E, 0x2018: 0x8F,
  0x2019: 0x90, 0x201A: 0x91, 0x2122: 0x92, 0xFB01: 0x93, 0xFB02: 0x94, 0x0141: 0x95, 0x0152: 0x96, 0x0160: 0x97,
  0x0178: 0x98, 0x017D: 0x99, 0x0131: 0x9A, 0x0142: 0x9B, 0x0153: 0x9C, 0x0161: 0x9D, 0x017E: 0x9E, 0x20AC: 0xA0
};
const PC_PDFDOC_UNKNOWN = 0x3F; // "?"
function pcPdfDocByte(ch){
  const c = ch.codePointAt(0);
  if(PC_PDFDOC_HIGH[c] !== undefined) return PC_PDFDOC_HIGH[c];
  // 0x80-0xA0 mean other characters in PDFDocEncoding than in Latin-1
  if(c <= 0xff && (c < 0x80 || c > 0xa0)) return c;
  return PC_PDFDOC_UNKNOWN;
}
function pcPasswordBytes(pw, R){
  const s = String(pw || '');
  if(R >= 5) return new TextEncoder().encode(s.normalize('NFKC')).subarray(0, PC_PW_MAX_R6);
  return Uint8Array.from(Array.from(s).slice(0, PC_PW_MAX_R4), pcPdfDocByte);
}
function pcPad32(pwBytes){ return pcConcat(pwBytes.subarray(0, PC_PW_MAX_R4), PC_PAD.subarray(0, PC_PW_MAX_R4 - Math.min(pwBytes.length, PC_PW_MAX_R4))); }
/* ISO 32000-2 algorithm 2.A: R5/R6 file key from a user or owner password. */
async function pcKeyR56(R, pwBytes, U, O, UE, OE){
  const hash = (pw, salt, udata) => R === 5 ? pcSha(256, pcConcat(pw, salt, udata)) : pcHashR6(pw, salt, udata);
  const zeroIv = new Uint8Array(16), none = new Uint8Array(0), U48 = U.subarray(0, 48);
  if(pcEq(await hash(pwBytes, U.subarray(32, 40), none), U, 32)){
    const inter = await hash(pwBytes, U.subarray(40, 48), none);
    return (await pcAesDecrypt(inter, zeroIv, UE.subarray(0, 32), true)).subarray(0, 32);
  }
  if(pwBytes.length && pcEq(await hash(pwBytes, O.subarray(32, 40), U48), O, 32)){
    const inter = await hash(pwBytes, O.subarray(40, 48), U48);
    return (await pcAesDecrypt(inter, zeroIv, OE.subarray(0, 32), true)).subarray(0, 32);
  }
  return null;
}
/* ISO 32000-2 algorithms 2 + 6: R2-R4 file key for a (padded) user password,
   or null when it does not match /U. */
function pcKeyR4ForUser(R, n, paddedUser, O, P, idBytes, encryptMetadata, U){
  const Pb = new Uint8Array([P & 0xff, (P >> 8) & 0xff, (P >> 16) & 0xff, (P >>> 24) & 0xff]);
  let key = pcMd5(pcConcat(paddedUser, O.subarray(0, 32), Pb, idBytes, (R >= 4 && !encryptMetadata) ? new Uint8Array([255,255,255,255]) : new Uint8Array(0)));
  if(R >= 3) for(let i = 0; i < 50; i++) key = pcMd5(key.subarray(0, n));
  key = key.subarray(0, n);
  if(R === 2) return pcEq(pcRc4(key, PC_PAD), U, 32) ? key : null;
  let x = pcRc4(key, pcMd5(pcConcat(PC_PAD, idBytes)));
  for(let i = 1; i <= 19; i++){ const k2 = key.map(b => b ^ i); x = pcRc4(k2, x); }
  return pcEq(x, U, 16) ? key : null;
}
/* ISO 32000-2 algorithm 7: the padded user password recovered from /O with
   the owner password. */
function pcUserFromOwner(R, n, ownerBytes, O){
  let k = pcMd5(pcPad32(ownerBytes));
  if(R >= 3) for(let i = 0; i < 50; i++) k = pcMd5(k);
  k = k.subarray(0, n);
  if(R === 2) return pcRc4(k, O.subarray(0, 32));
  let x = O.subarray(0, 32);
  for(let i = 19; i >= 0; i--) x = pcRc4(k.map(b => b ^ i), x);
  return x;
}
/* Works out the file key for the EMPTY user password, or throws a
   PasswordException when the file really needs a password to open.
   (v60) With `password`, that password is tried as the user password and
   then as the owner password; a wrong one throws the same PasswordException
   (err.wrongPassword = true, so the unlock tool can say so). */
async function pcDeriveKey(enc, idBytes, password){
  const { PDFName, PDFNumber, PDFBool, PDFDict } = PDFLib;
  const num = (k, d) => { const v = enc.lookup(PDFName.of(k)); return v instanceof PDFNumber ? v.asNumber() : d; };
  const filter = enc.lookup(PDFName.of('Filter'));
  if(filter && filter.asString && filter.asString() !== '/Standard') throw pcPasswordError();
  const V = num('V', 0), R = num('R', 2);
  const O = pcBytesOf(enc.lookup(PDFName.of('O'))), U = pcBytesOf(enc.lookup(PDFName.of('U')));
  const P = num('P', -4);
  const em = enc.lookup(PDFName.of('EncryptMetadata'));
  const encryptMetadata = !(em instanceof PDFBool && em.asBoolean && em.asBoolean() === false) && !(em && em.toString && em.toString() === 'false');
  // crypt filter method for streams and strings
  let stmMethod = V >= 4 ? 'AESV2' : 'V2', strMethod = stmMethod;
  if(V >= 4){
    const CF = enc.lookup(PDFName.of('CF'));
    const methodOf = name => {
      if(!name || name.asString() === '/Identity') return 'Identity';
      const cf = CF instanceof PDFDict ? CF.lookup(PDFName.of(name.asString().slice(1))) : null;
      const cfm = cf instanceof PDFDict ? cf.lookup(PDFName.of('CFM')) : null;
      const m = cfm ? cfm.asString().slice(1) : 'None';
      return m === 'None' ? 'Identity' : m;
    };
    stmMethod = methodOf(enc.lookup(PDFName.of('StmF')));
    strMethod = methodOf(enc.lookup(PDFName.of('StrF')));
  }
  const typed = password != null && password !== '';
  const fail = () => { const e = pcPasswordError(); if(typed) e.wrongPassword = true; return e; };
  if(R >= 5){
    const UE = pcBytesOf(enc.lookup(PDFName.of('UE'))), OE = pcBytesOf(enc.lookup(PDFName.of('OE')));
    // (v60) some writers (and PDF viewers) do not cut a password longer than
    // 127 bytes: when the standard's cut-off form fails, the full one is tried
    const full = new TextEncoder().encode(String(password || '').normalize('NFKC'));
    let fileKey = await pcKeyR56(R, pcPasswordBytes(password, R), U, O, UE, OE);
    if(!fileKey && full.length > PC_PW_MAX_R6) fileKey = await pcKeyR56(R, full, U, O, UE, OE);
    // (v157) qpdf, pdf.js and many writers don't apply SASLprep: the password
    // as typed (not NFKC-folded) too, so full-width digits or "Ⅸ" open
    const raw = new TextEncoder().encode(String(password || ''));
    if(!fileKey && typed && !pcEq(raw, full, Math.max(raw.length, full.length))) fileKey = await pcKeyR56(R, raw.subarray(0, PC_PW_MAX_R6), U, O, UE, OE);
    if(!fileKey) throw fail();
    return { R, V, key: fileKey, stmMethod, strMethod, encryptMetadata };
  }
  // (v74) V4 (crypt filters) without a /Length of its own has a 128-bit key:
  // read as 40 bits, a file that opens without a password was refused as
  // "password-protected" (pypdf test file r4-aes-v2-no-key-length.pdf)
  const n = R === 2 ? 5 : Math.max(5, Math.min(16, (num('Length', V >= 4 ? PC_V4_KEY_BITS : 40) || 40) / 8));
  // verify against /U: only then is the password the right one
  const pwBytes = pcPasswordBytes(password, R);
  let key = pcKeyR4ForUser(R, n, pcPad32(pwBytes), O, P, idBytes, encryptMetadata, U);
  if(!key && typed) key = pcKeyR4ForUser(R, n, pcUserFromOwner(R, n, pwBytes, O), O, P, idBytes, encryptMetadata, U);
  if(!key) throw fail();
  return { R, V, key, stmMethod, strMethod, encryptMetadata };
}
async function pcDecryptBytes(info, method, objNum, gen, data){
  if(method === 'Identity' || !data.length) return data;
  if(info.R >= 5) return data.length < 16 ? new Uint8Array(0) : pcAesDecrypt(info.key, data.subarray(0, 16), data.subarray(16));
  const aes = method === 'AESV2';
  const k = pcMd5(pcConcat(info.key, new Uint8Array([objNum & 255, (objNum >> 8) & 255, (objNum >> 16) & 255, gen & 255, (gen >> 8) & 255]), aes ? new Uint8Array([0x73,0x41,0x6C,0x54]) : new Uint8Array(0))).subarray(0, Math.min(info.key.length + 5, 16));
  if(aes) return data.length < 16 ? new Uint8Array(0) : pcAesDecrypt(k, data.subarray(0, 16), data.subarray(16));
  return pcRc4(k, data);
}

/* pdf-lib cannot read an ENCRYPTED object stream (it tries to inflate the
   still-encrypted bytes and silently drops every object inside, often the
   document catalog itself). While an encrypted file is being parsed, its
   object streams are decrypted first. Parsing is sequential, so one hook
   at a time; a small queue keeps two parses from overlapping. */
/* (v90) pdf-lib hands the thread back every 100 parsed / 50 written objects
   (setTimeout 0). A browser throttles those timers in a hidden tab (1/s, later
   1/min), so a big merge left running in a background tab took minutes to
   hours. Hidden (or in the worker, where nothing needs to stay responsive):
   no pauses. Decided when the work starts. */
/* (perf) A worker sends a sign of life this often while a job runs (pcWorkerSession, app.js, watches it). */
const PC_WORKER_BEAT_MS = 5000;
function pcInBackground(){ return typeof document === 'undefined' || document.hidden === true; }
function pcLoadOptions(opts){ return pcInBackground() ? Object.assign({}, opts, { parseSpeed: Infinity }) : opts; }
function pcSaveOptions(opts){ return pcInBackground() ? Object.assign({}, opts, { objectsPerTick: Infinity }) : opts; }
let pcParseQueue = Promise.resolve();
function pcWithParseLock(fn){
  const run = pcParseQueue.then(fn, fn);
  pcParseQueue = run.catch(() => { /* the caller gets this error from `run`; the queue only needs to move on */ });
  return run;
}
async function pcLoadDecrypted(bytes, info){
  const { PDFDocument, PDFParser, PDFObjectStreamParser, PDFRawStream, PDFName } = PDFLib;
  return pcWithParseLock(async () => {
    const origHeader = PDFParser.prototype.parseIndirectObjectHeader;
    const origFor = PDFObjectStreamParser.forStream;
    const pending = [];
    let lastRef = null;
    PDFParser.prototype.parseIndirectObjectHeader = function(){ const r = origHeader.apply(this, arguments); lastRef = r; return r; };
    PDFObjectStreamParser.forStream = function(raw, tick){
      const ref = lastRef;
      return { parseIntoContext: async function(){
        const plain = await pcDecryptBytes(info, info.stmMethod, ref.objectNumber, ref.generationNumber, raw.contents);
        const dec = PDFRawStream.of(raw.dict, plain);
        const p = origFor.call(PDFObjectStreamParser, dec, tick);
        const ctx = raw.dict.context, assign = ctx.assign;
        ctx.assign = function(r, o){ pending.push(r.toString()); return assign.call(ctx, r, o); };
        try{ await p.parseIntoContext(); } finally { ctx.assign = assign; }
      } };
    };
    try{
      const doc = await PDFDocument.load(bytes, pcLoadOptions({ ignoreEncryption: true, updateMetadata: false, throwOnInvalidObject: false }));
      doc.__pcFromObjStm = new Set(pending);
      return doc;
    }finally{
      PDFParser.prototype.parseIndirectObjectHeader = origHeader;
      PDFObjectStreamParser.forStream = origFor;
    }
  });
}
async function pcDecryptDoc(doc, info, encRef){
  const { PDFName, PDFDict, PDFArray, PDFRawStream, PDFString, PDFHexString, PDFRef } = PDFLib;
  const ctx = doc.context;
  const skip = doc.__pcFromObjStm || new Set();
  const encKey = encRef instanceof PDFRef ? encRef.toString() : null;
  const decStr = async (o, ref) => {
    const plain = await pcDecryptBytes(info, info.strMethod, ref.objectNumber, ref.generationNumber, o.asBytes());
    return PDFHexString.of(Array.from(plain, b => b.toString(16).padStart(2, '0')).join(''));
  };
  const walk = async (o, ref, inSig) => {
    if(o instanceof PDFString || o instanceof PDFHexString) return decStr(o, ref);
    if(o instanceof PDFArray){
      for(let i = 0; i < o.size(); i++){ const v = o.get(i); const nv = await walk(v, ref, false); if(nv !== v) o.set(i, nv); }
      return o;
    }
    if(o instanceof PDFDict){
      // (v90) /Type is optional in a signature dictionary: /ByteRange marks one too
      const isSig = inSig || o.get(PDFName.of('Type')) === PDFName.of('Sig') || !!o.get(PDFName.of('ByteRange'));
      for(const [k, v] of o.entries()){
        if(isSig && k === PDFName.of('Contents')) continue; // signature values are never encrypted
        const nv = await walk(v, ref, false);
        if(nv !== v) o.set(k, nv);
      }
      return o;
    }
    return o;
  };
  for(const [ref, obj] of ctx.enumerateIndirectObjects()){
    if(ref.toString() === encKey || skip.has(ref.toString())) continue;
    pcCheckCancel();
    if(obj instanceof PDFRawStream){
      await walk(obj.dict, ref, false);
      const type = obj.dict.get(PDFName.of('Type'));
      if(type === PDFName.of('XRef')) continue;
      if(type === PDFName.of('Metadata') && !info.encryptMetadata) continue;
      let method = info.stmMethod;
      const f = obj.dict.lookup(PDFName.of('Filter'));
      const firstFilter = f instanceof PDFArray ? f.lookup(0) : f;
      if(firstFilter === PDFName.of('Crypt')){
        // an explicit per-stream crypt filter; only /Identity is seen in practice
        method = 'Identity';
        if(f instanceof PDFArray){ f.remove(0); if(!f.size()) obj.dict.delete(PDFName.of('Filter')); }
        else obj.dict.delete(PDFName.of('Filter'));
        const dp = obj.dict.lookup(PDFName.of('DecodeParms'));
        if(dp instanceof PDFArray){ dp.remove(0); if(!dp.size()) obj.dict.delete(PDFName.of('DecodeParms')); }
        else if(dp) obj.dict.delete(PDFName.of('DecodeParms'));
      }
      const plain = await pcDecryptBytes(info, method, ref.objectNumber, ref.generationNumber, obj.contents);
      ctx.assign(ref, PDFRawStream.of(obj.dict, plain));
    }else{
      const nv = await walk(obj, ref, false);
      if(nv !== obj) ctx.assign(ref, nv);
    }
  }
  delete ctx.trailerInfo.Encrypt;
  if(encRef instanceof PDFRef) ctx.delete(encRef);
  doc.isEncrypted = false;
}

/* (v59) Not a readable PDF (no header, no page tree, empty). Named like
   pdf.js's own error for the same case, so every tool shows the same
   "Is it a valid PDF?" message and it is not logged as an unexpected fault;
   the parser's own error stays available as `cause` for debugging. */
function pcInvalidPdf(cause){
  const e = new Error('Not a readable PDF');
  e.name = 'InvalidPDFException';
  e.cause = cause;
  return e;
}
// ...but running out of memory on a big, valid PDF (a RangeError) or a
// cancel is not "an invalid file": those keep their own message
function pcAsInvalidPdf(e){ return (e instanceof RangeError || pcIsCancel(e)) ? e : pcInvalidPdf(e); }
/* Loads a PDF for editing with pdf-lib. Returns { doc, decrypted }.
   Throws an error named PasswordException when it needs a password to
   open, and (v59) one named InvalidPDFException (pcInvalidPdf) for a file
   that isn't a readable PDF. (v60) `password` (optional) opens a file that
   needs one; see pcDeriveKey. */
/* (v94) A pure XFA form: its pages are only a "please wait" placeholder and
   the real form lives in the XFA data, so a tool that rebuilds or draws on
   the pages said "Done!" over that placeholder. Same rule as pdf.js'
   formInfo: XFA data and no AcroForm fields ('pure'); both = 'hybrid'.
   ponytail: signature-only field lists count as fields (pdf.js excludes
   them); a pure XFA form with only a signature field passes as hybrid. */
function pcXfaKind(doc){
  const { PDFDict, PDFArray, PDFStream } = PDFLib;
  const acro = doc.catalog.lookup(pcN('AcroForm'));
  if(!(acro instanceof PDFDict)) return null;
  const xfa = acro.lookup(pcN('XFA'));
  const hasXfa = (xfa instanceof PDFArray && xfa.size() > 0) || xfa instanceof PDFStream;
  if(!hasXfa) return null;
  const fields = acro.lookup(pcN('Fields'));
  return fields instanceof PDFArray && fields.size() > 0 ? 'hybrid' : 'pure';
}
/* pdf.js' own isPureXfa only works with enableXfa, which the site never sets,
   so the checks in Word/PowerPoint/Excel/JPG never fired before v94. */
async function pcIsPureXfaJs(pdfDoc){
  if(pdfDoc.isPureXfa) return true;
  const meta = await pdfDoc.getMetadata();
  const info = (meta && meta.info) || {};
  return !!(info.IsXFAPresent && !info.IsAcroFormPresent);
}
/* opts.keepXfa: the tool rewrites the whole file and keeps the XFA data
   (compress, protect, unlock), so a pure XFA form is fine there. */
async function openPdfForEdit(bytes, password, opts){
  const r = await pcOpenWithTypedPassword(bytes, password);
  if(!(opts && opts.keepXfa) && pcXfaKind(r.doc) === 'pure') throw pcUserError(pcT('xfa'));
  return r;
}
/* (v119) A file that needs a password to open uses the one the visitor typed
   in the tool (pcHostPassword: the page's own field, or the copy the page sent
   along with this job to the worker). The file is first tried without it, so
   a file that opens on its own (no password, or only editing restrictions)
   is never refused because a password was typed for another file.
   An explicit `password` (Unlock; null = none, for Protect/Unlock's own checks)
   skips this. Opened with the typed password, the result is not "decrypted"
   (that note is about editing restrictions); the page says instead that the
   result no longer has a password (pcHostPasswordUsed). */
async function pcOpenWithTypedPassword(bytes, password){
  if(password !== undefined) return pcOpenPdfForEdit(bytes, password);
  try{ return await pcOpenPdfForEdit(bytes); }
  catch(e){
    const typed = pcHostPassword();
    if(!typed || !e || e.name !== 'PasswordException') throw e;
    const r = await pcOpenPdfForEdit(bytes, typed);
    pcHostPasswordUsed();
    return Object.assign({}, r, { decrypted: false });
  }
}
async function pcOpenPdfForEdit(bytes, password){
  const { PDFDocument, PDFParser, PDFName, PDFDict, PDFArray, PDFRef } = PDFLib;
  if(bytes instanceof ArrayBuffer) bytes = new Uint8Array(bytes);
  if(!bytes || !bytes.length) throw pcInvalidPdf(new Error('empty file'));
  let doc, dups;
  try{ ({ doc, dups } = await pcLoadTracked(bytes)); }
  catch(e){ throw pcCutOffOr(bytes, pcAsInvalidPdf(e)); }
  const encRef = doc.context.trailerInfo.Encrypt;
  pcResolveDuplicates(doc, bytes, dups, !!encRef); // (v157)
  if(!encRef){
    // (v59) a file without a usable page tree fails here, as "not a valid
    // PDF", instead of with a TypeError deep inside the tool that uses it
    try{ doc.catalog; pcNormalizePageTree(doc); doc.getPageCount(); } // (v157) repaired first: a loop overflowed pdf-lib's stack
    catch(e){ throw pcAsInvalidPdf(e); }
    pcCheckUsable(doc, bytes);
    return { doc, decrypted: false };
  }
  const enc = doc.context.lookup(encRef);
  if(!(enc instanceof PDFDict)) throw pcPasswordError();
  const idArr = doc.context.lookup(doc.context.trailerInfo.ID);
  const id0 = idArr instanceof PDFArray && idArr.size() ? pcBytesOf(idArr.lookup(0)) : new Uint8Array(0);
  if(!pcSubtle) throw pcPasswordError();
  const info = await pcDeriveKey(enc, id0, password);
  // Parse again, this time decrypting object streams while reading.
  doc = await pcLoadDecrypted(bytes, info);
  await pcDecryptDoc(doc, info, doc.context.trailerInfo.Encrypt || encRef);
  try{ pcNormalizePageTree(doc); doc.getPageCount(); } // (v157) as above
  catch(e){ throw pcAsInvalidPdf(e); }
  pcCheckUsable(doc, bytes);
  return { doc, decrypted: true };
}
/* (v157) pdf-lib reads a PDF from front to back and keeps the LAST
   definition of every object; viewers (pdf.js, pdfium, Acrobat) follow the
   cross-reference table. Across revisions (an incremental save) both agree.
   Within one revision a file can define the same object twice with
   different contents; the tools then saved a page other than the one the
   preview, Word/Text and every viewer show. The load notes every top-level
   definition and where it starts; pcResolveDuplicates then picks the one the
   revision's xref table points at, like a viewer, or refuses the file when
   that can't be read reliably (an xref stream, a wrong offset, encryption). */
async function pcLoadTracked(bytes){
  const { PDFDocument, PDFParser, PDFContext } = PDFLib;
  return pcWithParseLock(async () => {
    const origHeader = PDFParser.prototype.parseIndirectObjectHeader;
    const origAssign = PDFContext.prototype.assign;
    const defs = new Map();
    let lastRef = null, lastAt = -1;
    PDFParser.prototype.parseIndirectObjectHeader = function(){
      const at = this.bytes.offset();
      const r = origHeader.apply(this, arguments);
      lastRef = r; lastAt = at;
      return r;
    };
    PDFContext.prototype.assign = function(ref, obj){
      // only objects written at the top level (an object stream's members don't come through the header)
      if(lastRef && ref === lastRef){
        const k = ref.toString(), list = defs.get(k);
        if(list) list.push({ at: lastAt, obj }); else defs.set(k, [{ ref, at: lastAt, obj }]);
        lastRef = null;
      }
      return origAssign.call(this, ref, obj);
    };
    try{
      const doc = await PDFDocument.load(bytes, pcLoadOptions({ ignoreEncryption: true, updateMetadata: false, throwOnInvalidObject: false }));
      const dups = [];
      defs.forEach(list => { if(list.length > 1) dups.push({ ref: list[0].ref, defs: list }); });
      return { doc, dups };
    }finally{
      PDFParser.prototype.parseIndirectObjectHeader = origHeader;
      PDFContext.prototype.assign = origAssign;
    }
  });
}
const PC_EOF_BYTES = [0x25, 0x25, 0x45, 0x4F, 0x46]; // %%EOF
const PC_XREF_ENTRY_LEN = 20;
const PC_XREF_SLACK = 2; // an xref offset may point at the whitespace just before "N G obj"
function pcEofPositions(bytes){
  const out = [];
  for(let i = bytes.indexOf(0x25); i >= 0 && i + PC_EOF_BYTES.length <= bytes.length; i = bytes.indexOf(0x25, i + 1)){
    if(PC_EOF_BYTES.every((b, j) => bytes[i + j] === b)) out.push(i);
  }
  return out;
}
function pcAscii(bytes, from, to){ return new TextDecoder('latin1').decode(bytes.subarray(Math.max(0, from), Math.min(bytes.length, to))); }
/* The offset the classic xref table of the revision ending at `end` gives
   for object `num`, or null (no table, an xref stream, not listed). Offsets
   count from %PDF (a file can have junk in front of it). */
function pcXrefOffset(bytes, num, start, end){
  const tail = pcAscii(bytes, start, end);
  const sx = tail.lastIndexOf('startxref');
  if(sx < 0) return null;
  const m = /^startxref\s+(\d+)/.exec(tail.slice(sx));
  const head = pcAscii(bytes, 0, PC_PDF_HEADER_WINDOW).indexOf('%PDF');
  if(!m || head < 0) return null;
  const at = Number(m[1]) + head;
  const table = pcAscii(bytes, at, end);
  if(!/^xref\s/.test(table)) return null;
  const SUB = /^\s*(\d+)\s+(\d+)[ \t]*\r?\n/; // a subsection header: first object, count
  let pos = 'xref'.length;
  for(;;){
    const h = SUB.exec(table.slice(pos, pos + PC_XREF_SUB_WINDOW));
    if(!h) return null; // "trailer", or a table this can't read
    const first = Number(h[1]), count = Number(h[2]);
    pos += h[0].length;
    if(num >= first && num < first + count){
      const e = /^(\d{10}) (\d{5}) n/.exec(table.substr(pos + (num - first) * PC_XREF_ENTRY_LEN, PC_XREF_ENTRY_LEN));
      return e ? Number(e[1]) + head : null;
    }
    pos += count * PC_XREF_ENTRY_LEN;
  }
}
const PC_XREF_SUB_WINDOW = 64;
function pcResolveDuplicates(doc, bytes, dups, encrypted){
  if(!dups.length) return;
  const eofs = pcEofPositions(bytes);
  const revOf = at => { const i = eofs.findIndex(p => p > at); return i < 0 ? eofs.length : i; };
  for(const d of dups){
    const last = d.defs[d.defs.length - 1], rev = revOf(last.at);
    const same = d.defs.filter(x => revOf(x.at) === rev);
    if(same.length < 2 || same.every(x => x.obj.toString() === last.obj.toString())) continue; // a later revision replaces it: pdf-lib is right
    const off = encrypted ? null : pcXrefOffset(bytes, d.ref.objectNumber, rev ? eofs[rev - 1] : 0, rev < eofs.length ? eofs[rev] + PC_EOF_BYTES.length : bytes.length);
    const pick = off === null ? null : same.find(x => x.at >= off && x.at - off <= PC_XREF_SLACK);
    if(!pick) throw pcUserError(pcT('pdfConflict'));
    if(pick !== last){ doc.context.assign(d.ref, pick.obj); doc.__pcRepaired = true; }
  }
}
/* (v55) A PDF cut off halfway (an interrupted download) still opens in
   pdf-lib, with only the pages it could find -- and every tool then said
   "Done!" over a result with missing or blank pages. Such a file lacks the
   end-of-file marker AND has fewer pages than its page tree announces, or
   pages whose contents are missing; both are required, so a sloppy but
   complete PDF (a wrong /Count, junk after %%EOF) still works. */
const PC_EOF_WINDOW = 2048; // bytes at the end searched for %%EOF
function pcHasEofMarker(bytes){
  const tail = bytes.subarray(Math.max(0, bytes.length - PC_EOF_WINDOW));
  for(let i = tail.length - 5; i >= 0; i--){
    if(tail[i] === 0x25 && tail[i+1] === 0x25 && tail[i+2] === 0x45 && tail[i+3] === 0x4F && tail[i+4] === 0x46) return true;
  }
  return false;
}
/* (v89) A PDF that pdf-lib can't even parse, starts like a PDF but has no
   end marker: an interrupted download or copy. It gets the 'truncated'
   message, not "Is it a valid PDF?". Other errors pass through unchanged. */
const PC_PDF_HEADER_WINDOW = 1024;
function pcLooksCutOff(bytes){
  const head = bytes.subarray(0, PC_PDF_HEADER_WINDOW);
  for(let i = 0; i + 3 < head.length; i++){
    if(head[i] === 0x25 && head[i+1] === 0x50 && head[i+2] === 0x44 && head[i+3] === 0x46) return !pcHasEofMarker(bytes); // %PDF
  }
  return false;
}
function pcCutOffOr(bytes, err){
  return err && err.name === 'InvalidPDFException' && pcLooksCutOff(bytes) ? pcUserError(pcT('truncated')) : err;
}
/* (v90) ...and a PDF without a single page is no usable PDF either: pdf-lib's
   save() quietly adds a blank page, so Rotate said "Done!" over an empty page. */
function pcCheckUsable(doc, bytes){
  if(!doc.getPageCount()) throw pcInvalidPdf(new Error('no pages'));
  pcCheckComplete(doc, bytes);
}
function pcCheckComplete(doc, bytes){
  if(pcHasEofMarker(bytes)) return;
  const found = doc.getPageCount();
  // (v157) the /Count as the file had it, before pcNormalizePageTree rewrote it
  const announced = doc.__pcAnnouncedPages !== undefined ? doc.__pcAnnouncedPages : pcRootCount(doc, found);
  if(found < announced || pcMissingPageParts(doc) > 0) throw pcUserError(pcT('truncated'));
}
function pcRootCount(doc, fallback){
  const pages = doc.catalog.lookup(PDFLib.PDFName.of('Pages'));
  const countObj = pages && pages.lookup ? pages.lookup(PDFLib.PDFName.of('Count')) : null;
  return countObj && typeof countObj.asNumber === 'function' ? countObj.asNumber() : fallback;
}
/* (v157) A broken page tree: a /Kids entry that is null or points at nothing,
   a /Count that doesn't match the pages, or a loop (a kid pointing back at
   its ancestor). pdf-lib found the real pages but every tool that saves the
   document in place wrote the broken tree back ("Done!", while Chrome showed
   an unreadable or missing page), and a loop overflowed the stack.
   Walked here without recursion and with a `seen` set; only when something is
   wrong is the tree rewritten flat: every page gets its inherited
   MediaBox/CropBox/Rotate/Resources on itself and hangs directly under the
   root, in reading order. A healthy tree is left exactly as it is.
   Returns true when it was rewritten. */
const PC_INHERITED_PAGE_KEYS = ['MediaBox', 'CropBox', 'Rotate', 'Resources'];
function pcNormalizePageTree(doc){
  const { PDFDict, PDFArray, PDFRef, PDFNumber } = PDFLib;
  const ctx = doc.context, rootRef = doc.catalog.get(pcN('Pages'));
  const root = doc.catalog.lookup(pcN('Pages'));
  if(!(rootRef instanceof PDFRef) || !(root instanceof PDFDict)) return false; // no usable tree: the caller's own check says so
  const seen = new Set([root]), leaves = [];
  let broken = false;
  // each entry: a Pages node, the attributes it inherits, and where its own leaves start
  const stack = [{ node: root, inherit: {}, kids: null, i: 0, first: 0 }];
  while(stack.length){
    const top = stack[stack.length - 1];
    if(!top.kids) top.kids = top.node.lookup(pcN('Kids'));
    if(!(top.kids instanceof PDFArray) || top.i >= top.kids.size()){
      const count = top.node.lookup(pcN('Count'));
      if(!(count instanceof PDFNumber) || count.asNumber() !== leaves.length - top.first) broken = true;
      stack.pop();
      continue;
    }
    const raw = top.kids.get(top.i++);
    const kid = raw instanceof PDFRef ? ctx.lookup(raw) : raw;
    if(!(kid instanceof PDFDict) || seen.has(kid)){ broken = true; continue; }
    seen.add(kid);
    const inherit = Object.assign({}, top.inherit);
    PC_INHERITED_PAGE_KEYS.forEach(k => { const v = top.node.get(pcN(k)); if(v !== undefined) inherit[k] = v; });
    const isNode = kid.get(pcN('Type')) === pcN('Pages') || (kid.get(pcN('Type')) !== pcN('Page') && kid.has(pcN('Kids')));
    if(isNode){ stack.push({ node: kid, inherit, kids: null, i: 0, first: leaves.length }); continue; }
    if(!(raw instanceof PDFRef)){ broken = true; } // a page written inline in /Kids: pdf-lib needs a reference
    leaves.push({ ref: raw instanceof PDFRef ? raw : ctx.register(kid), page: kid, inherit });
  }
  if(!broken || !leaves.length) return false;
  doc.__pcAnnouncedPages = pcRootCount(doc, leaves.length);
  leaves.forEach(l => {
    PC_INHERITED_PAGE_KEYS.forEach(k => { if(!l.page.has(pcN(k)) && l.inherit[k] !== undefined) l.page.set(pcN(k), l.inherit[k]); });
    l.page.set(pcN('Parent'), rootRef);
  });
  root.set(pcN('Kids'), ctx.obj(leaves.map(l => l.ref)));
  root.set(pcN('Count'), PDFNumber.of(leaves.length));
  // pdf-lib reads the tree lazily; anything it cached from the old one is dropped
  if(doc.pageCache && typeof doc.pageCache.invalidate === 'function') doc.pageCache.invalidate();
  doc.pageCount = undefined;
  doc.__pcRepaired = true;
  return true;
}
/* Page contents and resources that the file refers to but doesn't contain
   (cut off): such pages would come out blank. */
function pcMissingPageParts(doc){
  const { PDFName, PDFRef, PDFArray } = PDFLib;
  let missing = 0;
  const check = v => { if(v instanceof PDFRef && doc.context.lookup(v) === undefined) missing++; };
  doc.getPages().forEach(p => {
    const c = p.node.get(PDFName.of('Contents'));
    if(c instanceof PDFArray) c.asArray().forEach(check); else check(c);
    check(p.node.get(PDFName.of('Resources')));
  });
  return missing;
}

/* ---------- helpers on pdf-lib objects ---------- */
function pcN(n){ return PDFLib.PDFName.of(n); }
function pcText(o){
  const { PDFString, PDFHexString, PDFName } = PDFLib;
  if(o instanceof PDFString || o instanceof PDFHexString) return o.decodeText();
  if(o instanceof PDFName) return o.decodeText ? o.decodeText() : o.asString().slice(1);
  return null;
}
/* Walks a name tree (/Names arrays under /Kids) and returns [key, value]. */
function pcNameTreeEntries(root, maxDepth){
  const { PDFDict, PDFArray } = PDFLib;
  const out = [];
  const seen = new Set();
  const walk = (node, depth) => {
    if(!(node instanceof PDFDict) || depth > (maxDepth || 32) || seen.has(node)) return;
    seen.add(node);
    const names = node.lookup(pcN('Names'));
    if(names instanceof PDFArray){
      for(let i = 0; i + 1 < names.size(); i += 2){
        const k = pcText(names.lookup(i));
        if(k != null) out.push([k, names.get(i + 1)]);
      }
    }
    const kids = node.lookup(pcN('Kids'));
    if(kids instanceof PDFArray) for(let i = 0; i < kids.size(); i++) walk(kids.lookup(i), depth + 1);
  };
  walk(root, 0);
  return out;
}

/* Walks a number tree (/Nums arrays under /Kids) and returns [number, value]. */
function pcNumberTreeEntries(root){
  const { PDFDict, PDFArray, PDFNumber } = PDFLib;
  const out = [], seen = new Set();
  const walk = (node, depth) => {
    if(!(node instanceof PDFDict) || depth > 32 || seen.has(node)) return;
    seen.add(node);
    const nums = node.lookup(pcN('Nums'));
    if(nums instanceof PDFArray){
      for(let i = 0; i + 1 < nums.size(); i += 2){
        const k = nums.lookup(i);
        if(k instanceof PDFNumber) out.push([k.asNumber(), nums.get(i + 1)]);
      }
    }
    const kids = node.lookup(pcN('Kids'));
    if(kids instanceof PDFArray) for(let i = 0; i < kids.size(); i++) walk(kids.lookup(i), depth + 1);
  };
  walk(root, 0);
  return out;
}

/* Everything a donor document needs before its pages are copied: every
   reference FROM an annotation or bookmark TO a page is replaced by a
   neutral marker (/PCPG<index>), because pdf-lib's copier would otherwise
   copy the whole target page again as an invisible orphan (bloat + broken
   link). The markers are turned back into real page references in the new
   document, or the link is dropped when its page isn't part of the result. */
/* (v73) Some PDF makers (LibreOffice Impress, among others) give every page
   one shared resource list holding ALL the images, fonts and graphics
   states of the document, and point the page's groups (transparency
   groups, soft masks) at that same list. A page copied on its own then
   drags every image along: splitting a 180-slide lesson of 45 MB made 180
   files of 45 MB each, the browser ran out of room while zipping them and
   the visitor got "not a valid PDF". Pages that share their list get one
   of their own with only what their content draws (Do, Tf, gs, scn, sh),
   and the groups they draw get the same treatment. Whenever that can't be
   worked out for sure (a filter pdf-lib can't read; a form, Type3 font or
   annotation appearance that borrows the page's resources), the page keeps
   the full list: the result is then larger, never wrong. */
const PC_SLIM_MIN_ENTRIES = 4;  // resource lists this small are not worth the work
const PC_SLIM_MAX_DEPTH = 10;   // groups inside groups
const PC_NAME = '/([^\\s/\\[\\]<>(){}%]+)';
const PC_END = '(?![^\\s/\\[\\]<>(){}%])';
const PC_NUM = '[-+]?(?:\\d+\\.?\\d*|\\.\\d+)';
const PC_RES_USE = {           // resource kind -> how content uses one of them
  XObject: new RegExp(PC_NAME + '\\s+Do' + PC_END, 'g'),
  Font: new RegExp(PC_NAME + '\\s+' + PC_NUM + '\\s+Tf' + PC_END, 'g'),
  ExtGState: new RegExp(PC_NAME + '\\s+gs' + PC_END, 'g'),
  Pattern: new RegExp(PC_NAME + '\\s+(?:scn|SCN)' + PC_END, 'g'),
  Shading: new RegExp(PC_NAME + '\\s+sh' + PC_END, 'g')
};
const PC_SLIM_BAIL = 'pc-slim-bail';
function pcPdfNameDecode(raw){ return raw.replace(/#([0-9A-Fa-f]{2})/g, (m, h) => String.fromCharCode(parseInt(h, 16))); }
const PC_LATIN1_CHUNK = 0x8000; // characters per String.fromCharCode call (stays under the argument limit)
function pcLatin1(bytes){
  let text = '';
  for(let i = 0; i < bytes.length; i += PC_LATIN1_CHUNK) text += String.fromCharCode.apply(null, bytes.subarray(i, i + PC_LATIN1_CHUNK));
  return text;
}
function pcStreamText(s){
  const { PDFRawStream, decodePDFRawStream } = PDFLib;
  if(s instanceof PDFRawStream) return pcLatin1(decodePDFRawStream(s).decode()); // throws on a filter it can't read
  if(s && typeof s.getUnencodedContents === 'function') return pcLatin1(s.getUnencodedContents());
  throw new Error(PC_SLIM_BAIL);
}
function pcNamesUsed(text, re){
  const used = new Set();
  re.lastIndex = 0;
  for(let m; (m = re.exec(text)); ){ used.add(m[1]); used.add(pcPdfNameDecode(m[1])); }
  return used;
}
function pcResourceCount(res){
  return Object.keys(PC_RES_USE).reduce((n, kind) => {
    const d = res.lookup(pcN(kind));
    return n + (d instanceof PDFLib.PDFDict ? d.entries().length : 0);
  }, 0);
}
/* A form or tiling pattern with a large resource list gets a copy whose list
   holds only what its own content uses (memo: one copy per original). */
const PC_SLIM_BAIL_MARK = { bail: true }; // memo entry: this group draws with the page's list
function pcSlimStreamRef(ctx, ref, memo, depth){
  const { PDFRef } = PDFLib;
  if(!(ref instanceof PDFRef)) return ref;
  const key = ref.toString();
  if(memo.get(key) === PC_SLIM_BAIL_MARK) throw new Error(PC_SLIM_BAIL); // every page that draws it keeps its full list
  if(memo.has(key)) return memo.get(key);
  memo.set(key, ref); // a group that (oddly) contains itself keeps its full list
  try{
    const copy = pcSlimStream(ctx, ref, memo, depth);
    memo.set(key, copy);
    return copy;
  }catch(e){
    if(e && e.message === PC_SLIM_BAIL) memo.set(key, PC_SLIM_BAIL_MARK);
    throw e;
  }
}
function pcSlimStream(ctx, ref, memo, depth){
  const { PDFRawStream, PDFDict } = PDFLib;
  const s = ctx.lookup(ref);
  if(!(s instanceof PDFRawStream)) return ref;
  const sub = s.dict.get(pcN('Subtype')), isForm = sub === pcN('Form'), isTiling = s.dict.get(pcN('PatternType')) && !sub;
  if(!isForm && !isTiling) return ref; // an image: nothing inside to slim
  const resRef = s.dict.get(pcN('Resources'));
  if(!resRef) throw new Error(PC_SLIM_BAIL); // draws with the page's own list
  const res = ctx.lookup(resRef);
  if(!(res instanceof PDFDict) || depth > PC_SLIM_MAX_DEPTH || pcResourceCount(res) < PC_SLIM_MIN_ENTRIES) return ref;
  const text = pcDecodedOrNull(s);
  if(text == null) return ref; // unreadable: this group keeps its full list
  const slim = pcSlimResources(ctx, res, text, memo, depth + 1);
  if(!slim) return ref;
  const dict = s.dict.clone(ctx);
  dict.set(pcN('Resources'), slim);
  return ctx.register(PDFRawStream.of(dict, s.contents));
}
// decoded content, or null when pdf-lib can't read its filter (only that failure is expected here)
function pcDecodedOrNull(s){
  try{ return pcStreamText(s); }
  catch(e){ if(pcSlimOutOfMemory(e)) throw e; return null; }
}
function pcSlimOutOfMemory(e){ return !!e && /allocation|memory/i.test(String(e.message || '')); }
function pcSlimGraphicsState(ctx, ref, memo, depth){
  const { PDFDict } = PDFLib;
  const gs = ctx.lookup(ref);
  const sm = gs instanceof PDFDict ? gs.lookup(pcN('SMask')) : null;
  if(!(sm instanceof PDFDict) || !sm.get(pcN('G'))) return ref;
  const g = pcSlimStreamRef(ctx, sm.get(pcN('G')), memo, depth);
  if(g === sm.get(pcN('G'))) return ref;
  const sm2 = sm.clone(ctx); sm2.set(pcN('G'), g);
  const gs2 = gs.clone(ctx); gs2.set(pcN('SMask'), sm2);
  return gs2;
}
// a Type3 font without its own list draws its glyphs with the page's list
function pcCheckType3(ctx, ref){
  const f = ctx.lookup(ref);
  if(f instanceof PDFLib.PDFDict && f.get(pcN('Subtype')) === pcN('Type3') && !f.get(pcN('Resources'))) throw new Error(PC_SLIM_BAIL);
}
// a new resource list with only what `text` uses, or null if nothing can go
function pcSlimResources(ctx, res, text, memo, depth){
  const { PDFDict } = PDFLib;
  const out = res.clone(ctx); // the shared list itself stays as it was
  let changed = false;
  for(const kind of Object.keys(PC_RES_USE)){
    const d = res.lookup(pcN(kind));
    if(!(d instanceof PDFDict)) continue;
    const used = pcNamesUsed(text, PC_RES_USE[kind]);
    const nd = ctx.obj({});
    let kindChanged = false;
    for(const [k, v] of d.entries()){
      if(!used.has(k.asString().slice(1)) && !used.has(k.decodeText ? k.decodeText() : '')){ kindChanged = true; continue; }
      let nv = v;
      if(kind === 'Font') pcCheckType3(ctx, v);
      if(kind === 'XObject' || kind === 'Pattern') nv = pcSlimStreamRef(ctx, v, memo, depth);
      else if(kind === 'ExtGState') nv = pcSlimGraphicsState(ctx, v, memo, depth);
      if(nv !== v) kindChanged = true;
      nd.set(k, nv);
    }
    if(kindChanged){ out.set(pcN(kind), nd); changed = true; }
  }
  return changed ? out : null;
}
// an annotation appearance without its own list may draw with the page's list
function pcAnnotsBorrow(node){
  const { PDFArray, PDFDict, PDFRawStream } = PDFLib;
  const annots = node.lookup(pcN('Annots'));
  if(!(annots instanceof PDFArray)) return false;
  const borrows = o => o instanceof PDFRawStream ? !o.dict.get(pcN('Resources'))
    : o instanceof PDFDict ? o.entries().some(([, v]) => borrows(node.context.lookup(v))) : false;
  for(let i = 0; i < annots.size(); i++){
    const a = annots.lookup(i);
    const ap = a instanceof PDFDict ? a.lookup(pcN('AP')) : null;
    if(ap instanceof PDFDict && ap.entries().some(([, v]) => borrows(node.context.lookup(v)))) return true;
  }
  return false;
}
function pcSlimPage(page, memo){
  const ctx = page.node.context;
  const res = ctx.lookup(page.node.getInheritableAttribute(pcN('Resources')));
  if(!(res instanceof PDFLib.PDFDict) || pcAnnotsBorrow(page.node)) return;
  const parts = [];
  const c = page.node.lookup(pcN('Contents'));
  (c instanceof PDFLib.PDFArray ? c.asArray().map(r => ctx.lookup(r)) : (c ? [c] : [])).forEach(x => parts.push(pcDecodedOrNull(x)));
  if(parts.some(x => x == null)) return; // a content part pdf-lib can't read: keep the full list
  try{
    const slim = pcSlimResources(ctx, res, parts.join('\n'), memo, 0); // parts of one page join at token boundaries
    if(slim) page.node.set(pcN('Resources'), slim);
  }catch(e){
    if(e && e.message === PC_SLIM_BAIL) return; // the page keeps the full list
    throw e;
  }
}
/* Only pages that share their list with other pages, only when they are
   copied (taking 1 page out of 1000 slims 1 page), each page once. */
function pcSlimForCopy(doc, prep, idx){
  const { PDFDict } = PDFLib;
  if(!prep.slim){
    const count = new Map();
    prep.pages.forEach(p => { const d = doc.context.lookup(p.node.getInheritableAttribute(pcN('Resources'))); if(d instanceof PDFDict) count.set(d, (count.get(d) || 0) + 1); });
    prep.slim = { count, memo: new Map(), done: new Set() };
  }
  const { count, memo, done } = prep.slim;
  if(done.has(idx)) return;
  done.add(idx);
  const page = prep.pages[idx];
  const d = doc.context.lookup(page.node.getInheritableAttribute(pcN('Resources')));
  if(!(d instanceof PDFDict) || (count.get(d) || 0) < 2 || pcResourceCount(d) < PC_SLIM_MIN_ENTRIES) return;
  try{ pcSlimPage(page, memo); }
  catch(e){
    if(pcSlimOutOfMemory(e) || pcIsCancel(e)) throw e;
    pcLogUnexpected(e); // a fault in this size saving: the page keeps its full list, the result stays correct
  }
}

function pcPrepDonor(doc){
  if(doc.__pcPrep) return doc.__pcPrep;
  const { PDFDict, PDFArray, PDFRef, PDFName, PDFNumber, PDFString, PDFHexString } = PDFLib;
  const ctx = doc.context;
  const pages = doc.getPages();
  const idxByRef = new Map(pages.map((p, i) => [p.ref.toString(), i]));
  const cat = doc.catalog;
  // named destinations
  const named = new Map();
  const oldDests = cat.lookup(pcN('Dests'));
  if(oldDests instanceof PDFDict) for(const [k, v] of oldDests.entries()) named.set(k.decodeText ? k.decodeText() : k.asString().slice(1), v);
  const namesDict = cat.lookup(pcN('Names'));
  if(namesDict instanceof PDFDict){
    const dt = namesDict.lookup(pcN('Dests'));
    if(dt) pcNameTreeEntries(dt).forEach(([k, v]) => { if(!named.has(k)) named.set(k, v); });
  }
  const resolveDest = (d, depth) => {
    if(depth > 4 || d == null) return null;
    if(d instanceof PDFRef) d = ctx.lookup(d);
    if(d instanceof PDFString || d instanceof PDFHexString || d instanceof PDFName){
      const key = pcText(d);
      return key != null && named.has(key) ? resolveDest(named.get(key), (depth || 0) + 1) : null;
    }
    if(d instanceof PDFDict) return resolveDest(d.get(pcN('D')), (depth || 0) + 1);
    if(d instanceof PDFArray && d.size()){
      const first = d.get(0);
      let idx = null;
      if(first instanceof PDFRef && idxByRef.has(first.toString())) idx = idxByRef.get(first.toString());
      else if(first instanceof PDFNumber) idx = first.asNumber();
      if(idx == null || idx < 0 || idx >= pages.length) return null;
      const rest = [];
      for(let i = 1; i < d.size(); i++){ const v = d.lookup(i); rest.push(v instanceof PDFDict || v instanceof PDFArray || v instanceof PDFRef ? PDFLib.PDFNull : v); }
      return { idx, rest };
    }
    return null;
  };
  const marker = r => ctx.obj([PDFName.of('PCPG' + r.idx), ...r.rest]);
  const gotoDest = a => (a instanceof PDFDict && a.get(pcN('S')) === pcN('GoTo')) ? a.get(pcN('D')) : undefined;
  // annotations
  const widgetRefs = [];
  pages.forEach((p) => {
    const node = p.node;
    node.delete(pcN('B')); // article beads point at other pages
    const annots = node.lookup(pcN('Annots'));
    if(!(annots instanceof PDFArray)) return;
    for(let i = 0; i < annots.size(); i++){
      const aRef = annots.get(i);
      const a = annots.lookup(i);
      if(!(a instanceof PDFDict)) continue;
      a.delete(pcN('P'));
      const sub = a.get(pcN('Subtype'));
      if(sub === pcN('Widget') && aRef instanceof PDFRef) widgetRefs.push(aRef);
      if(sub === pcN('Link')){
        let d = a.get(pcN('Dest'));
        const act = a.lookup(pcN('A'));
        if(d === undefined) d = gotoDest(act);
        if(d !== undefined){
          const r = resolveDest(d, 0);
          if(r){ a.set(pcN('Dest'), marker(r)); if(gotoDest(act) !== undefined) a.delete(pcN('A')); }
          else { a.set(pcN('Dest'), ctx.obj([PDFName.of('PCPGNONE')])); a.delete(pcN('A')); }
        }
      }
    }
  });
  // bookmarks, read into plain JS so they can be filtered per result
  const readItems = (firstRef, depth, seen) => {
    const items = [];
    let ref = firstRef, guard = 0;
    while(ref && guard++ < 100000){
      const key = ref.toString ? ref.toString() : String(ref);
      if(seen.has(key)) break;
      seen.add(key);
      const d = ctx.lookup(ref);
      if(!(d instanceof PDFDict)) break;
      let dest = d.get(pcN('Dest'));
      const act = d.lookup(pcN('A'));
      let action = null;
      if(dest === undefined){ dest = gotoDest(act); if(dest === undefined && act) action = d.get(pcN('A')); }
      const count = d.lookup(pcN('Count'));
      items.push({
        title: d.lookup(pcN('Title')),
        dest: dest !== undefined ? resolveDest(dest, 0) : null,
        action,
        color: d.get(pcN('C')),
        flags: d.get(pcN('F')),
        open: count instanceof PDFNumber ? count.asNumber() > 0 : false,
        children: depth < 64 ? readItems(d.get(pcN('First')), depth + 1, seen) : [],
      });
      ref = d.get(pcN('Next'));
    }
    return items;
  };
  const outlines = cat.lookup(pcN('Outlines'));
  const outline = outlines instanceof PDFDict ? readItems(outlines.get(pcN('First')), 0, new Set()) : [];
  // top-level form fields that own a widget on some page
  const topOf = new Map();
  widgetRefs.forEach(wr => {
    let ref = wr, obj = ctx.lookup(wr), guard = 0;
    while(obj instanceof PDFDict && obj.get(pcN('Parent')) instanceof PDFRef && guard++ < 64){
      ref = obj.get(pcN('Parent')); obj = ctx.lookup(ref);
    }
    topOf.set(wr.toString(), ref);
  });
  const acro = cat.lookup(pcN('AcroForm'));
  // attachments
  let embedded = [];
  if(namesDict instanceof PDFDict){
    const ef = namesDict.lookup(pcN('EmbeddedFiles'));
    if(ef) embedded = pcNameTreeEntries(ef);
  }
  // page labels ("i, ii, iii, 1, 2 ..."), one effective label per page
  let labels = null;
  const pl = cat.lookup(pcN('PageLabels'));
  if(pl instanceof PDFDict){
    const entries = pcNumberTreeEntries(pl).sort((a, b) => a[0] - b[0]);
    if(entries.length){
      labels = pages.map((_, i) => {
        let e = null;
        for(const x of entries){ if(x[0] <= i) e = x; else break; }
        if(!e) return { S: null, P: '', St: i + 1 };
        const d = ctx.lookup(e[1]);
        const S = d instanceof PDFDict ? d.lookup(pcN('S')) : null;
        const P = d instanceof PDFDict ? d.lookup(pcN('P')) : null;
        const St = d instanceof PDFDict ? d.lookup(pcN('St')) : null;
        return { S: S instanceof PDFName ? S.asString().slice(1) : null, P: P ? (pcText(P) || '') : '', St: (St instanceof PDFNumber ? St.asNumber() : 1) + (i - e[0]) };
      });
    }
  }
  // tagged PDF: every page reference inside the structure tree becomes a
  // /PCPG marker (as for links), and the top elements lose their link to
  // the old root, so copying an element never drags the whole old tree or
  // an orphan page along.
  let struct = null;
  const str = cat.lookup(pcN('StructTreeRoot'));
  if(str instanceof PDFDict){
    const seen = new Set();
    const mark = d => {
      const pg = d.get(pcN('Pg'));
      if(pg instanceof PDFRef){
        const idx = idxByRef.get(pg.toString());
        d.set(pcN('Pg'), PDFName.of(idx != null ? 'PCPG' + idx : 'PCPGNONE'));
      }
    };
    // (v157) and per element the pages it touches (pagesOf), so a split part
    // copies only its own elements instead of the whole tree every time
    const pagesOf = new WeakMap(), none = new Set();
    const pgIdx = (d, inherited) => {
      const pg = d.get(pcN('Pg'));
      if(pg instanceof PDFName){ const n = parseInt(pg.asString().slice(5), 10); return isNaN(n) ? null : n; }
      return inherited;
    };
    const walk = (o, depth, inherited) => {
      if(depth > 200) return none;
      const key = o instanceof PDFRef ? o.toString() : null;
      const v = o instanceof PDFRef ? ctx.lookup(o) : o;
      if(key){ if(seen.has(key)) return (v && pagesOf.get(v)) || none; seen.add(key); }
      if(v instanceof PDFArray){
        const all = new Set();
        for(let i = 0; i < v.size(); i++){
          const kid = v.get(i);
          if(kid instanceof PDFNumber){ if(inherited != null) all.add(inherited); }
          else walk(kid, depth + 1, inherited).forEach(n => all.add(n));
        }
        return all;
      }
      if(v instanceof PDFNumber) return inherited != null ? new Set([inherited]) : none;
      if(!(v instanceof PDFDict)) return none;
      mark(v);
      const own = pgIdx(v, inherited);
      const k = v.get(pcN('K'));
      const set = k !== undefined ? new Set(walk(k, depth + 1, own)) : new Set();
      if(own != null && (k === undefined || v.lookup(pcN('Type')) === pcN('MCR') || v.lookup(pcN('Type')) === pcN('OBJR'))) set.add(own);
      pagesOf.set(v, set);
      return set;
    };
    const topK = str.get(pcN('K'));
    const tops = [];
    const addTop = o => { const v = o instanceof PDFRef ? ctx.lookup(o) : o; if(v instanceof PDFDict){ v.set(pcN('P'), PDFName.of('PCSTROOT')); tops.push(o); } };
    const tk = topK instanceof PDFRef ? ctx.lookup(topK) : topK;
    if(tk instanceof PDFArray){ for(let i = 0; i < tk.size(); i++) addTop(tk.get(i)); }
    else if(tk instanceof PDFDict) addTop(topK);
    tops.forEach(t => walk(t, 0, null));
    const ptree = str.lookup(pcN('ParentTree'));
    const parents = new Map();
    if(ptree instanceof PDFDict) pcNumberTreeEntries(ptree).forEach(([k, v]) => parents.set(k, v));
    const mi = cat.lookup(pcN('MarkInfo'));
    struct = { tops, parents, pagesOf, roleMap: str.get(pcN('RoleMap')), classMap: str.get(pcN('ClassMap')),
      marked: mi instanceof PDFDict && String(mi.lookup(pcN('Marked'))) === 'true' };
  }
  doc.__pcPrep = { pages, idxByRef, outline, topOf, acro: acro instanceof PDFDict ? acro : null,
    ocp: cat.lookup(pcN('OCProperties')), embedded, labels, struct };
  return doc.__pcPrep;
}

/* (v157) Copies a structure element with only the kids that touch a chosen
   page (want: donor page indices), instead of cp.copy of the whole element:
   splitting a tagged 400-page file page by page copied the whole tree 400
   times (7 min). The copier learns every copied element, so the parent tree
   (copied later) points at these copies. Returns the new ref, or null when
   nothing of the element is on a chosen page. */
const PC_STRUCT_INDEX_MIN = 64;
const PC_STRUCT_KID_INDEX = new WeakMap();
function pcStructCopier(dctx, octx, cp, pagesOf, want){
  const { PDFRef, PDFDict, PDFArray, PDFNumber } = PDFLib;
  const N_K = pcN('K'), N_P = pcN('P'), N_S = pcN('S'), N_TYPE = pcN('Type'), N_MCR = pcN('MCR'), N_OBJR = pcN('OBJR');
  const elsewhere = v => { const s = pagesOf.get(v); if(!s || !s.size) return false; for(const n of s) if(want.has(n)) return false; return true; }; // no page known: kept, as before
  // a long kid list (a /Document with every paragraph of the file) is indexed
  // by page once per donor, so each part only visits its own kids
  const kidsToVisit = kv => {
    const n = kv.size();
    if(n <= PC_STRUCT_INDEX_MIN) return Array.from({ length: n }, (_, i) => i);
    let ix = PC_STRUCT_KID_INDEX.get(kv);
    if(!ix){
      ix = { always: [], byPage: new Map() };
      for(let i = 0; i < n; i++){
        const kid = kv.get(i), kd = kid instanceof PDFRef ? dctx.lookup(kid) : kid;
        const ps = kd instanceof PDFDict ? pagesOf.get(kd) : null;
        if(!ps || !ps.size){ ix.always.push(i); continue; }
        ps.forEach(p => { if(!ix.byPage.has(p)) ix.byPage.set(p, []); ix.byPage.get(p).push(i); });
      }
      PC_STRUCT_KID_INDEX.set(kv, ix);
    }
    const pick = new Set(ix.always);
    want.forEach(p => (ix.byPage.get(p) || []).forEach(i => pick.add(i)));
    return Array.from(pick).sort((a, b) => a - b);
  };
  const copyEl = (o, depth) => {
    const v = o instanceof PDFRef ? dctx.lookup(o) : o;
    if(!(v instanceof PDFDict) || depth > 200) return cp.copy(o);
    if(elsewhere(v)) return null;
    const type = v.get(N_TYPE);
    if(type === N_MCR || type === N_OBJR) return cp.copy(o); // small; prune() decides
    if(o instanceof PDFRef && cp.traversedObjects.has(o)) return cp.traversedObjects.get(o);
    const ref = octx.nextRef();
    if(o instanceof PDFRef) cp.traversedObjects.set(o, ref);
    const nd = octx.obj({});
    for(const [k, val] of v.entries()) if(k !== N_K && k !== N_P) nd.set(k, cp.copy(val));
    const k = v.get(N_K);
    const kv = k instanceof PDFRef ? dctx.lookup(k) : k;
    const kidCopy = kid => {
      if(kid instanceof PDFNumber) return kid;
      const c = copyEl(kid, depth + 1);
      const cd = c instanceof PDFRef ? octx.lookup(c) : c;
      if(cd instanceof PDFDict && cd.has(N_S)) cd.set(N_P, ref);
      return c;
    };
    if(kv instanceof PDFArray){
      const arr = [];
      for(const i of kidsToVisit(kv)){ const c = kidCopy(kv.get(i)); if(c !== null) arr.push(c); }
      nd.set(N_K, octx.obj(arr));
    }else if(k !== undefined){
      const c = kidCopy(k);
      if(c !== null) nd.set(N_K, c);
    }
    octx.assign(ref, nd);
    return ref;
  };
  return copyEl;
}

/* Builds a new PDF from parts = [{ doc, indices }] (indices 0-based, in
   the order the pages should appear). */
/* (v94) The donor's calculation order (/CO) limited to fields whose
   top-level field is copied (tops: Map of top-level field refs). */
function pcKeptCalcOrder(doc, prep, tops){
  const co = prep.acro.lookup(pcN('CO'));
  if(!(co instanceof PDFLib.PDFArray)) return [];
  const keep = [];
  for(let i = 0; i < co.size(); i++){
    const r = co.get(i);
    if(r instanceof PDFLib.PDFRef && tops.has(pcFieldTop(doc.context, r).toString())) keep.push(r);
  }
  return keep;
}
const PC_MAX_FIELD_DEPTH = 64; // same guard as the widget walk in pcPrepDonor
function pcFieldTop(ctx, ref){
  let cur = ref;
  for(let depth = 0; depth < PC_MAX_FIELD_DEPTH; depth++){
    const d = ctx.lookup(cur);
    const parent = d instanceof PDFLib.PDFDict ? d.get(pcN('Parent')) : null;
    if(!(parent instanceof PDFLib.PDFRef)) return cur;
    cur = parent;
  }
  return cur;
}
/* (v157) opts.part: the result is a part of one document (split, extract),
   so the document's attachments are not handed along with every part. */
async function pcAssemble(parts, opts){
  const { PDFDocument, PDFObjectCopier, PDFPage, PDFDict, PDFArray, PDFRef, PDFName, PDFNumber, PDFString, PDFHexString, PDFBool } = PDFLib;
  const out = await PDFDocument.create({ updateMetadata: false });
  const octx = out.context;
  const copiers = new Map();
  const copierFor = d => { if(!copiers.has(d)) copiers.set(d, PDFObjectCopier.for(d.context, octx)); return copiers.get(d); };
  const donorIds = new Map();
  const donorId = d => { if(!donorIds.has(d)) donorIds.set(d, donorIds.size); return donorIds.get(d); };
  const newRefOf = new Map();        // "donor:idx" -> new page ref (first copy)
  const placed = [];                 // [{ donor, idx, ref, node }]
  for(const part of parts){
    const prep = pcPrepDonor(part.doc);
    const cp = copierFor(part.doc);
    const did = donorId(part.doc);
    for(const idx of part.indices){
      pcCheckCancel();
      pcSlimForCopy(part.doc, prep, idx); // (v73) a page that shares its resources takes only its own along
      const src = prep.pages[idx];
      const leaf = cp.copy(src.node);
      const ref = octx.register(leaf);
      out.addPage(PDFPage.of(leaf, ref, out));
      const key = did + ':' + idx;
      if(!newRefOf.has(key)) newRefOf.set(key, ref);
      placed.push({ donor: part.doc, did, idx, ref, node: leaf });
    }
  }
  const mapMarker = (did, arr) => {
    if(!(arr instanceof PDFArray) || !arr.size()) return null;
    const m = arr.get(0);
    if(!(m instanceof PDFName)) return arr;
    const s = m.asString();
    if(s.indexOf('/PCPG') !== 0) return arr;
    const idx = parseInt(s.slice(5), 10);
    const ref = Number.isFinite(idx) ? newRefOf.get(did + ':' + idx) : null;
    if(!ref) return null;
    arr.set(0, ref);
    return arr;
  };
  // annotations: page links + /P
  const liveAnnots = new Set();
  placed.forEach(pl => {
    const annots = pl.node.lookup(pcN('Annots'));
    if(!(annots instanceof PDFArray)) return;
    for(let i = annots.size() - 1; i >= 0; i--){
      const aRef = annots.get(i);
      const a = annots.lookup(i);
      if(!(a instanceof PDFDict)) continue;
      if(a.get(pcN('Subtype')) === pcN('Link')){
        const d = a.lookup(pcN('Dest'));
        if(d instanceof PDFArray && d.get(0) instanceof PDFName && d.get(0).asString().indexOf('/PCPG') === 0){
          if(!mapMarker(pl.did, d)){ annots.remove(i); continue; }
        }
      }
      a.set(pcN('P'), pl.ref);
      if(aRef instanceof PDFRef) liveAnnots.add(aRef.toString());
    }
  });
  // bookmarks
  const buildItems = (items, did) => {
    const out2 = [];
    items.forEach(it => {
      const kids = buildItems(it.children, did);
      let destRef = null;
      if(it.dest){ destRef = newRefOf.get(did + ':' + it.dest.idx) || null; }
      const keep = destRef || kids.length || (it.action && !it.dest);
      if(!keep) return;
      out2.push({ it, destRef, kids, did });
    });
    return out2;
  };
  let top = [];
  parts.forEach(part => {
    const did = donorId(part.doc);
    if(part.__outlineDone) return;
    const prep = pcPrepDonor(part.doc);
    // each donor's bookmarks once, even when it contributes several parts
    if(parts.filter(p => p.doc === part.doc)[0] !== part) return;
    top = top.concat(buildItems(prep.outline, did));
  });
  if(top.length){
    const visible = n => n.kids.reduce((s, k) => s + 1 + (k.it.open ? visible(k) : 0), 0);
    const outlinesRef = octx.nextRef();
    const write = (nodes, parentRef) => {
      const refs = nodes.map(() => octx.nextRef());
      nodes.forEach((n, i) => {
        const d = octx.obj({});
        const title = n.it.title;
        d.set(pcN('Title'), (title instanceof PDFString || title instanceof PDFHexString) ? title : PDFHexString.fromText(''));
        d.set(pcN('Parent'), parentRef);
        if(i > 0) d.set(pcN('Prev'), refs[i - 1]);
        if(i < nodes.length - 1) d.set(pcN('Next'), refs[i + 1]);
        if(n.destRef) d.set(pcN('Dest'), octx.obj([n.destRef, ...n.it.dest.rest]));
        else if(n.it.action && !n.it.dest) d.set(pcN('A'), copierFor(parts.find(p => donorId(p.doc) === n.did).doc).copy(n.it.action));
        if(n.it.color instanceof PDFArray) d.set(pcN('C'), n.it.color.clone(octx));
        if(n.it.flags instanceof PDFNumber) d.set(pcN('F'), n.it.flags);
        if(n.kids.length){
          const kr = write(n.kids, refs[i]);
          d.set(pcN('First'), kr[0]); d.set(pcN('Last'), kr[kr.length - 1]);
          d.set(pcN('Count'), PDFNumber.of(n.it.open ? visible(n) : -n.kids.length));
        }
        octx.assign(refs[i], d);
      });
      return refs;
    };
    const topRefs = write(top, outlinesRef);
    const total = top.reduce((s, n) => s + 1 + (n.it.open ? visible(n) : 0), 0);
    octx.assign(outlinesRef, octx.obj({ Type: 'Outlines', First: topRefs[0], Last: topRefs[topRefs.length - 1], Count: total }));
    out.catalog.set(pcN('Outlines'), outlinesRef);
  }
  // form fields
  const fieldRefs = [];
  const fieldNames = new Set();
  const coRefs = [];                 // (v94) calculation order of every donor
  const renamedDonors = [], xfaDonors = [];
  let acroOut = null;
  const donorsSeen = new Set();
  parts.forEach(part => {
    if(donorsSeen.has(part.doc)) return;
    donorsSeen.add(part.doc);
    const prep = pcPrepDonor(part.doc);
    if(!prep.acro) return;
    if(pcXfaKind(part.doc) === 'hybrid') xfaDonors.push(part.doc); // its XFA part is not kept
    let renamed = false;
    const did = donorId(part.doc);
    const cp = copierFor(part.doc);
    const wanted = new Set(placed.filter(p => p.did === did).map(p => p.idx));
    const tops = new Map();
    prep.pages.forEach((p, i) => {
      if(!wanted.has(i)) return;
      const annots = p.node.lookup(pcN('Annots'));
      if(!(annots instanceof PDFArray)) return;
      for(let k = 0; k < annots.size(); k++){
        const r = annots.get(k);
        if(r instanceof PDFRef && prep.topOf.has(r.toString())){ const t = prep.topOf.get(r.toString()); tops.set(t.toString(), t); }
      }
    });
    if(!acroOut){
      acroOut = octx.obj({});
      for(const [k, v] of prep.acro.entries()){
        const ks = k.asString();
        if(ks === '/Fields' || ks === '/XFA' || ks === '/CO') continue;
        acroOut.set(k, cp.copy(v));
      }
    }else{
      const na = prep.acro.lookup(pcN('NeedAppearances'));
      if(na && na.toString() === 'true') acroOut.set(pcN('NeedAppearances'), PDFBool.True);
      const dr = prep.acro.lookup(pcN('DR'));
      if(dr instanceof PDFDict){
        let odr = acroOut.lookup(pcN('DR'));
        if(!(odr instanceof PDFDict)){ odr = octx.obj({}); acroOut.set(pcN('DR'), odr); }
        const cdr = cp.copy(dr);
        const cdrD = cdr instanceof PDFRef ? octx.lookup(cdr) : cdr;
        if(cdrD instanceof PDFDict) for(const [k, v] of cdrD.entries()){
          const cur = odr.lookup(k);
          const vv = v instanceof PDFRef ? octx.lookup(v) : v;
          if(cur instanceof PDFDict && vv instanceof PDFDict){ for(const [k2, v2] of vv.entries()) if(!cur.has(k2)) cur.set(k2, v2); }
          else if(!odr.has(k)) odr.set(k, v);
        }
      }
    }
    for(const t of tops.values()){
      const nr = cp.copy(t);
      const fd = octx.lookup(nr);
      if(fd instanceof PDFDict){
        let name = pcText(fd.lookup(pcN('T'))) || '';
        if(name && fieldNames.has(name)){
          let n = 2; while(fieldNames.has(name + '_' + n)) n++;
          name = name + '_' + n;
          fd.set(pcN('T'), PDFHexString.fromText(name));
          renamed = true;
        }
        if(name) fieldNames.add(name);
        fd.delete(pcN('Parent'));
        // widgets of this field that sit on pages NOT in the result
        const prune = (d, depth) => {
          const kids = d.lookup(pcN('Kids'));
          if(!(kids instanceof PDFArray) || depth > 32) return;
          for(let k = kids.size() - 1; k >= 0; k--){
            const kr = kids.get(k), kd = kids.lookup(k);
            if(!(kd instanceof PDFDict)) continue;
            const isWidget = kd.get(pcN('Subtype')) === pcN('Widget');
            if(isWidget && kr instanceof PDFRef && !liveAnnots.has(kr.toString())){ kids.remove(k); continue; }
            prune(kd, depth + 1);
            const sub = kd.lookup(pcN('Kids'));
            if(!isWidget && sub instanceof PDFArray && !sub.size()) kids.remove(k);
          }
        };
        prune(fd, 0);
      }
      fieldRefs.push(nr);
    }
    if(renamed) renamedDonors.push(part.doc);
    // (v94) calculations only of fields that are in the result, and not for a
    // file whose fields were renamed: its scripts would read the other file's
    // field of that name and compute wrong totals
    else coRefs.push(...pcKeptCalcOrder(part.doc, prep, tops).map(r => cp.copy(r)));
  });
  if(acroOut){
    if(coRefs.length) acroOut.set(pcN('CO'), octx.obj(coRefs));
    acroOut.set(pcN('Fields'), octx.obj(fieldRefs));
    out.catalog.set(pcN('AcroForm'), octx.register(acroOut));
  }
  // layers: keep every layer and its default on/off state
  const ocgs = [], on = [], off = [], order = [];
  donorsSeen.clear();
  parts.forEach(part => {
    if(donorsSeen.has(part.doc)) return;
    donorsSeen.add(part.doc);
    const prep = pcPrepDonor(part.doc);
    const ocp = prep.ocp;
    if(!(ocp instanceof PDFDict)) return;
    const cp = copierFor(part.doc);
    const list = ocp.lookup(pcN('OCGs'));
    if(!(list instanceof PDFArray)) return;
    const D = ocp.lookup(pcN('D'));
    const srcOn = D instanceof PDFDict ? D.lookup(pcN('ON')) : null;
    const srcOff = D instanceof PDFDict ? D.lookup(pcN('OFF')) : null;
    const base = D instanceof PDFDict ? D.lookup(pcN('BaseState')) : null;
    const inArr = (arr, r) => arr instanceof PDFArray && arr.asArray().some(x => x instanceof PDFRef && x.toString() === r.toString());
    for(let i = 0; i < list.size(); i++){
      const r = list.get(i);
      if(!(r instanceof PDFRef)) continue;
      const nr = cp.copy(r);
      ocgs.push(nr);
      const isOff = inArr(srcOff, r) || (base === pcN('OFF') && !inArr(srcOn, r));
      (isOff ? off : on).push(nr);
    }
    const ord = D instanceof PDFDict ? D.lookup(pcN('Order')) : null;
    if(ord instanceof PDFArray) for(let i = 0; i < ord.size(); i++) order.push(cp.copy(ord.get(i)));
  });
  if(ocgs.length){
    const D = octx.obj({ BaseState: 'ON' });
    if(on.length) D.set(pcN('ON'), octx.obj(on));
    if(off.length) D.set(pcN('OFF'), octx.obj(off));
    if(order.length) D.set(pcN('Order'), octx.obj(order));
    out.catalog.set(pcN('OCProperties'), octx.obj({ OCGs: octx.obj(ocgs), D }));
  }
  // attachments
  const files = [];
  const fileNames = new Set();
  donorsSeen.clear();
  parts.forEach(part => {
    if(donorsSeen.has(part.doc)) return;
    donorsSeen.add(part.doc);
    const prep = pcPrepDonor(part.doc);
    const cp = copierFor(part.doc);
    if(opts && opts.part) return;
    prep.embedded.forEach(([name, spec]) => {
      let n = name, k = 2;
      while(fileNames.has(n)) n = name.replace(/(\.[^.]*)?$/, m => ' (' + (k++) + ')' + m);
      fileNames.add(n);
      files.push([n, cp.copy(spec)]);
    });
  });
  if(files.length){
    files.sort((a, b) => a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0);
    const arr = [];
    files.forEach(([n, s]) => { arr.push(PDFHexString.fromText(n)); arr.push(s); });
    out.catalog.set(pcN('Names'), octx.obj({ EmbeddedFiles: octx.obj({ Names: octx.obj(arr) }) }));
  }
  // page labels (v47)
  if(parts.some(p => pcPrepDonor(p.doc).labels)){
    const nums = [];
    let prev = null;
    placed.forEach((pl, i) => {
      const lab = pcPrepDonor(pl.donor).labels;
      const L = lab ? lab[pl.idx] : { S: 'D', P: '', St: pl.idx + 1 };
      const cont = prev && prev.S === L.S && prev.P === L.P && L.St === prev.St + 1;
      if(!cont){
        const d = octx.obj({});
        if(L.S) d.set(pcN('S'), PDFName.of(L.S));
        if(L.P) d.set(pcN('P'), PDFHexString.fromText(L.P));
        if(L.St !== 1) d.set(pcN('St'), PDFNumber.of(L.St));
        nums.push(PDFNumber.of(i), d);
      }
      prev = L;
    });
    out.catalog.set(pcN('PageLabels'), octx.obj({ Nums: octx.obj(nums) }));
  }
  // tagged PDF structure (v47): elements for the pages in the result, with
  // a fresh parent tree
  if(parts.some(p => pcPrepDonor(p.doc).struct)){
    const rootRef = octx.nextRef();
    const topRefs = [];
    let roleMap = null, classMap = null, marked = false;
    const doneDonors = new Set();
    parts.forEach(part => {
      if(doneDonors.has(part.doc)) return;
      doneDonors.add(part.doc);
      const prep = pcPrepDonor(part.doc);
      if(!prep.struct) return;
      const cp = copierFor(part.doc), did = donorId(part.doc);
      marked = marked || prep.struct.marked;
      const mapPg = d => {
        const pg = d.get(pcN('Pg'));
        if(pg instanceof PDFName && pg.asString().indexOf('/PCPG') === 0){
          const ref = newRefOf.get(did + ':' + parseInt(pg.asString().slice(5), 10));
          if(ref) d.set(pcN('Pg'), ref); else { d.delete(pcN('Pg')); return false; }
        }
        return true;
      };
      // returns false when nothing of this element is left
      const prune = (ref, depth, pageOk) => {
        const d = ref instanceof PDFRef ? octx.lookup(ref) : ref;
        if(!(d instanceof PDFDict) || depth > 200) return false;
        let ok = pageOk;
        if(d.has(pcN('Pg'))) ok = mapPg(d);
        const type = d.lookup(pcN('Type'));
        if(type === pcN('MCR')) return ok;
        if(type === pcN('OBJR')){ const o = d.get(pcN('Obj')); return ok && o instanceof PDFRef && liveAnnots.has(o.toString()); }
        const k = d.get(pcN('K'));
        if(k === undefined) return true;
        const kv = k instanceof PDFRef ? octx.lookup(k) : k;
        const keep = kid => {
          if(kid instanceof PDFNumber) return ok;
          return prune(kid, depth + 1, ok);
        };
        if(kv instanceof PDFArray){
          for(let i = kv.size() - 1; i >= 0; i--) if(!keep(kv.get(i))) kv.remove(i);
          return kv.size() > 0;
        }
        if(!keep(k)){ d.delete(pcN('K')); return false; }
        return true;
      };
      const want = new Set();
      parts.forEach(p => { if(p.doc === part.doc) p.indices.forEach(i => want.add(i)); });
      const copyEl = pcStructCopier(part.doc.context, octx, cp, prep.struct.pagesOf, want);
      prep.struct.tops.forEach(t => {
        const nr = copyEl(t, 0);
        if(nr === null) return; // nothing of this element is on a chosen page
        const nd = nr instanceof PDFRef ? octx.lookup(nr) : nr;
        if(nd instanceof PDFDict) nd.set(pcN('P'), rootRef);
        if(prune(nr, 0, true)) topRefs.push(nr instanceof PDFRef ? nr : octx.register(nr));
      });
      if(prep.struct.roleMap && !roleMap) roleMap = cp.copy(prep.struct.roleMap);
      else if(prep.struct.roleMap){
        const src = cp.copy(prep.struct.roleMap), s2 = src instanceof PDFRef ? octx.lookup(src) : src, t2 = roleMap instanceof PDFRef ? octx.lookup(roleMap) : roleMap;
        if(s2 instanceof PDFDict && t2 instanceof PDFDict) for(const [k, v] of s2.entries()) if(!t2.has(k)) t2.set(k, v);
      }
      if(prep.struct.classMap && !classMap) classMap = cp.copy(prep.struct.classMap);
    });
    // parent tree: page -> array of its elements, annotation -> element
    const nums = [];
    let next = 0;
    placed.forEach(pl => {
      const prep = pcPrepDonor(pl.donor);
      if(!prep.struct) return;
      const cp = copierFor(pl.donor);
      const sp = pl.node.lookup(pcN('StructParents'));
      if(sp instanceof PDFNumber && prep.struct.parents.has(sp.asNumber())){
        pl.node.set(pcN('StructParents'), PDFNumber.of(next));
        nums.push(PDFNumber.of(next++), cp.copy(prep.struct.parents.get(sp.asNumber())));
      }else if(sp) pl.node.delete(pcN('StructParents'));
      const annots = pl.node.lookup(pcN('Annots'));
      if(annots instanceof PDFArray) for(let i = 0; i < annots.size(); i++){
        const a = annots.lookup(i);
        const k = a instanceof PDFDict ? a.lookup(pcN('StructParent')) : null;
        if(k instanceof PDFNumber && prep.struct.parents.has(k.asNumber())){
          a.set(pcN('StructParent'), PDFNumber.of(next));
          nums.push(PDFNumber.of(next++), cp.copy(prep.struct.parents.get(k.asNumber())));
        }else if(k) a.delete(pcN('StructParent'));
      }
    });
    if(topRefs.length){
      const root = octx.obj({ Type: 'StructTreeRoot', K: octx.obj(topRefs), ParentTree: octx.obj({ Nums: octx.obj(nums) }), ParentTreeNextKey: next });
      if(roleMap) root.set(pcN('RoleMap'), roleMap);
      if(classMap) root.set(pcN('ClassMap'), classMap);
      octx.assign(rootRef, root);
      out.catalog.set(pcN('StructTreeRoot'), rootRef);
      if(marked) out.catalog.set(pcN('MarkInfo'), octx.obj({ Marked: true }));
    }
  }
  // document info + catalog settings of the first document
  const first = parts[0] && parts[0].doc;
  if(first){
    const cp = copierFor(first);
    const safe = f => { try{ return f(); }catch(e){ return undefined; } };
    const t = safe(() => first.getTitle()); if(t) out.setTitle(t);
    const a = safe(() => first.getAuthor()); if(a) out.setAuthor(a);
    const s = safe(() => first.getSubject()); if(s) out.setSubject(s);
    // (v157) as written: splitting on spaces too lost the separators ("client: ACME; paid")
    const kw = safe(() => first.getInfoDict().get(pcN('Keywords'))); if(kw && pcText(kw)) out.getInfoDict().set(pcN('Keywords'), copierFor(first).copy(kw));
    const cr = safe(() => first.getCreator()); if(cr) out.setCreator(cr);
    const cd = safe(() => first.getCreationDate()); if(cd) out.setCreationDate(cd);
    out.setModificationDate(new Date());
    out.setProducer('pdfcommunity.org (pdf-lib)');
    ['Lang', 'ViewerPreferences', 'PageLayout'].forEach(k => {
      const v = first.catalog.get(pcN(k));
      if(v !== undefined) out.catalog.set(pcN(k), cp.copy(v));
    });
    const pm = first.catalog.lookup(pcN('PageMode'));
    if(pm instanceof PDFName){
      const m = pm.asString();
      if((m === '/UseOutlines' && top.length) || (m === '/UseOC' && ocgs.length) || (m === '/UseAttachments' && files.length) || m === '/FullScreen') out.catalog.set(pcN('PageMode'), pm);
    }
  }
  // objects copied along but no longer used (a structure element of a
  // page that isn't in the result, ...) are left out of the file
  pcCollectGarbage(out);
  // (v94) for the note under the result (pcAssembleNotes in app.js)
  out.__pcFormInfo = { renamedDonors, xfaDonors };
  return out;
}

/* Keeps only objects still reachable from the document (after pages were
   removed in place, or images replaced), so dropped pages really leave. */
function pcCollectGarbage(doc){
  const { PDFRef, PDFDict, PDFArray, PDFRawStream } = PDFLib;
  const ctx = doc.context;
  const seen = new Set();
  const stack = [];
  const push = o => { if(o) stack.push(o); };
  push(ctx.trailerInfo.Root); push(ctx.trailerInfo.Info); push(ctx.trailerInfo.Encrypt);
  while(stack.length){
    const o = stack.pop();
    if(o instanceof PDFRef){
      const k = o.toString();
      if(seen.has(k)) continue;
      seen.add(k);
      push(ctx.lookup(o));
    }else if(o instanceof PDFDict){
      for(const [, v] of o.entries()) push(v);
    }else if(o instanceof PDFArray){
      for(let i = 0; i < o.size(); i++) push(o.get(i));
    }else if(o instanceof PDFRawStream || (o && o.dict instanceof PDFDict)){
      push(o.dict);
    }
  }
  let removed = 0;
  for(const [ref] of ctx.enumerateIndirectObjects()){
    if(!seen.has(ref.toString())){ ctx.delete(ref); removed++; }
  }
  return removed;
}

/* (v157) A page that is no longer in the page tree (deleted, or not part of
   this split/extract) can still be pointed at by a form button's or an
   open action's GoTo, which kept the whole page (text and all) in the file.
   Every such pointer becomes null; the garbage collection then drops it. */
function pcDropStrayPages(doc){
  const { PDFRef, PDFDict, PDFArray, PDFNull } = PDFLib;
  const ctx = doc.context;
  const inTree = new Set(doc.getPages().map(p => p.ref.toString()));
  const stray = new Map();
  const isStray = v => {
    if(!(v instanceof PDFRef)) return false;
    const k = v.toString();
    if(inTree.has(k)) return false;
    if(!stray.has(k)){ const o = ctx.lookup(v); stray.set(k, o instanceof PDFDict && o.get(pcN('Type')) === pcN('Page')); }
    return stray.get(k);
  };
  const fix = (o, depth) => {
    if(depth > PC_STRAY_DEPTH) return;
    if(o && !(o instanceof PDFDict) && o.dict instanceof PDFDict) o = o.dict; // a stream
    if(o instanceof PDFDict){
      for(const [k, v] of o.entries()){
        if(isStray(v)) o.set(k, PDFNull);
        else if(!(v instanceof PDFRef)) fix(v, depth + 1);
      }
    }else if(o instanceof PDFArray){
      for(let i = 0; i < o.size(); i++){
        const v = o.get(i);
        if(isStray(v)) o.set(i, PDFNull);
        else if(!(v instanceof PDFRef)) fix(v, depth + 1);
      }
    }
  };
  for(const [ref, o] of ctx.enumerateIndirectObjects()){
    if(isStray(ref)) continue; // the stray page itself goes as a whole
    fix(o, 0);
  }
}
const PC_STRAY_DEPTH = 32;

/* (v157) Every pdf-lib save goes through here: objects no longer reachable
   (old revisions of an incrementally saved file: earlier Info/XMP, replaced
   page content) would otherwise be written along and stay readable. */
async function pcSavePdf(doc, opts){
  await doc.flush(); // pending embeds (a page drawn onto a sheet is copied in only now) must be in the file before the clean-up
  pcDropStrayPages(doc);
  pcCollectGarbage(doc);
  return doc.save(pcSaveOptions(opts || {}));
}

/* Content streams of a page are wrapped in q ... Q, so a page whose own
   drawing leaves a transformation or colour changed at the end can't
   shift, scale or recolour what is stamped on top of it. */
function pcWrapContent(doc, page){
  try{
    const ctx = doc.context;
    // (v111) one q and one Q stream per document (was two new objects per page)
    if(!doc.__pcWrapRefs) doc.__pcWrapRefs = [ctx.register(ctx.stream('q')), ctx.register(ctx.stream('Q'))];
    page.node.wrapContentStreams(doc.__pcWrapRefs[0], doc.__pcWrapRefs[1]);
  }catch(e){ pcLogUnexpected(e); }
}
/* (v90) A box may be written with any two opposite corners ([0 842 595 0]);
   pdf-lib then gives a negative width/height, which put stamps off the page
   and mirrored n-up pages. Viewers normalise it, so this does too. */
function pcNormRect(r){
  return { x: Math.min(r.x, r.x + r.width), y: Math.min(r.y, r.y + r.height), width: Math.abs(r.width), height: Math.abs(r.height) };
}
/* (v90) A page without any MediaBox (not on the page, not inherited) is
   shown as US Letter by pdf.js and Acrobat; the tools did "Is it a valid
   PDF?". It gets that box written on the page, so the result is valid too. */
const PC_DEFAULT_MEDIABOX = [0, 0, 612, 792];
/* (v157) ...and so is a box without any area ([0 0 0 0], or unreadable
   numbers): pdf.js and pdfium show that page as US Letter too, while N-up
   divided by its width of 0 and failed with "Is it a valid PDF?". */
function pcMediaBox(page){
  const box = page.node.getInheritableAttribute(pcN('MediaBox')) ? pcNormRect(page.getMediaBox()) : null;
  if(box && box.width > 0 && box.height > 0) return box;
  page.setMediaBox(...PC_DEFAULT_MEDIABOX);
  return pcNormRect(page.getMediaBox());
}
/* The page as the reader sees it: the visible (crop) box and the rotation.
   toUser(u, v) maps a point measured from the visible bottom-left corner
   (u to the right, v up, in points) to the page's own coordinates. */
function pcVisualFrame(page){
  const mb = pcMediaBox(page);
  let box;
  try{ box = pcNormRect(page.getCropBox()); }catch(e){ box = mb; }
  // the visible area is the crop box clipped to the media box
  const x0 = Math.max(box.x, mb.x), y0 = Math.max(box.y, mb.y);
  const x1 = Math.min(box.x + box.width, mb.x + mb.width), y1 = Math.min(box.y + box.height, mb.y + mb.height);
  const B = (x1 > x0 && y1 > y0) ? { x: x0, y: y0, width: x1 - x0, height: y1 - y0 } : mb;
  let rot = ((page.getRotation().angle % 360) + 360) % 360;
  if(rot % 90) rot = 0; // (v88) /Rotate must be a multiple of 90; viewers show any other value as 0
  const W = B.width, H = B.height;
  const vw = (rot === 90 || rot === 270) ? H : W;
  const vh = (rot === 90 || rot === 270) ? W : H;
  const toUser = (u, v) => {
    if(rot === 90) return [B.x + W - v, B.y + u];
    if(rot === 180) return [B.x + W - u, B.y + H - v];
    if(rot === 270) return [B.x + v, B.y + H - u];
    return [B.x + u, B.y + v];
  };
  return { box: B, rot, vw, vh, toUser };
}
/* Draws one line of text centred horizontally, `bottom` points above the
   visible bottom edge, upright for the reader. */
function pcStampText(page, text, opts){
  const f = pcVisualFrame(page);
  const w = opts.font.widthOfTextAtSize(text, opts.size);
  const side = opts.side || 36;
  const vx = opts.align === 'left' ? side : opts.align === 'right' ? f.vw - side - w : (f.vw - w) / 2;
  const [x, y] = f.toUser(vx, opts.bottom);
  page.drawText(text, { x, y, size: opts.size, font: opts.font, color: opts.color, rotate: PDFLib.degrees(f.rot) });
}

/* ---------- compress: recompress images inside the PDF ---------- */
function pcPngUnpredict(data, width, colors, bpc, predictor){
  if(!predictor || predictor < 10) return data;
  const bpp = Math.max(1, Math.ceil(colors * bpc / 8));
  const rowLen = Math.ceil(width * colors * bpc / 8);
  const rows = Math.floor(data.length / (rowLen + 1));
  const out = new Uint8Array(rows * rowLen);
  let prev = new Uint8Array(rowLen);
  for(let r = 0; r < rows; r++){
    const type = data[r * (rowLen + 1)];
    const src = data.subarray(r * (rowLen + 1) + 1, (r + 1) * (rowLen + 1));
    const cur = out.subarray(r * rowLen, (r + 1) * rowLen);
    for(let i = 0; i < rowLen; i++){
      const a = i >= bpp ? cur[i - bpp] : 0, b = prev[i], c = i >= bpp ? prev[i - bpp] : 0;
      let v = src[i];
      if(type === 1) v += a;
      else if(type === 2) v += b;
      else if(type === 3) v += (a + b) >> 1;
      else if(type === 4){ const p = a + b - c, pa = Math.abs(p - a), pb = Math.abs(p - b), pc = Math.abs(p - c); v += (pa <= pb && pa <= pc) ? a : (pb <= pc ? b : c); }
      cur[i] = v & 255;
    }
    prev = cur;
  }
  return out;
}
async function pcInflate(data){
  if(typeof DecompressionStream === 'undefined') throw new Error('no DecompressionStream');
  const ds = new DecompressionStream('deflate');
  const buf = await new Response(new Blob([data]).stream().pipeThrough(ds)).arrayBuffer();
  return new Uint8Array(buf);
}
async function pcDeflate(data){
  if(typeof CompressionStream === 'undefined') return null;
  const cs = new CompressionStream('deflate');
  return new Uint8Array(await new Response(new Blob([data]).stream().pipeThrough(cs)).arrayBuffer());
}
/* (v68) A 2D canvas of w x h pixels: a page canvas, or an OffscreenCanvas
   in the worker (which has no document). */
function pcNewCanvas(w, h){
  if(typeof document === 'undefined') return new OffscreenCanvas(w, h);
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  return c;
}
/* The canvas as JPEG bytes, or null when the browser can't encode it (too
   large / out of memory): the caller then keeps the original picture.
   (v68) A browser that can't write JPEG hands back a PNG instead (that is
   what the standard says): that would end up in the file as a broken
   "JPEG", so anything that isn't one counts as "can't". */
function pcCanvasToJpeg(canvas, q){
  const jpegOrNull = u8 => (u8 && u8.length > 2 && u8[0] === 0xFF && u8[1] === 0xD8 ? u8 : null);
  if(typeof canvas.convertToBlob === 'function'){ // OffscreenCanvas (worker)
    return canvas.convertToBlob({ type: 'image/jpeg', quality: q })
      .then(b => b.arrayBuffer())
      .then(ab => jpegOrNull(new Uint8Array(ab)), () => null);
  }
  return new Promise((resolve) => canvas.toBlob(b => {
    if(!b) return resolve(null);
    b.arrayBuffer().then(ab => resolve(jpegOrNull(new Uint8Array(ab))), () => resolve(null));
  }, 'image/jpeg', q));
}
/* (v55) The EXIF orientation (1-8) of a JPEG, 1 when it has none. */
const JPEG_SOI = 0xFFD8, JPEG_APP1 = 0xFFE1, JPEG_SOS = 0xFFDA, EXIF_ORIENTATION_TAG = 0x0112;
function pcJpegExifOrientation(bytes){
  const b = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  const u16 = (o, le) => le ? b[o] | (b[o+1] << 8) : (b[o] << 8) | b[o+1];
  if(b.length < 4 || u16(0) !== JPEG_SOI) return 1;
  for(let o = 2; o + 4 <= b.length; ){
    const marker = u16(o), len = u16(o + 2);
    if(marker === JPEG_SOS || (marker & 0xFF00) !== 0xFF00) return 1;
    if(marker === JPEG_APP1 && b[o+4] === 0x45 && b[o+5] === 0x78 && b[o+6] === 0x69 && b[o+7] === 0x66){ // "Exif"
      const t = o + 10, le = b[t] === 0x49; // "II" = little endian
      const ifd = t + ((le ? b[t+4] | (b[t+5] << 8) | (b[t+6] << 16) | (b[t+7] << 24) : (b[t+4] << 24) | (b[t+5] << 16) | (b[t+6] << 8) | b[t+7]) >>> 0);
      if(ifd + 2 > b.length) return 1;
      const n = u16(ifd, le);
      for(let i = 0; i < n; i++){
        const e = ifd + 2 + i * 12;
        if(e + 10 > b.length) return 1;
        if(u16(e, le) === EXIF_ORIENTATION_TAG){ const v = u16(e + 8, le); return v >= 1 && v <= 8 ? v : 1; }
      }
      return 1;
    }
    o += 2 + len;
  }
  return 1;
}
/* (v55) The same JPEG without its Exif block. A PDF viewer ignores Exif,
   so the pixels are what the reader sees -- but the browser's decoder
   applies the Exif orientation (Chromium now even with imageOrientation
   'none'), which turned such a photo upside down or sideways during
   compression. Decoding the stripped copy keeps the pixels as they are. */
function pcStripJpegExif(bytes){
  const b = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  if(b.length < 4 || ((b[0] << 8) | b[1]) !== JPEG_SOI) return b;
  const keep = [[0, 2]];
  let o = 2, dropped = false;
  while(o + 4 <= b.length){
    const marker = (b[o] << 8) | b[o+1], len = (b[o+2] << 8) | b[o+3];
    if(marker === JPEG_SOS || (marker & 0xFF00) !== 0xFF00 || len < 2) break;
    const isExif = marker === JPEG_APP1 && b[o+4] === 0x45 && b[o+5] === 0x78 && b[o+6] === 0x69 && b[o+7] === 0x66;
    if(isExif) dropped = true; else keep.push([o, o + 2 + len]);
    o += 2 + len;
  }
  if(!dropped) return b;
  keep.push([o, b.length]);
  const out = new Uint8Array(keep.reduce((n, [a, z]) => n + Math.max(0, Math.min(z, b.length) - a), 0));
  let p = 0;
  keep.forEach(([a, z]) => { const part = b.subarray(a, Math.min(z, b.length)); out.set(part, p); p += part.length; });
  return out;
}
/* Decodes a JPEG for re-encoding. createImageBitmap can refuse under memory
   pressure ("could not be decoded" with big files open in other tools), so
   it is retried once and then decoded through an <img> element instead;
   null means "leave this image as it is". */
async function pcDecodeJpeg(bytes){
  const blob = new Blob([pcStripJpegExif(bytes)], { type: 'image/jpeg' });
  const tryBitmap = async opts => { try{ return await createImageBitmap(blob, opts); }catch(e){ return null; } };
  let bmp = await tryBitmap({ colorSpaceConversion: 'none', imageOrientation: 'none' });
  if(!bmp) bmp = await tryBitmap({ colorSpaceConversion: 'none' });
  if(!bmp){ await new Promise(r => setTimeout(r, 50)); bmp = await tryBitmap({ colorSpaceConversion: 'none' }); }
  if(bmp) return bmp;
  if(typeof Image === 'undefined') return null; // (v68) worker: no <img> to fall back on, the image stays as it is
  const url = URL.createObjectURL(blob);
  try{
    const img = new Image();
    img.src = url;
    await img.decode();
    return { width: img.naturalWidth, height: img.naturalHeight, __img: img, close(){ img.src = ''; } };
  }catch(e){ return null; }
  finally{ setTimeout(() => URL.revokeObjectURL(url), 0); }
}
/* (v75) The images other images use as their /SMask (soft mask), by ref tag. */
/* tag of every soft mask -> how many images use it (v157: a mask used by one
   image only is scaled along with it) */
function pcSoftMaskRefs(ctx){
  const { PDFRawStream, PDFRef } = PDFLib;
  const out = new Map();
  for(const [, obj] of ctx.enumerateIndirectObjects()){
    if(!(obj instanceof PDFRawStream) || obj.dict.get(pcN('Subtype')) !== pcN('Image')) continue;
    const sm = obj.dict.get(pcN('SMask'));
    if(sm instanceof PDFRef) out.set(sm.tag, (out.get(sm.tag) || 0) + 1);
  }
  return out;
}
/* (v90) Progress inside one file: the compress jobs report how far they are
   (0..1) through onProgress, at most ~10 times a second (in the worker each
   report is a message to the page). */
const PC_PROGRESS_MIN_MS = 100;
function pcThrottledProgress(onProgress){
  if(typeof onProgress !== 'function') return () => {};
  let last = 0;
  return frac => {
    const now = Date.now();
    if(now - last < PC_PROGRESS_MIN_MS) return;
    last = now;
    onProgress(Math.max(0, Math.min(1, frac)));
  };
}
const PC_COMPRESS_SCAN_SHARE = 0.9; // the image pass; saving the file is the rest
/* (v90) [/ASCII85Decode /DCTDecode] (ReportLab's default) or any stream with
   an ASCII filter in front: that text encoding is decoded, so the image or
   stream behind it can be handled like any other (and it is 25-100% smaller
   already). The stream with the remaining filters, or null when there is no
   ASCII filter in front. A broken ASCII stream throws (left as it is). */
const PC_ASCII_FILTERS = ['ASCII85Decode', 'ASCIIHexDecode'];
function pcRestOfFilters(ctx, nd, rest, restParms){
  if(!rest.length){ nd.delete(pcN('Filter')); nd.delete(pcN('DecodeParms')); return; }
  nd.set(pcN('Filter'), rest.length === 1 ? rest[0] : ctx.obj(rest));
  if(!restParms.some(p => p && p !== PDFLib.PDFNull)){ nd.delete(pcN('DecodeParms')); return; }
  nd.set(pcN('DecodeParms'), rest.length === 1 ? restParms[0] : ctx.obj(rest.map((_, i) => restParms[i] || PDFLib.PDFNull)));
}
function pcPeelAsciiFilter(ctx, obj){
  const { PDFRawStream, PDFArray } = PDFLib;
  const d = obj.dict;
  if(d.get(pcN('Type')) === pcN('XRef')) return null;
  const f = d.lookup(pcN('Filter'));
  const list = f instanceof PDFArray ? f.asArray().map(x => ctx.lookup(x)) : (f ? [f] : []);
  if(!list.length || !PC_ASCII_FILTERS.some(n => list[0] === pcN(n))) return null;
  const one = d.clone(ctx);
  one.set(pcN('Filter'), list[0]);
  one.delete(pcN('DecodeParms'));
  const plain = PDFLib.decodePDFRawStream(PDFRawStream.of(one, obj.contents)).decode();
  // (v97) an ASCII stream that decodes to nothing (a "<~" in front, cut off) stays as it is: it became an empty image and "99% smaller"
  if(!plain.length && obj.contents.length) throw Object.assign(new Error('ASCII stream decodes to nothing'), { name: 'InvalidPDFException' }); // a broken file, not a fault in the site
  const dp = d.lookup(pcN('DecodeParms'));
  const restParms = dp instanceof PDFArray ? dp.asArray().slice(1) : (dp && list.length > 1 ? [dp] : []);
  const nd = d.clone(ctx);
  pcRestOfFilters(ctx, nd, list.slice(1), restParms);
  nd.delete(pcN('Length'));
  return PDFRawStream.of(nd, plain);
}
/* ---------- (e-cmyk) CMYK JPEGs, decoded by pdf.js's own decoder ---------- */
/* The browser turns a CMYK JPEG into RGB its own way: more saturated than a
   PDF viewer (~25/255), and Chrome inverts every CMYK JPEG, so one without
   an Adobe marker became a negative. PcJpegImage (pc-jpeg.js, pdf.js's
   decoder) gives exactly what pdf.js shows. It is only in the worker build:
   on the page (no worker) CMYK photos stay as they are. */
const PC_CMYK_DECODE_LEN = 8;
const PC_DECODE_ONE = 256; // pdf.js's fixed-point 1.0 (JpegStream.jpegOptions)
const PC_8BIT_MAX = 255;
// ~8 bytes a pixel while decoding (4 x 16-bit): 25 MP (a 6000 x 4000 photo) is ~200 MB
// ponytail: fixed cap; decode in strips if larger CMYK photos turn up
const PC_CMYK_DECODE_PX = 25e6;
// pixels are sampled (nearest, as pdf.js does) at up to twice the new size; the canvas then scales smoothly
const PC_CMYK_OVERSAMPLE = 2;
/* /Decode of a CMYK JPEG the way pdf.js applies it: null = nothing to do,
   undefined = not 8 numbers (the image is left as it is). */
function pcCmykDecodeTransform(dec){
  if(!dec) return null;
  if(!(dec instanceof PDFLib.PDFArray) || dec.size() !== PC_CMYK_DECODE_LEN) return undefined;
  const v = Array.from({ length: PC_CMYK_DECODE_LEN }, (_, i) => { const n = dec.lookup(i); return n instanceof PDFLib.PDFNumber ? n.asNumber() : NaN; });
  if(!v.every(isFinite)) return undefined;
  const t = new Int32Array(PC_CMYK_DECODE_LEN);
  for(let i = 0; i < PC_CMYK_DECODE_LEN; i += 2){
    t[i] = (v[i + 1] - v[i]) * PC_DECODE_ONE | 0;
    t[i + 1] = v[i] * PC_8BIT_MAX | 0;
  }
  return t.every((x, i) => x === (i % 2 ? 0 : PC_DECODE_ONE)) ? null : t;
}
/* /DecodeParms /ColorTransform (YCCK or not), as pdf.js reads it; undefined = the JPEG decides. */
function pcJpegColorTransform(d){
  const dp = d.lookup(pcN('DecodeParms'));
  const ct = dp instanceof PDFLib.PDFDict ? dp.lookup(pcN('ColorTransform')) : null;
  return ct instanceof PDFLib.PDFNumber && Number.isInteger(ct.asNumber()) ? ct.asNumber() : undefined;
}
/* A CMYK JPEG as RGBA ImageData of about twice nw x nh, or null when it is
   not the w x h, 4-component picture the PDF says. Throws for a broken JPEG. */
function pcDecodeCmykJpeg(bytes, d, transform, w, h, nw, nh){
  const jpeg = new PcJpegImage({ decodeTransform: transform, colorTransform: pcJpegColorTransform(d) });
  jpeg.parse(bytes);
  if(jpeg.width !== w || jpeg.height !== h || jpeg.numComponents !== 4) return null;
  const dw = Math.min(w, nw * PC_CMYK_OVERSAMPLE), dh = Math.min(h, nh * PC_CMYK_OVERSAMPLE);
  return new ImageData(jpeg.getData({ width: dw, height: dh, isSourcePDF: true }), dw, dh);
}
/* ---------- (v119, k7-compress) How large each image is SHOWN ---------- */
/* Balanced and Strong used to cap an image at a fixed 2600/1700 px, whatever
   its size on the page: a 2400 px photo shown 8 cm wide on a slide kept all
   its pixels. The page contents are read (only q/Q/cm/Do and what to skip)
   to find the largest size at which each image is drawn; it is then kept at
   PC_DISPLAY_DPI for that size. Anything this reader can't follow leaves the
   images concerned at the old fixed cap (never smaller than shown): a filter
   it can't decode, an inline image without a clear end, an image that is
   also used by an annotation, a pattern, a soft mask or a Type 3 font. */
const PC_DISPLAY_DPI = { balanced: 200, strong: 144 };
const PC_POINTS_PER_INCH = 72;
const PC_SCAN_MAX_DEPTH = 8;      // forms inside forms
const PC_SHOWN_MIN_PX = 64;       // never scaled below this (longest side)
const PC_UNKNOWN = { w: Infinity, h: Infinity };
const PC_NUM_RE = /^[+-]?(\d+\.?\d*|\.\d+)$/;
function pcIsWs(c){ return c === 0 || c === 9 || c === 10 || c === 12 || c === 13 || c === 32; }
function pcIsDelim(c){ return c === 40 || c === 41 || c === 60 || c === 62 || c === 91 || c === 93 || c === 123 || c === 125 || c === 47 || c === 37; }
function pcMulMatrix(m, n){ // m then n (PDF row vectors: m x n)
  return [m[0] * n[0] + m[1] * n[2], m[0] * n[1] + m[1] * n[3], m[2] * n[0] + m[3] * n[2], m[2] * n[1] + m[3] * n[3],
    m[4] * n[0] + m[5] * n[2] + n[4], m[4] * n[1] + m[5] * n[3] + n[5]];
}
function pcToken(bytes, i, j){ let s = ''; for(let k = i; k < j; k++) s += String.fromCharCode(bytes[k]); return s; }
function pcNameOf(raw){ return raw.replace(/#([0-9A-Fa-f]{2})/g, (m, h) => String.fromCharCode(parseInt(h, 16))); }
/* After "ID": the end of the inline image's data ("EI" between white space), or -1. */
function pcInlineImageEnd(bytes, i){
  for(let k = i + 1; k + 2 <= bytes.length; k++){
    if(bytes[k] === 69 && bytes[k + 1] === 73 && pcIsWs(bytes[k - 1]) && (k + 2 === bytes.length || pcIsWs(bytes[k + 2]) || pcIsDelim(bytes[k + 2]))) return k + 2;
  }
  return -1;
}
/* Calls onDo(name, ctm) for every "/Name Do" of one content stream. Returns
   false when it met something it can't follow (the caller then gives up on
   this stream). */
function pcScanContent(bytes, ctm0, onDo){
  const stack = [];
  let ctm = ctm0, nums = [], name = null, i = 0;
  const n = bytes.length;
  while(i < n){
    const c = bytes[i];
    if(pcIsWs(c)){ i++; continue; }
    if(c === 37){ while(i < n && bytes[i] !== 10 && bytes[i] !== 13) i++; continue; } // comment
    if(c === 40){ // (string), nested parentheses and escapes
      let depth = 1; i++;
      while(i < n && depth){ const d = bytes[i]; if(d === 92) i += 2; else { if(d === 40) depth++; else if(d === 41) depth--; i++; } }
      nums = []; name = null; continue;
    }
    if(c === 60){ if(bytes[i + 1] === 60){ i += 2; continue; } while(i < n && bytes[i] !== 62) i++; i++; nums = []; name = null; continue; }
    if(c === 62){ i += bytes[i + 1] === 62 ? 2 : 1; continue; }
    if(c === 91 || c === 93 || c === 123 || c === 125 || c === 41){ i++; continue; }
    if(c === 47){ let j = i + 1; while(j < n && !pcIsWs(bytes[j]) && !pcIsDelim(bytes[j])) j++; name = pcNameOf(pcToken(bytes, i + 1, j)); i = j; continue; }
    let j = i; while(j < n && !pcIsWs(bytes[j]) && !pcIsDelim(bytes[j])) j++;
    const tok = pcToken(bytes, i, j); i = j;
    if(PC_NUM_RE.test(tok)){ nums.push(+tok); continue; }
    if(tok === 'q') stack.push(ctm);
    else if(tok === 'Q'){ if(stack.length) ctm = stack.pop(); }
    else if(tok === 'cm'){ if(nums.length < 6) return false; ctm = pcMulMatrix(nums.slice(-6), ctm); }
    else if(tok === 'Do'){ if(name) onDo(name, ctm); }
    else if(tok === 'ID'){ const end = pcInlineImageEnd(bytes, i); if(end < 0) return false; i = end; }
    nums = []; name = null;
  }
  return true;
}
/* The decoded bytes of a content stream, or null for a filter not handled here. */
async function pcContentBytes(obj){
  const { PDFArray } = PDFLib;
  const f = obj.dict.lookup(pcN('Filter'));
  const list = f instanceof PDFArray ? f.asArray() : (f ? [f] : []);
  if(!list.length) return obj.contents;
  if(list.length !== 1 || list[0] !== pcN('FlateDecode') || obj.dict.has(pcN('DecodeParms'))) return null;
  // (v119 review) the browser's inflate is strict (junk after the data, a cut-off stream): such a stream is "unknown", not a fault
  try{ return await pcInflate(obj.contents); }catch(e){ pcRethrowMemory(e); return null; }
}
function pcRecordShown(shown, tag, w, h){
  const old = shown.get(tag) || { w: 0, h: 0 };
  shown.set(tag, { w: Math.max(old.w, w), h: Math.max(old.h, h) });
}
/* Every image reachable from a resource dictionary through things this reader
   does not follow (patterns, soft masks, Type 3 fonts), or through all of it
   (whole=true: a stream that could not be read), is marked as unknown. */
function pcMarkUnknown(ctx, res, shown, whole, seen){
  const { PDFDict, PDFRawStream, PDFRef } = PDFLib;
  if(!(res instanceof PDFDict) || seen.has(res)) return;
  seen.add(res);
  const each = (key, fn) => { const d = res.lookup(pcN(key)); if(d instanceof PDFDict) d.entries().forEach(([, v]) => fn(v, ctx.lookup(v))); };
  // (a PDFDict keeps its entries in a field named `dict` too: test for a stream first)
  const resOf = o => o instanceof PDFLib.PDFStream ? o.dict.lookup(pcN('Resources')) : (o instanceof PDFDict ? o.lookup(pcN('Resources')) : null);
  if(whole) each('XObject', (ref, o) => {
    if(!(o instanceof PDFRawStream)) return;
    if(o.dict.get(pcN('Subtype')) === pcN('Image')){ if(ref instanceof PDFRef) shown.set(ref.tag, PC_UNKNOWN); }
    else pcMarkUnknown(ctx, resOf(o), shown, true, seen);
  });
  each('Pattern', (ref, o) => pcMarkUnknown(ctx, resOf(o), shown, true, seen));
  each('Font', (ref, o) => { if(o instanceof PDFDict && o.lookup(pcN('Subtype')) === pcN('Type3')) pcMarkUnknown(ctx, resOf(o), shown, true, seen); });
  each('ExtGState', (ref, o) => {
    const sm = o instanceof PDFDict ? o.lookup(pcN('SMask')) : null;
    const g = sm instanceof PDFDict ? sm.lookup(pcN('G')) : null;
    if(g) pcMarkUnknown(ctx, resOf(g), shown, true, seen);
  });
}
async function pcScanShown(ctx, contents, res, ctm, shown, depth, forms){
  const { PDFDict, PDFRawStream, PDFRef, PDFArray } = PDFLib;
  pcMarkUnknown(ctx, res, shown, false, new Set());
  const parts = [];
  for(const c of contents){
    const b = c instanceof PDFRawStream ? await pcContentBytes(c) : null;
    if(!b){ pcMarkUnknown(ctx, res, shown, true, new Set()); return; }
    parts.push(b, new Uint8Array([10]));
  }
  const all = new Uint8Array(parts.reduce((s, p) => s + p.length, 0));
  parts.reduce((at, p) => { all.set(p, at); return at + p.length; }, 0);
  const xobjs = res instanceof PDFDict ? res.lookup(pcN('XObject')) : null;
  const todo = [];
  const ok = pcScanContent(all, ctm, (name, m) => {
    const ref = xobjs instanceof PDFDict ? xobjs.get(pcN(name)) : null;
    const o = ref ? ctx.lookup(ref) : null;
    if(!(o instanceof PDFRawStream)) return;
    if(o.dict.get(pcN('Subtype')) === pcN('Image')){
      if(ref instanceof PDFRef) pcRecordShown(shown, ref.tag, Math.hypot(m[0], m[1]), Math.hypot(m[2], m[3]));
    }else todo.push({ ref, o, m });
  });
  if(!ok){ pcMarkUnknown(ctx, res, shown, true, new Set()); return; }
  for(const { ref, o, m } of todo){
    const key = ref instanceof PDFRef ? ref.tag : null;
    const ownRes = o.dict.lookup(pcN('Resources')) || res;
    if(depth >= PC_SCAN_MAX_DEPTH || (key && forms.has(key))){ pcMarkUnknown(ctx, ownRes, shown, true, new Set()); continue; }
    const fm = o.dict.lookup(pcN('Matrix'));
    const mm = fm instanceof PDFArray && fm.size() === 6 ? fm.asArray().map(x => { const v = ctx.lookup(x); return v instanceof PDFLib.PDFNumber ? v.asNumber() : NaN; }) : PC_IDENTITY;
    if(mm.some(v => !isFinite(v))){ pcMarkUnknown(ctx, ownRes, shown, true, new Set()); continue; } // (v119 review) a matrix it can't read
    if(key) forms.add(key);
    await pcScanShown(ctx, [o], ownRes, pcMulMatrix(mm, m), shown, depth + 1, forms);
    if(key) forms.delete(key);
  }
}
/* (v119) The images a page draws with Do, in drawing order (a form is followed
   where it is drawn, as pdf.js does), as { ref, obj }; image masks are left
   out (pdf.js paints those with another operator). null when the page can't
   be followed (a filter or inline image this reader can't handle). */
const PC_IDENTITY = [1, 0, 0, 1, 0, 0];
async function pcPageImageOrder(ctx, page){
  const { PDFArray, PDFDict, PDFRawStream, PDFRef } = PDFLib;
  const out = [];
  const walk = async (contents, res, depth, forms) => {
    const parts = [];
    for(const c of contents){
      const b = c instanceof PDFRawStream ? await pcContentBytes(c) : null;
      if(!b) return false;
      parts.push(b, new Uint8Array([10]));
    }
    const all = new Uint8Array(parts.reduce((s, p) => s + p.length, 0));
    parts.reduce((at, p) => { all.set(p, at); return at + p.length; }, 0);
    const names = [];
    if(!pcScanContent(all, PC_IDENTITY, name => names.push(name))) return false;
    const xobjs = res instanceof PDFDict ? res.lookup(pcN('XObject')) : null;
    for(const name of names){
      const ref = xobjs instanceof PDFDict ? xobjs.get(pcN(name)) : null;
      const o = ref ? ctx.lookup(ref) : null;
      if(!(o instanceof PDFRawStream)) continue;
      if(o.dict.get(pcN('Subtype')) === pcN('Image')){
        const im = o.dict.lookup(pcN('ImageMask'));
        if(!(im && im.toString() === 'true')) out.push({ ref, obj: o });
        continue;
      }
      const key = ref instanceof PDFRef ? ref.tag : null;
      if(depth >= PC_SCAN_MAX_DEPTH || (key && forms.has(key))) return false;
      if(key) forms.add(key);
      const ok = await walk([o], o.dict.lookup(pcN('Resources')) || res, depth + 1, forms);
      if(key) forms.delete(key);
      if(!ok) return false;
    }
    return true;
  };
  const c = page.node.Contents();
  const contents = c instanceof PDFArray ? c.asArray().map(x => ctx.lookup(x)) : (c ? [c] : []);
  return (await walk(contents, page.node.Resources(), 0, new Set())) ? out : null;
}
/* (v119) The stored JPEG of an image, when it can be saved as it is: a
   plain 8-bit RGB or grey JPEG without mask or /Decode (anything else needs
   the pixels pdf.js decoded). EXIF is removed (a viewer would turn the picture by it). */
function pcPlainJpegBytes(obj){
  const { PDFArray, PDFNumber } = PDFLib;
  const d = obj.dict;
  const f = d.lookup(pcN('Filter'));
  const list = f instanceof PDFArray ? f.asArray() : (f ? [f] : []);
  if(list.length !== 1 || list[0] !== pcN('DCTDecode')) return null;
  if(d.has(pcN('SMask')) || d.has(pcN('Mask')) || d.has(pcN('Decode')) || d.has(pcN('SMaskInData'))) return null;
  const bpc = d.lookup(pcN('BitsPerComponent'));
  if(!(bpc instanceof PDFNumber) || bpc.asNumber() !== 8) return null;
  const cs = d.lookup(pcN('ColorSpace'));
  let comps = cs === pcN('DeviceRGB') ? 3 : cs === pcN('DeviceGray') ? 1 : 0;
  if(cs instanceof PDFArray && cs.lookup(0) === pcN('ICCBased')){
    const icc = cs.lookup(1), n = icc && icc.dict ? icc.dict.lookup(pcN('N')) : null;
    comps = n instanceof PDFNumber ? n.asNumber() : 0;
  }
  if(comps !== 1 && comps !== 3) return null;
  return pcStripJpegExif(obj.contents);
}
/* ref tag -> { w, h }: the largest width and height (in points, along the
   image's own axes) at which an image is drawn on any page; PC_UNKNOWN when
   that can't be known. Images that are not in the map are left at the fixed cap. */
async function pcShownSizes(doc){
  const { PDFArray, PDFDict } = PDFLib;
  const ctx = doc.context, shown = new Map();
  for(const page of doc.getPages()){
    pcCheckCancel();
    const node = page.node;
    const c = node.Contents();
    const contents = c instanceof PDFArray ? c.asArray().map(x => ctx.lookup(x)) : (c ? [c] : []);
    const res = node.Resources();
    // (v119 review) /UserUnit: the page is shown larger than its points say; its images stay at the fixed cap
    const uu = node.lookup(pcN('UserUnit'));
    if(uu instanceof PDFLib.PDFNumber && uu.asNumber() !== 1){ pcMarkUnknown(ctx, res, shown, true, new Set()); continue; }
    try{ await pcScanShown(ctx, contents, res, [1, 0, 0, 1, 0, 0], shown, 0, new Set()); }
    catch(e){ pcRethrowMemory(e); if(pcIsCancel(e)) throw e; pcLogUnexpected(e); pcMarkUnknown(ctx, res, shown, true, new Set()); }
    // images in annotation looks are drawn at a size this reader does not work out
    const annots = node.lookup(pcN('Annots'));
    if(annots instanceof PDFArray) annots.asArray().forEach(a => {
      const ap = ctx.lookup(a) instanceof PDFDict ? ctx.lookup(a).lookup(pcN('AP')) : null;
      if(!(ap instanceof PDFDict)) return;
      ap.entries().forEach(([, v]) => {
        const o = ctx.lookup(v);
        const list = o instanceof PDFDict ? o.entries().map(([, x]) => ctx.lookup(x)) : [o];
        list.forEach(s => { if(s instanceof PDFLib.PDFStream) pcMarkUnknown(ctx, s.dict.lookup(pcN('Resources')), shown, true, new Set()); });
      });
    });
  }
  return shown;
}
/* The scale for an image of w x h that is shown at most `s` (points), at `dpi`; 1 when unknown. */
function pcShownScale(s, w, h, dpi){
  if(!s || !dpi || !isFinite(s.w) || !isFinite(s.h) || !(s.w > 0 || s.h > 0)) return 1;
  const need = Math.max(s.w * dpi / PC_POINTS_PER_INCH / w, s.h * dpi / PC_POINTS_PER_INCH / h);
  return Math.min(1, Math.max(need, PC_SHOWN_MIN_PX / Math.max(w, h)));
}
/* One image of pcCompressPdf as a smaller JPEG stream. Returns { stream } when
   that worked, { unsupported: true } when this kind of image can't be
   handled here (CMYK, indexed colours, 16-bit, a mask, too large, a picture
   the browser can't decode or encode...), and {} when it is fine as it is
   (already small, a soft mask, or Light leaves Flate images alone). */
const PC_IMG_NOT_SMALLER = {};
const PC_IMG_UNSUPPORTED = { unsupported: true };
/* (v90) Memory full is passed on as it is (the page turns it into the
   'out of memory' message); this core also runs in the worker, which has no
   pcThrowIfOutOfMemory. */
/* (v73, moved here from app.js in v94 so the worker and pcRethrowMemory use
   the same test) Running out of memory while a big result is built (a buffer
   that can't be allocated, a file the browser can no longer read, full
   storage) is not a broken file. */
const PC_OOM_NAMES = /^(NotReadableError|QuotaExceededError)$/;
const PC_OOM_TEXT = /out of memory|allocation failed|allocation size overflow/i;
// (v90) V8 says this when a buffer of that size can't be made (a huge result)
const PC_OOM_RANGE_TEXT = /invalid typed array length|invalid array buffer length|array buffer allocation failed/i;
function pcIsOutOfMemory(err){
  if(!err) return false;
  if(err instanceof RangeError && PC_OOM_RANGE_TEXT.test(String(err.message || ''))) return true;
  return PC_OOM_NAMES.test(err.name || '') || PC_OOM_TEXT.test(String(err.message || ''));
}
/* (v94) A full memory while compressing a picture is passed on (it used to
   become "these images can't be made smaller" for the narrower test here). */
function pcRethrowMemory(e){
  if(e && (e.name === 'RangeError' || pcIsOutOfMemory(e))) throw e;
}
/* A canvas the browser can't make (too large for its memory) gives no 2D
   context: Safari returns null instead of throwing. */
function pcCanvas2d(c){
  const g = c.getContext('2d');
  if(!g) throw new Error('Canvas allocation failed');
  return g;
}
function pcReleaseCanvas(c){ if(c){ c.width = 0; c.height = 0; } }
async function pcImageAsJpeg(ctx, ref, obj, filters, o){
  const { PDFRawStream, PDFNumber, PDFArray, PDFDict } = PDFLib;
  const d = obj.dict;
  // (v75) a soft mask (the transparency of another image) must stay
  // DeviceGray: as a colour JPEG it turned a cut-out picture into a
  // see-through one with black stripes (lung model, Leemans deck p. 21)
  if(o.softMasks.has(ref.tag)) return PC_IMG_NOT_SMALLER;
  const imgMask = d.lookup(pcN('ImageMask'));
  if(imgMask && imgMask.toString() === 'true') return PC_IMG_NOT_SMALLER;
  const bpcO = d.lookup(pcN('BitsPerComponent'));
  if(!(bpcO instanceof PDFNumber)) return PC_IMG_UNSUPPORTED;
  const bpc = bpcO.asNumber();
  // (v157) a stencil /Mask (a stream) stays as it is and maps onto the new picture; a colour-key mask can't survive JPEG
  if(d.has(pcN('SMaskInData')) || d.lookup(pcN('Mask')) instanceof PDFArray) return PC_IMG_UNSUPPORTED;
  const W = d.lookup(pcN('Width')), H = d.lookup(pcN('Height'));
  if(!(W instanceof PDFNumber) || !(H instanceof PDFNumber)) return PC_IMG_UNSUPPORTED;
  const w = W.asNumber(), h = H.asNumber();
  // colour space: DeviceRGB / DeviceGray / DeviceCMYK, or ICCBased with 1, 3 or 4 components;
  // (v157) or /Indexed on one of those (RGB or grey)
  let cs = d.lookup(pcN('ColorSpace'));
  const palette = pcIndexedPalette(cs);
  if(palette === undefined) return PC_IMG_UNSUPPORTED;
  let comps = palette ? palette.comps : pcColorComps(cs);
  if(!comps) return PC_IMG_UNSUPPORTED;
  const isJpeg = filters.length === 1 && filters[0] === '/DCTDecode';
  if(isJpeg ? bpc !== 8 || palette : PC_FLATE_BPC.indexOf(bpc) < 0 || (bpc === 16 && palette) || (bpc < 8 && !palette && comps !== 1)) return PC_IMG_UNSUPPORTED;
  // (v119, k7-compress; e-cmyk) a CMYK photo (print PDFs): only a JPEG, at
  // Balanced and Strong (Light keeps print colours); it becomes an RGB
  // (screen) JPEG with the colours pdf.js shows, and the page says so
  // ponytail: a Flate (lossless) CMYK image stays as it is; convert it by hand if such files turn up
  let cmykTransform = null;
  if(comps === 4){
    if(!isJpeg || !o.convertFlate || typeof PcJpegImage === 'undefined' || w * h > PC_CMYK_DECODE_PX) return PC_IMG_UNSUPPORTED;
    if(bpc !== 8) return PC_IMG_UNSUPPORTED;
    cmykTransform = pcCmykDecodeTransform(d.lookup(pcN('Decode')));
    if(cmykTransform === undefined) return PC_IMG_UNSUPPORTED;
  }
  // (v157) only the plain inversion ([1 0] per component) is handled; any other /Decode stays
  const invert = comps !== 4 && pcDecodeInverted(d.lookup(pcN('Decode')), palette ? 1 : comps);
  if(invert === undefined) return PC_IMG_UNSUPPORTED;
  if(invert && palette) return PC_IMG_UNSUPPORTED;
  if(isJpeg && invert) return PC_IMG_UNSUPPORTED;
  // (v157) a soft mask used by this image only is scaled along; with /Matte
  // it must have the same size, so an unreadable one keeps the picture as it is
  const sm = pcSoftMaskOf(ctx, d, o);
  if(sm === undefined) return PC_IMG_NOT_SMALLER;
  const isFlateOrRaw = filters.length === 0 || (filters.length === 1 && filters[0] === '/FlateDecode');
  if(!isJpeg && !isFlateOrRaw) return PC_IMG_UNSUPPORTED; // JPX, CCITT, JBIG2, LZW...
  if(!isJpeg && !o.convertFlate) return PC_IMG_NOT_SMALLER; // Light: lossless images stay lossless
  // (v119) and no more pixels than its largest size on the page needs (pcShownSizes)
  const scale = Math.min(1, o.maxSide / Math.max(w, h), Math.sqrt(o.MAX_PX / (w * h)), pcShownScale(o.shown && o.shown.get(ref.tag), w, h, o.dpi));
  if(sm && sm.matte && scale < 1 && !sm.canScale) return PC_IMG_NOT_SMALLER;
  const nw = Math.max(1, Math.round(w * scale)), nh = Math.max(1, Math.round(h * scale));
  let source = null; // canvas image source
  try{
    if(comps === 4){
      source = pcDecodeCmykJpeg(obj.contents, d, cmykTransform, w, h, nw, nh);
      if(!source) return PC_IMG_UNSUPPORTED;
    }else if(isJpeg){
      // (v89) decoded at full size before it is scaled down: a huge scan
      // could take a phone's tab down, so it is left as it is
      // ponytail: fixed cap; decode at reduced size if such scans matter
      if(w * h > o.MAX_JPEG_DECODE_PX) return PC_IMG_UNSUPPORTED;
      const bmp = await pcDecodeJpeg(obj.contents);
      if(!bmp) return PC_IMG_UNSUPPORTED;
      if(bmp.width !== w || bmp.height !== h){ bmp.close && bmp.close(); return PC_IMG_UNSUPPORTED; } // EXIF rotation applied, or odd file
      source = bmp;
    }else{
      const dp = d.lookup(pcN('DecodeParms'));
      const pred = dp instanceof PDFDict ? dp.lookup(pcN('Predictor')) : null;
      // (a Predictor only means something for a compressed image)
      const predictor = filters.length && pred instanceof PDFNumber ? pred.asNumber() : 1;
      if(predictor > 1 && predictor < 10) return PC_IMG_UNSUPPORTED; // TIFF predictor: rare, left alone
      // (v59) also an image stored without compression (no filter)
      let raw = filters.length ? await pcInflate(obj.contents) : obj.contents;
      const per = palette ? 1 : comps;
      raw = pcPngUnpredict(raw, w, per, bpc, predictor);
      if(raw.length < Math.ceil(w * per * bpc / 8) * h) return PC_IMG_UNSUPPORTED;
      if(w * h > o.MAX_PX) return PC_IMG_UNSUPPORTED;
      source = pcRawToImageData(raw, w, h, per, bpc, palette, invert);
    }
  }catch(e){ // left as it is (as before v90); memory full is not 'unsupported'; (v111) logged
    pcRethrowMemory(e);
    if(e && e.name !== 'JpegError') pcLogUnexpected(e); // (e-cmyk) a broken CMYK JPEG is the file, not a fault here
    return PC_IMG_UNSUPPORTED;
  }
  // (v94) the canvases are released (and the decoded picture closed) also
  // when drawing fails, e.g. a canvas the browser can't make that large
  const c = pcNewCanvas(nw, nh);
  let t = null, jpg;
  try{
    const g = pcCanvas2d(c);
    if(source instanceof ImageData){
      if(nw === source.width && nh === source.height) g.putImageData(source, 0, 0);
      else {
        t = pcNewCanvas(source.width, source.height);
        pcCanvas2d(t).putImageData(source, 0, 0);
        g.imageSmoothingQuality = 'high';
        g.drawImage(t, 0, 0, nw, nh);
      }
    }else{
      g.imageSmoothingQuality = 'high';
      g.drawImage(source.__img || source, 0, 0, nw, nh);
    }
    jpg = await pcCanvasToJpeg(c, o.q);
  }finally{
    pcReleaseCanvas(t);
    pcReleaseCanvas(c);
    if(!(source instanceof ImageData) && source.close) source.close();
  }
  if(!jpg) return PC_IMG_UNSUPPORTED; // this browser could not write the JPEG
  if(jpg.length >= obj.contents.length * 0.9) return PC_IMG_NOT_SMALLER;
  const nd = d.clone(ctx);
  nd.set(pcN('Filter'), pcN('DCTDecode'));
  nd.delete(pcN('DecodeParms'));
  nd.delete(pcN('Decode')); // (v119) the new RGB JPEG holds the colours as shown
  nd.set(pcN('Width'), PDFNumber.of(nw));
  nd.set(pcN('Height'), PDFNumber.of(nh));
  nd.set(pcN('BitsPerComponent'), PDFNumber.of(8));
  if(comps !== 3 || palette) nd.set(pcN('ColorSpace'), pcN('DeviceRGB')); // the browser always writes RGB JPEGs (v119: also from CMYK, whose profile no longer fits)
  if(comps === 4) o.cmyk++;
  nd.delete(pcN('Length'));
  if(sm && (nw !== w || nh !== h)){
    const smask = await pcScaledSoftMask(ctx, sm, nw, nh);
    if(smask){ ctx.assign(sm.ref, smask); o.replaced.add(sm.ref.tag); }
    else if(sm.matte) return PC_IMG_NOT_SMALLER; // /Matte needs both the same size
  }
  return { stream: PDFRawStream.of(nd, jpg) };
}
const PC_FLATE_BPC = [1, 2, 4, 8, 16];
function pcColorComps(cs){
  const { PDFArray, PDFNumber } = PDFLib;
  if(cs === pcN('DeviceRGB')) return 3;
  if(cs === pcN('DeviceGray')) return 1;
  if(cs === pcN('DeviceCMYK')) return 4;
  if(cs instanceof PDFArray && cs.lookup(0) === pcN('ICCBased')){
    const icc = cs.lookup(1);
    const n = icc && icc.dict ? icc.dict.lookup(pcN('N')) : null;
    const c = n instanceof PDFNumber ? n.asNumber() : 0;
    return c === 1 || c === 3 || c === 4 ? c : 0;
  }
  return 0;
}
/* (v157) /Indexed on RGB or grey: { comps, table, hival }; null when cs is
   not indexed; undefined when it is but can't be read here. */
function pcIndexedPalette(cs){
  const { PDFArray, PDFNumber, PDFString, PDFHexString, PDFRawStream } = PDFLib;
  if(!(cs instanceof PDFArray) || cs.lookup(0) !== pcN('Indexed')) return null;
  const comps = pcColorComps(cs.lookup(1));
  const hival = cs.lookup(2);
  if((comps !== 1 && comps !== 3) || !(hival instanceof PDFNumber)) return undefined;
  const lk = cs.lookup(3);
  let table = null;
  try{
    if(lk instanceof PDFHexString || lk instanceof PDFString) table = lk.asBytes();
    else if(lk instanceof PDFRawStream) table = PDFLib.decodePDFRawStream(lk).decode();
  }catch(e){ pcRethrowMemory(e); return undefined; }
  const n = hival.asNumber() + 1;
  if(!table || table.length < n * comps) return undefined;
  return { comps, table, hival: hival.asNumber() };
}
/* (v157) /Decode: false when absent or [0 1 ...], true for [1 0 ...], undefined for anything else. */
function pcDecodeInverted(dec, n){
  const { PDFArray } = PDFLib;
  if(dec === undefined) return false;
  if(!(dec instanceof PDFArray) || dec.size() !== 2 * n) return undefined;
  const v = dec.asArray().map(x => x.asNumber ? x.asNumber() : NaN);
  if(v.every((x, i) => x === (i % 2 ? 1 : 0))) return false;
  if(v.every((x, i) => x === (i % 2 ? 0 : 1))) return true;
  return undefined;
}
/* (v157) Unpacks 1/2/4/8/16-bit samples (grey, RGB or palette indices) to RGBA. */
function pcRawToImageData(raw, w, h, per, bpc, palette, invert){
  const id = new ImageData(w, h), px = id.data;
  const rowLen = Math.ceil(w * per * bpc / 8), max = (1 << Math.min(bpc, 8)) - 1;
  const sample = (row, k) => {
    if(bpc === 8) return raw[row + k];
    if(bpc === 16) return raw[row + 2 * k];
    const bit = k * bpc, byte = raw[row + (bit >> 3)];
    return (byte >> (8 - bpc - (bit & 7))) & max;
  };
  const scale = bpc < 8 ? 255 / max : 1;
  for(let y = 0, j = 0; y < h; y++){
    const row = y * rowLen;
    for(let x = 0; x < w; x++){
      let r, g, b;
      if(palette){
        const ix = Math.min(sample(row, x), palette.hival) * palette.comps, t = palette.table;
        if(palette.comps === 3){ r = t[ix]; g = t[ix + 1]; b = t[ix + 2]; } else r = g = b = t[ix];
      }else if(per === 3){
        r = sample(row, 3 * x) * scale; g = sample(row, 3 * x + 1) * scale; b = sample(row, 3 * x + 2) * scale;
      }else r = g = b = sample(row, x) * scale;
      if(invert){ r = 255 - r; g = 255 - g; b = 255 - b; }
      px[j++] = r; px[j++] = g; px[j++] = b; px[j++] = 255;
    }
  }
  return id;
}
/* (v157) The soft mask of an image: null when there is none, undefined when
   it is shared with other images and has /Matte (then this image can't be
   resized), else { ref, obj, matte, canScale }. */
function pcSoftMaskOf(ctx, d, o){
  const { PDFRef, PDFRawStream, PDFNumber } = PDFLib;
  const ref = d.get(pcN('SMask'));
  if(!(ref instanceof PDFRef)) return null;
  const obj = ctx.lookup(ref);
  if(!(obj instanceof PDFRawStream)) return null;
  const md = obj.dict, matte = md.has(pcN('Matte'));
  const shared = (o.softMasks.get(ref.tag) || 0) > 1;
  if(shared) return matte ? undefined : null;
  const filters = [].concat(md.lookup(pcN('Filter')) || []).map(f => f.asString ? f.asString() : '');
  const bpcO = md.lookup(pcN('BitsPerComponent')), dp = md.lookup(pcN('DecodeParms'));
  const canScale = (filters.length === 0 || (filters.length === 1 && filters[0] === '/FlateDecode'))
    && bpcO instanceof PDFNumber && bpcO.asNumber() === 8 && !md.has(pcN('Decode'))
    && (!dp || (filters.length && dp.lookup && !(dp.lookup(pcN('Predictor')) instanceof PDFNumber && dp.lookup(pcN('Predictor')).asNumber() > 1 && dp.lookup(pcN('Predictor')).asNumber() < 10)));
  return { ref, obj, matte, canScale };
}
/* (v157) The soft mask redrawn at nw x nh as an 8-bit grey Flate stream, or null. */
async function pcScaledSoftMask(ctx, sm, nw, nh){
  const { PDFNumber, PDFDict, PDFRawStream } = PDFLib;
  if(!sm.canScale) return null;
  const md = sm.obj.dict, MW = md.lookup(pcN('Width')), MH = md.lookup(pcN('Height'));
  if(!(MW instanceof PDFNumber) || !(MH instanceof PDFNumber)) return null;
  const mw = MW.asNumber(), mh = MH.asNumber();
  let src = null, dst = null;
  try{
    const filters = [].concat(md.lookup(pcN('Filter')) || []);
    const dp = md.lookup(pcN('DecodeParms'));
    const pred = dp instanceof PDFDict ? dp.lookup(pcN('Predictor')) : null;
    let raw = filters.length ? await pcInflate(sm.obj.contents) : sm.obj.contents;
    raw = pcPngUnpredict(raw, mw, 1, 8, filters.length && pred instanceof PDFNumber ? pred.asNumber() : 1);
    if(raw.length < mw * mh) return null;
    src = pcNewCanvas(mw, mh);
    pcCanvas2d(src).putImageData(pcRawToImageData(raw, mw, mh, 1, 8, null, false), 0, 0);
    dst = pcNewCanvas(nw, nh);
    const g = pcCanvas2d(dst);
    g.imageSmoothingQuality = 'high';
    g.drawImage(src, 0, 0, nw, nh);
    const px = g.getImageData(0, 0, nw, nh).data, grey = new Uint8Array(nw * nh);
    for(let i = 0; i < grey.length; i++) grey[i] = px[i * 4];
    const z = await pcDeflate(grey);
    if(!z) return null;
    const nd = md.clone(ctx);
    nd.set(pcN('Width'), PDFNumber.of(nw)); nd.set(pcN('Height'), PDFNumber.of(nh));
    nd.set(pcN('Filter'), pcN('FlateDecode')); nd.delete(pcN('DecodeParms')); nd.delete(pcN('Length'));
    return PDFRawStream.of(nd, z);
  }catch(e){ pcRethrowMemory(e); pcLogUnexpected(e); return null; }
  finally{ pcReleaseCanvas(src); pcReleaseCanvas(dst); }
}
/* An uncompressed stream deflated, or null when that doesn't save enough. */
async function pcDeflatedStream(ctx, obj){
  const z = await pcDeflate(obj.contents);
  if(!z || z.length >= obj.contents.length * 0.9) return null;
  const nd = obj.dict.clone(ctx);
  nd.set(pcN('Filter'), pcN('FlateDecode'));
  nd.delete(pcN('Length'));
  return PDFLib.PDFRawStream.of(nd, z);
}
/* level: 0.85 (light), 0.6 (balanced), 0.35 (strong) - the existing option values. */
async function pcCompressPdf(bytes, level, onProgress){
  const { PDFRawStream, PDFArray } = PDFLib;
  const original = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  const { doc, decrypted } = await openPdfForEdit(original, undefined, { keepXfa: true });
  const ctx = doc.context;
  const MAX_PX = 16e6;
  const o = {
    q: level >= 0.8 ? 0.82 : level >= 0.5 ? 0.7 : 0.5,
    maxSide: level >= 0.8 ? 1e9 : level >= 0.5 ? 2600 : 1700,
    convertFlate: level < 0.8,
    MAX_PX,
    MAX_JPEG_DECODE_PX: 4 * MAX_PX, // ~256 MB of pixels while decoding
    softMasks: pcSoftMaskRefs(ctx), replaced: new Set(), // (v157) soft masks already redrawn with their image
    dpi: level >= 0.8 ? 0 : level >= 0.5 ? PC_DISPLAY_DPI.balanced : PC_DISPLAY_DPI.strong, // (v119)
    shown: null, cmyk: 0,
  };
  if(o.dpi){
    try{ o.shown = await pcShownSizes(doc); }
    catch(e){ pcRethrowMemory(e); if(pcIsCancel(e)) throw e; pcLogUnexpected(e); } // the fixed cap still applies
  }
  const progress = pcThrottledProgress(onProgress);
  let imagesShrunk = 0;
  let imagesFound = 0; // (v86) none at all: the strength makes no difference (the page says so)
  let imagesUnsupported = 0; // (v90) images this tool can't make smaller (see pcImageAsJpeg)
  const flat = o2 => o2 instanceof PDFArray ? o2.asArray().map(x => ctx.lookup(x)) : (o2 ? [o2] : []);
  const all = ctx.enumerateIndirectObjects();
  for(let i = 0; i < all.length; i++){
    const [ref, found] = all[i];
    if(!(found instanceof PDFRawStream) || o.replaced.has(ref.tag)) continue;
    pcCheckCancel();
    progress(i / all.length * PC_COMPRESS_SCAN_SHARE);
    let obj = found, peeled = null;
    try{ peeled = pcPeelAsciiFilter(ctx, obj); }
    catch(e){ pcRethrowMemory(e); pcLogUnexpected(e); } // a broken ASCII stream stays as it is (an image is then counted as unsupported)
    if(peeled){ ctx.assign(ref, peeled); obj = peeled; }
    const d = obj.dict;
    const isImage = d.get(pcN('Subtype')) === pcN('Image');
    const filters = flat(d.lookup(pcN('Filter'))).map(f => f && f.asString ? f.asString() : '');
    if(!isImage){
      // uncompressed content/font/other streams: deflate them
      const type = d.get(pcN('Type'));
      if(!filters.length && obj.contents.length > 512 && type !== pcN('Metadata') && type !== pcN('XRef') && !d.has(pcN('DecodeParms'))){
        const z = await pcDeflatedStream(ctx, obj);
        if(z) ctx.assign(ref, z);
      }
      continue;
    }
    imagesFound++;
    if(obj.contents.length < 20000){ if(peeled) imagesShrunk++; continue; }
    // (v59) an image stored without any compression (some scanners and old
    // tools write them) gets the same JPEG treatment as a Flate image, and
    // otherwise at least lossless Flate
    const r = await pcImageAsJpeg(ctx, ref, obj, filters, o);
    if(r.stream){ ctx.assign(ref, r.stream); imagesShrunk++; continue; }
    const z = !filters.length && !d.has(pcN('DecodeParms')) ? await pcDeflatedStream(ctx, obj) : null;
    if(z){ ctx.assign(ref, z); imagesShrunk++; continue; }
    if(peeled) imagesShrunk++;
    else if(r.unsupported) imagesUnsupported++;
  }
  pcCollectGarbage(doc);
  progress(PC_COMPRESS_SCAN_SHARE);
  const outBytes = await pcSavePdf(doc, { useObjectStreams: true });
  // (v157) a damaged original (no xref, a wrong startxref, a broken page tree...) is never handed back:
  // pdf-lib repaired it while reading, and that copy is the one that opens everywhere
  const repaired = !pcOriginalIntact(original, doc);
  const kept = !repaired && outBytes.length >= original.length * 0.98;
  // (v96) a new copy breaks a certificate signature; the original handed back keeps it
  const certSigned = !kept && hasCertificateSignature(doc);
  return { bytes: kept ? original : outBytes, kept, repaired, decrypted, certSigned, imagesShrunk, imagesFound, imagesUnsupported, cmyk: kept ? 0 : o.cmyk };
}
/* (v157) Whether a viewer can open the file as it is: it ends with %%EOF and
   its last startxref points at an xref table or an xref stream object, and
   the load didn't have to repair anything (pcNormalizePageTree, pcResolveDuplicates). */
const PC_STARTXREF_RE = /^startxref\s+(\d+)\s+%%EOF/;
const PC_XREF_AT_RE = /^\s*(xref\b|\d+\s+\d+\s+obj\b)/;
function pcOriginalIntact(bytes, doc){
  if(doc.__pcRepaired || !pcHasEofMarker(bytes)) return false;
  const tail = pcAscii(bytes, bytes.length - PC_EOF_WINDOW, bytes.length);
  const m = PC_STARTXREF_RE.exec(tail.slice(Math.max(0, tail.lastIndexOf('startxref'))));
  const head = pcAscii(bytes, 0, PC_PDF_HEADER_WINDOW).indexOf('%PDF');
  if(!m || head < 0) return false;
  return PC_XREF_AT_RE.test(pcAscii(bytes, head + Number(m[1]), head + Number(m[1]) + PC_XREF_SUB_WINDOW));
}


/* (v90; v96 moved here from tools-extra.js, so Merge and Compress -- also
   in the worker -- can tell too) A PDF that carries a certificate-based digital signature: saving it
   again (pdf-lib writes the whole file) makes that signature invalid. The
   tools still work, but the visitor is told (one place for every tool that
   rewrites a PDF, here and in tools-more.js, which is loaded after this file).
   (v89) Read from the form's fields directly: doc.getForm() deleted the
   XFA data of hybrid forms and added an empty form to every signed PDF. */
function hasCertificateSignature(doc){
  const { PDFName: N, PDFDict: Dict, PDFArray: Arr } = PDFLib;
  try{
    const form = doc.catalog.lookup(N.of('AcroForm'));
    // each entry: a list of fields and the field type they inherit (/FT)
    const todo = form instanceof Dict ? [{ list: form.lookup(N.of('Fields')), ft: null }] : [], seen = new Set();
    while(todo.length){
      const { list, ft: inherited } = todo.pop();
      if(!(list instanceof Arr)) continue;
      for(let i = 0; i < list.size(); i++){
        const field = list.lookup(i);
        if(!(field instanceof Dict) || seen.has(field)) continue;
        seen.add(field);
        const ft = field.get(N.of('FT')) || inherited;
        if(ft === N.of('Sig') && field.get(N.of('V'))) return true;
        todo.push({ list: field.lookup(N.of('Kids')), ft });
      }
    }
    return false;
  }catch(e){ pcLogUnexpected(e); return false; }
}
/* ---------- (v68) compression jobs: the same code in the page and the worker ---------- */
/* A PDF compressed at `level`: { bytes, size, decrypted, noImages, imagesUnsupported }.
   bytes is null when nothing could be saved; the caller then hands back the
   original file unchanged (size is then the original size).
   (v90) imagesUnsupported: nothing was saved and the file has images this
   tool could not shrink at all -- the page then says so, instead of
   "already well optimized". */
async function pcCompressPdfFile(file, level, onProgress){
  const r = await pcCompressPdf(new Uint8Array(await file.arrayBuffer()), level, onProgress);
  return { bytes: r.kept ? null : r.bytes, size: r.bytes.length, repaired: r.repaired, decrypted: r.decrypted, certSigned: r.certSigned, noImages: r.imagesFound === 0,
    imagesUnsupported: r.kept && r.imagesShrunk === 0 && r.imagesUnsupported > 0, cmyk: r.cmyk };
}
/* (v90) A Word/PowerPoint file is a zip: a few MB can unpack to gigabytes (a
   "zip bomb", or a deck with a huge embedded video), which took the tab down
   without a word. The sizes the zip announces are added up right after
   opening, before anything is unpacked; above the limit the file is refused
   with a message. Lower on a phone or a device with little memory.
   (v97) A zip that lies about them is stopped while unpacking: no entry may
   unpack more than it announced (pcCapUnzip), so the real total stays under the limit. */
const PC_OFFICE_MAX_UNZIPPED = 1024 * 1024 * 1024;
const PC_OFFICE_MAX_UNZIPPED_SMALL = 300 * 1024 * 1024;
const PC_LOW_MEMORY_GB = 4;
/* (v90) The page passes its own answer to the worker (opt.unzipLimit): an
   iPad sends a Mac user agent, and only the page can see its touch screen. */
function pcOfficeUnzipLimit(){
  const nav = typeof navigator !== 'undefined' ? navigator : {};
  const ipad = nav.platform === 'MacIntel' && nav.maxTouchPoints > 1;
  const small = (nav.deviceMemory && nav.deviceMemory <= PC_LOW_MEMORY_GB) || ipad || /iPhone|iPad|iPod|Android/i.test(nav.userAgent || '');
  return small ? PC_OFFICE_MAX_UNZIPPED_SMALL : PC_OFFICE_MAX_UNZIPPED;
}
function pcUnzippedSize(zip){
  return Object.values(zip.files).reduce((n, f) => n + (f.dir ? 0 : (f._data && f._data.uncompressedSize) || 0), 0);
}
function pcOfficeTooLarge(limit){
  return pcUserError(pcT('officeTooLarge', Math.round((limit || pcOfficeUnzipLimit()) / (1024 * 1024))));
}
/* Opens a Word/PowerPoint file: { zip, unzipped } (unzipped = announced size
   in bytes). A file that isn't a zip throws InvalidOfficeFile, one too large
   for this device a translated message. `budget` (optional): bytes left. */
async function pcOpenOfficeZip(buf, budget, limit){
  let zip;
  try{ zip = await JSZip.loadAsync(buf); }
  catch(e){ throw Object.assign(new Error('Not a readable Office file'), { name: 'InvalidOfficeFile', cause: e }); } // (v89) a broken file, not a fault in the site
  const unzipped = pcUnzippedSize(zip);
  const cap = budget == null ? (limit || pcOfficeUnzipLimit()) : budget;
  if(unzipped > cap) throw pcOfficeTooLarge(limit);
  pcCapUnzip(zip, limit);
  return { zip, unzipped };
}
/* (v97) Every entry's async('string' | 'uint8array') unpacks as a stream and
   stops with the "too large" message as soon as it unpacks more than the zip
   announced for it (stopped at once: little memory used). */
function pcCapUnzip(zip, limit){
  Object.values(zip.files).forEach(entry => {
    if(entry.dir) return;
    const plain = entry.async.bind(entry);
    entry.async = type => (type !== 'string' && type !== 'uint8array') ? plain(type) : new Promise((resolve, reject) => {
      const chunks = [];
      const announced = (entry._data && entry._data.uncompressedSize) || 0;
      let stopped = false, own = 0;
      const stream = entry.internalStream('uint8array');
      stream.on('data', chunk => {
        if(stopped) return;
        own += chunk.length;
        if(own > announced){ stopped = true; stream.pause(); reject(pcOfficeTooLarge(limit)); return; }
        chunks.push(chunk);
      }).on('error', e => { if(!stopped){ stopped = true; reject(e); } })
        .on('end', () => {
          if(stopped) return;
          const bytes = new Uint8Array(chunks.reduce((n, c) => n + c.length, 0));
          chunks.reduce((o, c) => { bytes.set(c, o); return o + c.length; }, 0);
          resolve(type === 'string' ? new TextDecoder('utf-8').decode(bytes) : bytes);
        }).resume();
    });
  });
}
/* Compresses a Word or PowerPoint file by re-encoding the embedded JPEG
   images at a lower quality: { bytes, size, imagesProcessed, imagesUnsupported },
   bytes null when the result would not be smaller. PNGs are deliberately skipped:
   their file name would have to change to be rewritten as JPEG, and every
   reference to it would then also have to change -- that risk isn't worth
   the limited extra saving, and JPEGs are the main cause of a large file
   anyway. `quality` is read once by the caller: a click during the batch
   doesn't change it halfway. */
async function pcCompressOfficeFile(file, opt, onProgress){
  const quality = opt && typeof opt === 'object' ? opt.quality : opt;
  const limit = opt && typeof opt === 'object' ? opt.unzipLimit : null;
  const bytes = await file.arrayBuffer();
  const { zip } = await pcOpenOfficeZip(bytes, null, limit);
  const outZip = new JSZip();
  const progress = pcThrottledProgress(onProgress);
  let imagesProcessed = 0, imagesUnsupported = 0;
  const entries = Object.entries(zip.files);
  for(let i = 0; i < entries.length; i++){
    const [path, zipFile] = entries[i];
    pcCheckCancel(); // (v90) on the page (no worker) Cancel waited for the whole file
    progress(i / entries.length * PC_COMPRESS_SCAN_SHARE);
    if(zipFile.dir) continue;
    const data = await zipFile.async('uint8array');
    if(!/\/media\/.*\.jpe?g$/i.test(path)){ outZip.file(path, data); continue; }
    const smaller = await pcShrinkJpeg(data, quality);
    outZip.file(path, smaller instanceof Uint8Array ? smaller : data);
    if(smaller instanceof Uint8Array) imagesProcessed++;
    else if(smaller === PC_IMG_UNSUPPORTED) imagesUnsupported++;
  }
  progress(PC_COMPRESS_SCAN_SHARE);
  const out = await outZip.generateAsync({ type: 'uint8array', compression: 'DEFLATE', compressionOptions: { level: 6 } });
  if(out.length >= bytes.byteLength) return { bytes: null, size: bytes.byteLength, imagesProcessed: 0, imagesUnsupported: imagesProcessed === 0 && imagesUnsupported > 0 };
  return { bytes: out, size: out.length, imagesProcessed, imagesUnsupported: false };
}
/* (v90) Width and height from a JPEG's frame header (SOFn), or null: read
   before decoding, so a picture too large to handle is never decoded. */
const JPEG_SOF_MARKERS = new Set([0xFFC0, 0xFFC1, 0xFFC2, 0xFFC3, 0xFFC5, 0xFFC6, 0xFFC7, 0xFFC9, 0xFFCA, 0xFFCB, 0xFFCD, 0xFFCE, 0xFFCF]);
function pcJpegSize(bytes){
  const b = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  if(b.length < 4 || ((b[0] << 8) | b[1]) !== JPEG_SOI) return null;
  for(let o = 2; o + 9 <= b.length; ){
    if(b[o] !== 0xFF){ o++; continue; } // stray byte or fill
    const marker = (b[o] << 8) | b[o+1];
    if(marker === 0xFFFF){ o++; continue; } // fill bytes before a marker
    if(JPEG_SOF_MARKERS.has(marker)) return { height: (b[o+5] << 8) | b[o+6], width: (b[o+7] << 8) | b[o+8] };
    if(marker === JPEG_SOS) return null;
    o += 2 + ((b[o+2] << 8) | b[o+3]);
  }
  return null;
}
/* (v55) A JPEG from a Word/PowerPoint file re-encoded at `quality`: the new
   bytes; PC_IMG_NOT_SMALLER when that isn't smaller, PC_IMG_UNSUPPORTED
   (v90) when it can't be done here (the original then stays either way).
   The decoded picture and the canvas are released right away (a deck with
   many photos used to run the phone out of memory). */
const OFFICE_JPEG_MAX_PX = 16e6; // same limit as the PDF route (pcCompressPdf)
async function pcShrinkJpeg(data, quality){
  // a photo turned by its EXIF tag is shown turned by Office; the new JPEG
  // would lose that tag, so such a photo is left as it is
  if(pcJpegExifOrientation(data) > 1) return PC_IMG_UNSUPPORTED;
  // too big for a phone's canvas: kept as it is, and (v90) not even decoded
  const size = pcJpegSize(data);
  if(!size || size.width * size.height > OFFICE_JPEG_MAX_PX) return PC_IMG_UNSUPPORTED;
  const bmp = await pcDecodeJpeg(data);
  if(!bmp) return PC_IMG_UNSUPPORTED;
  if(bmp.width * bmp.height > OFFICE_JPEG_MAX_PX){ if(bmp.close) bmp.close(); return PC_IMG_UNSUPPORTED; }
  const canvas = pcNewCanvas(bmp.width, bmp.height);
  try{
    const ctx = pcCanvas2d(canvas); // (v94) no canvas = memory full: passed on, not 'unsupported'
    ctx.drawImage(bmp.__img || bmp, 0, 0);
    const out = await pcCanvasToJpeg(canvas, quality);
    // (v49) an empty encode (canvas too large / out of memory) must never
    // replace the real picture
    if(!out || out.length <= 100) return PC_IMG_UNSUPPORTED;
    return out.length < data.length ? out : PC_IMG_NOT_SMALLER;
  }catch(e){
    pcRethrowMemory(e); // (v94) a full memory is not "this picture can't be made smaller"
    // one picture that can't be re-encoded stays as it is; the rest of the
    // document is still compressed (as before v55)
    pcLogUnexpected(e);
    return PC_IMG_UNSUPPORTED;
  }finally{
    if(bmp.close) bmp.close();
    canvas.width = canvas.height = 0;
  }
}
/* ---------- (v113) moved here from app.js and tools-extra.js: the worker needs them too ---------- */
/* (v73) Running out of memory while a big result is built (a buffer that
   can't be allocated, a file or finished part the browser can no longer
   read, full storage) used to end in "Is it a valid PDF?", which sends the
   visitor looking for a fault in a file that is fine. */
/* (v89) A chosen file that was moved, renamed or deleted on the device
   before the tool read it: every browser rejects the read with a
   NotFoundError. It used to say "Is it a valid PDF?" about a file that is fine. */
function pcIsFileGone(err){ return !!err && err.name === 'NotFoundError'; }
/* (v90) pdf.js's own worker (pdf.worker.min.mjs) could not be loaded (no
   connection, a filter blocking .mjs, a server without that file type):
   pdf.js then says "Setting up fake worker failed". That is a missing part
   of the site, not a broken PDF. */
const PC_PDFJS_WORKER_FAILED = /fake worker failed/i;
function pcIsLibFailure(err){ return !!err && PC_PDFJS_WORKER_FAILED.test(String(err.message || '')); }
// The message for a failed read (file gone, memory full, a part of the site
// that could not load), else null
function pcReadFailureMessage(err){
  if(pcIsFileGone(err)) return pcT('fileGone');
  if(pcIsOutOfMemory(err)) return pcT('outOfMemory');
  if(pcIsLibFailure(err)) return pcT('libLoadFailed');
  return null;
}
// in a catch that would otherwise say "not a valid PDF"
function pcThrowIfOutOfMemory(err){ const m = pcReadFailureMessage(err); if(m) throw pcUserError(m); }

/* Gets the file name without ".pdf", so downloads keep the same name
   as the original file instead of a generic one. */
function baseFileName(name){
  return name.replace(/\.pdf$/i, '');
}

function pcFieldError(msg){ return Object.assign(pcUserError(msg), { field: true }); } // an error in what the visitor typed (see pcMarkField)
/* (v94) iOS and macOS turn a typed "-" into "–" or "—" (smart punctuation),
   and some keyboards give "−" (minus sign): every dash means a range. */
const PC_DASH_RE = /[\u2010-\u2015\u2212\uFE58\uFE63\uFF0D]/g;
function pcNormDashes(s){ return String(s || '').replace(PC_DASH_RE, '-'); }

function pcCheckPageExists(p, total){
  if(p >= 1 && p <= total) return;
  throw pcFieldError(total === 1 ? pcT('pageMissingOne', p) : pcT('pageMissing', p, total));
}

async function openForTool(file, passwordMsg){
  const bytes = new Uint8Array(await file.arrayBuffer());
  let r;
  try{
    r = await openPdfForEdit(bytes);
  }catch(err){
    if(pcIsCancel(err)) throw err;
    if(err && err.userFacing) throw err;
    if(err && err.name === 'PasswordException') throw Object.assign(pcUserError(passwordMsg), { pw: true }); // (v119) the page then asks for it
    pcThrowIfOutOfMemory(err); // (v73) a full memory is not a broken file
    pcLogUnexpected(err); // (v111) a fault of ours must not vanish into "is it a valid PDF?"
    throw pcUserError(pcT('errInvalidPdf'));
  }
  // (v90) every tool here writes the whole file again, which breaks a certificate signature
  return Object.assign({}, r, { certSigned: hasCertificateSignature(r.doc) });
}

/* v47: "1-3, 4-10" (ranges) makes exactly those parts; plain numbers
   ("3, 7") still mean "cut after page 3 and after page 7". Before v47 a
   range like "3-5" was silently read as "3". */
/* (v55) A range or cut point that falls outside the document is an error
   with the real page count ("page 5-7 on a 3-page PDF" used to be dropped
   silently, which turned "2, 5-7" into "only page 2" and lost pages 1 and 3). */
function parseSplitInput(str, total){
  const tokens = pcNormDashes(str).split(/[,;]/).map(s=>s.trim()).filter(Boolean);
  if(!tokens.length) return { mode: 'each' };
  const hasRange = tokens.some(t => t.includes('-'));
  if(!hasRange){
    if(tokens.some(t => !/^\d+$/.test(t))) throw pcFieldError(pcT('splitRange'));
    if(total < 2) throw pcUserError(pcT('splitOnePage'));
    // (v89) the checked tokens themselves: "2;4" used to give only a cut after 2
    const points = tokens.map(Number).sort((a,b)=>a-b);
    const bad = points.find(p => p < 1 || p >= total);
    if(bad !== undefined || !points.length) throw pcFieldError(pcT('splitPointRange', total - 1, total));
    return { mode: 'points', points };
  }
  const ranges = [];
  for(const t of tokens){
    const m = t.match(/^(\d*)\s*-\s*(\d*)$/);
    if(m && (m[1] || m[2])){
      let a = m[1] ? parseInt(m[1],10) : 1, b = m[2] ? parseInt(m[2],10) : total;
      pcCheckPageExists(a, total);
      pcCheckPageExists(b, total);
      if(a > b){ const x = a; a = b; b = x; }
      ranges.push([a-1, b-1]);
    }else if(/^\d+$/.test(t)){
      const n = parseInt(t,10);
      pcCheckPageExists(n, total);
      ranges.push([n-1, n-1]);
    }else throw pcFieldError(pcT('splitRange'));
  }
  return { mode: 'ranges', ranges };
}

/* (v157) "a new file every N pages": the last part may be shorter. */
function splitEveryRanges(every, total){
  if(every >= total) throw pcFieldError(pcT('splitEveryAll', total, every));
  const ranges = [];
  for(let s = 0; s < total; s += every) ranges.push([s, Math.min(s + every, total) - 1]);
  return ranges;
}
async function splitOnePdf(file, splitInput, onProgress, every){
  const { doc: donor, decrypted, certSigned } = await openForTool(file, pcT('errPwSplit'));
  const total = donor.getPageCount();
  const parsed = every ? { mode: 'every' } : parseSplitInput(splitInput, total);
  let ranges;
  if(parsed.mode === 'every'){
    ranges = splitEveryRanges(every, total);
  }else if(parsed.mode === 'each'){
    ranges = Array.from({length: total}, (_,i)=>[i,i]);
  }else if(parsed.mode === 'ranges'){
    ranges = parsed.ranges;
  }else{
    ranges = [];
    let start = 0;
    for(const p of parsed.points){
      const end = Math.min(p-1, total-1);
      if(end >= start){ ranges.push([start, end]); start = end+1; }
    }
    if(start <= total-1) ranges.push([start, total-1]);
  }

  const parts = [];
  for(let i=0;i<ranges.length;i++){
    pcCheckCancel();
    await onProgress(i / ranges.length);
    const [s,e] = ranges[i];
    const indices = [];
    for(let p=s;p<=e;p++) indices.push(p);
    const sub = await pcAssemble([{ doc: donor, indices }], { part: true });
    const outBytes = await pcSavePdf(sub);
    // (v157) zero-padded, so page-02 sorts before page-10 in every file manager
    const pad = n => String(n).padStart(String(total).length, '0');
    const rangeLabel = (s===e ? `page-${pad(s+1)}` : `pages-${pad(s+1)}-${pad(e+1)}`);
    parts.push({
      blob: new Blob([outBytes], {type:'application/pdf'}),
      filename: `${baseFileName(file.name)}-${rangeLabel}.pdf`,
      decrypted, certSigned, name: file.name, xfaHybrid: pcXfaKind(donor) === 'hybrid',
    });
  }
  return { parts, uncovered: uncoveredPages(ranges, total) };
}
/* (v90) Pages that are in none of the parts ("3, 5-7" on 10 pages leaves out
   1-2, 4 and 8-10): named in a note instead of being dropped silently. */
function uncoveredPages(ranges, total){
  const inPart = new Set();
  ranges.forEach(([s, e]) => { for(let p = s; p <= e; p++) inPart.add(p); });
  const out = [];
  for(let p = 0; p < total; p++) if(!inPart.has(p)) out.push(p + 1);
  return out;
}

/* (v113) Merge in one job, so it can run in the worker (pc-worker.js) as
   well as on the page. files: File objects (not copied). Returns the merged
   bytes and what the page needs for its texts; a file that can't be opened
   is listed with its reason, as before. */
async function pcMergePdfFiles(files, _opt, onProgress){
  const donors = [], skipped = [], decrypted = [];
  let needsPassword = false; // (v119) the page then asks for the password
  for(let i = 0; i < files.length; i++){
    const f = files[i];
    pcCheckCancel();
    try{
      // (an empty file or one without pages is refused by openPdfForEdit)
      const r = await openPdfForEdit(new Uint8Array(await f.arrayBuffer()));
      donors.push({ doc: r.doc, indices: r.doc.getPageIndices(), name: f.name });
      if(r.decrypted) decrypted.push(f.name);
    }catch(err){
      if(pcIsCancel(err)) throw err;
      pcLogUnexpected(err); // (v90) a real fault is logged, not only listed as "couldn't be opened"
      // (v49) say WHY when we know it
      if(err && err.name === 'PasswordException') needsPassword = true;
      const why = err && err.name === 'PasswordException' ? pcT('passwordShort')
        : err && err.name === 'InvalidPDFException' ? pcT('notPdfShort') // (v94) as Split says
        : err && err.message === pcT('xfa') ? pcT('xfaShort') // (v94) the full advice is too long for this list
        : err && err.userFacing ? err.message : pcReadFailureMessage(err); // (v89) a file that is gone
      skipped.push(f.name + ' (' + (why || pcT('notPdfShort')) + ')'); // (v111) never a bare name
    }
    if(onProgress) onProgress((i + 1) / files.length * 0.8);
  }
  // (v52) one usable file is no merge; (v94) with the reasons
  if(donors.length < 2) throw Object.assign(pcUserError(pcT('errMergeOpen', skipped.join(', '))), { pw: needsPassword });
  const merged = await pcAssemble(donors);
  pcCheckCancel();
  const bytes = await pcSavePdf(merged);
  const nameOf = d => donors.find(x => x.doc === d).name;
  const info = merged.__pcFormInfo || { renamedDonors: [], xfaDonors: [] };
  return {
    bytes, pages: merged.getPageCount(), firstName: donors[0].name, skipped, decrypted,
    certNames: [...new Set(donors.filter(d => hasCertificateSignature(d.doc)).map(d => d.name))],
    // plain names, so the note can be made on the page (pcAssembleNotes)
    formInfo: { renamedDonors: info.renamedDonors.map(nameOf), xfaDonors: info.xfaDonors.map(nameOf) },
  };
}
/* (v113) Split of one file as a job (see splitOnePdf). */
function pcSplitPdfFile(file, opt, onProgress){ return splitOnePdf(file, opt.input, onProgress, opt.every); }
/* (v113) Several results in one zip, as a job: 500 parts used to freeze the
   page for 4-7 s (Cancel did not respond). Stored, not compressed, as before
   (PDFs and pictures are compressed already). */
async function pcZipFiles(list){
  const zip = new JSZip();
  list.forEach(x => zip.file(x.name, x.blob));
  pcCheckCancel();
  return { bytes: await zip.generateAsync({ type: 'uint8array' }) };
}
/* ---------- (v119) Rotate, Page numbers and Delete pages (moved from tools-extra.js) and
   the page-list parser (from app.js): jobs, so they run in the worker too. ---------- */
const PAGE_LOOP_SHARE = 0.5; // Rotate, Page numbers: the page loop's share of one file on the bar; saving takes the rest
/* (v56) The page-list parser of Split/Rotate/Delete (tools-extra.js) and
   Extract (tools-more.js), which each had their own copy (and tools-more.js
   its own copy of the messages). Strict: "2, 5-7", "5-" (to the end), "-3"
   (from the start); stray commas are fine. A typo ("5 7") or a page that
   doesn't exist is an error with its own translated message, instead of
   being skipped silently (v55) -- which used to rotate or remove the wrong
   pages and still say "Done!". */
function pcParsePageSpec(str){
  const parts = pcNormDashes(str).split(/[,;]/).map(s => s.trim()).filter(Boolean);
  return parts.map(part => {
    const m = part.match(/^(\d*)\s*-\s*(\d*)$/);
    if(m && (m[1] || m[2])) return { from: m[1] ? parseInt(m[1], 10) : null, to: m[2] ? parseInt(m[2], 10) : null };
    if(/^\d+$/.test(part)){ const n = parseInt(part, 10); return { from: n, to: n }; }
    throw pcFieldError(pcT('pageBadInput', part));
  });
}
/* 1-based page numbers of a parsed spec, each once, in the order given.
   A range is counted upwards ("7-5" = 5, 6, 7), or -- with typedOrder, for
   Extract, where the order makes the new document -- as typed (7, 6, 5). */
function pcResolvePageSpec(spec, total, typedOrder){
  const out = [], seen = new Set();
  spec.forEach(r => {
    const a = r.from == null ? 1 : r.from, b = r.to == null ? total : r.to;
    pcCheckPageExists(a, total);
    pcCheckPageExists(b, total);
    const first = typedOrder ? a : Math.min(a, b), last = typedOrder ? b : Math.max(a, b);
    const step = first <= last ? 1 : -1;
    for(let p = first; p !== last + step; p += step) if(!seen.has(p)){ seen.add(p); out.push(p); }
  });
  return out;
}

async function rotateOnePdf(file, deg, selectionStr, onProgress){
  const { doc: donor, decrypted, certSigned } = await openForTool(file, pcT('errPwRotate'));
  const { degrees } = PDFLib;
  // empty (or only commas/spaces) = every page, as the field's label says
  const spec = pcParsePageSpec(selectionStr);
  const only = spec.length ? new Set(pcResolvePageSpec(spec, donor.getPageCount())) : null;
  const pages = donor.getPages();
  for(let i = 0; i < pages.length; i++){
    await onProgress(PAGE_LOOP_SHARE * i / pages.length);
    if(only && !only.has(i+1)) continue;
    const page = pages[i];
    let current = page.getRotation().angle;
    if(current % 90) current = 0; // (v89) like pcVisualFrame: viewers show any other /Rotate as 0
    page.setRotation(degrees(((current + deg) % 360 + 360) % 360));
  }
  await onProgress(PAGE_LOOP_SHARE);
  donor.setModificationDate(new Date());
  const outBytes = await pcSavePdf(donor);
  return {
    blob: new Blob([outBytes], {type:'application/pdf'}),
    filename: baseFileName(file.name) + '-rotated.pdf',
    decrypted, certSigned, name: file.name,
  };
}

const PC_NUM_SIZE = { small: 0.8, medium: 1, large: 1.5 };
const PC_NUM_COLOR = { grey: [0.35, 0.35, 0.35], black: [0, 0, 0], white: [1, 1, 1], blue: [0.1, 0.25, 0.65], red: [0.75, 0.1, 0.1] };
function formatPageNumber(format, pageNum, total){
  if(format === 'n-of-total') return pcT('pageOfTotal', pageNum, total);
  if(format === 'dash') return `- ${pageNum} -`;
  return String(pageNum);
}

/* v47: the number is placed at the bottom centre of the page AS THE
   READER SEES IT (pcStampText, app.part5.js): inside the crop box, relative
   to a media box that doesn't start at 0,0, and upright on pages with a
   /Rotate flag. Before v47 it used the media box and a fixed y = 18, so on
   a cropped page it fell outside the visible area, on a rotated page it
   ran sideways along an edge, and on a shifted media box it floated. The
   existing page content is wrapped in q ... Q first, like the watermark. */
async function addPageNumbersOnePdf(file, format, options, onProgress){
  const o = Object.assign({ position: 'bottom-center', start: 1, skipFirst: false, size: 'medium', color: 'grey', pages: [] }, options || {});
  const { doc, decrypted, certSigned } = await openForTool(file, pcT('errPwNumbers'));
  const { StandardFonts, rgb } = PDFLib;
  const font = await doc.embedFont(StandardFonts.Helvetica);
  const pages = doc.getPages();
  if(pages.length <= (o.skipFirst ? 1 : 0)) throw pcUserError(pcT('numbersNone'));
  // (v157) an optional page list; the numbers count the numbered pages (1, 2, 3 on pages 3-5)
  const only = o.pages && o.pages.length ? new Set(pcResolvePageSpec(o.pages, pages.length)) : null;
  const which = pages.map((_, i) => i).filter(i => !(o.skipFirst && i === 0) && (!only || only.has(i + 1)));
  if(!which.length) throw pcFieldError(pcT('numNoPages'));
  // "Page X of N": N is the last number that is printed
  const total = o.start + which.length - 1;
  const [vert, horiz] = o.position.split('-');
  const factor = PC_NUM_SIZE[o.size] || 1, c = PC_NUM_COLOR[o.color] || PC_NUM_COLOR.grey;
  for(let n = 0; n < which.length; n++){
    const i = which[n];
    await onProgress(PAGE_LOOP_SHARE * n / which.length);
    const page = pages[i];
    const text = formatPageNumber(format, o.start + n, total);
    const f = pcVisualFrame(page);
    // 10 pt on normal pages, smaller on very small pages (labels, A7); (v157) times the chosen size
    const size = Math.max(6, Math.min(10, Math.min(f.vw, f.vh) / 30)) * factor;
    const margin = Math.max(8, Math.min(18, f.vh * 0.03));
    pcWrapContent(doc, page);
    pcStampText(page, text, { font, size, color: rgb(c[0], c[1], c[2]),
      bottom: vert === 'top' ? f.vh - margin - size * 0.72 : margin,
      align: horiz || 'center', side: Math.max(18, Math.min(42, f.vw * 0.06)) });
  }
  await onProgress(PAGE_LOOP_SHARE);
  doc.setModificationDate(new Date());
  const outBytes = await pcSavePdf(doc);
  return {
    blob: new Blob([outBytes], {type:'application/pdf'}),
    filename: baseFileName(file.name) + '-numbered.pdf',
    decrypted, certSigned, name: file.name,
  };
}

const DELETE_STEPS = 3;
async function deletePagesOnePdf(file, selectionStr, onProgress){
  // (v111) an empty field is a field error before the file is opened (it was after a full parse of every file)
  const spec = pcParsePageSpec(selectionStr);
  if(!spec.length){
    throw pcFieldError(pcT('errDeleteNeed'));
  }
  const { doc: donor, decrypted, certSigned } = await openForTool(file, pcT('errPwDelete'));
  const total = donor.getPageCount();
  const toRemove = new Set(pcResolvePageSpec(spec, total));
  if(toRemove.size >= total){
    throw pcUserError(pcT('errDeleteAll'));
  }

  const keepIndices = [];
  for(let i = 0; i < total; i++){
    if(!toRemove.has(i+1)) keepIndices.push(i);
  }
  await onProgress(1 / DELETE_STEPS);
  const sub = await pcAssemble([{ doc: donor, indices: keepIndices }]);
  await onProgress(2 / DELETE_STEPS);
  const outBytes = await pcSavePdf(sub);
  return {
    blob: new Blob([outBytes], {type:'application/pdf'}),
    filename: baseFileName(file.name) + '-pages-removed.pdf',
    decrypted, certSigned, name: file.name, xfaHybrid: pcXfaKind(donor) === 'hybrid',
  };
}

function pcRotatePdfFile(file, opt, onProgress){ return rotateOnePdf(file, opt.deg, opt.pages, onProgress || pcNoProgress); }
function pcNumbersPdfFile(file, opt, onProgress){ return addPageNumbersOnePdf(file, opt.format, opt.options, onProgress || pcNoProgress); }
function pcDeletePdfFile(file, opt, onProgress){ return deletePagesOnePdf(file, opt.pages, onProgress || pcNoProgress); }
function pcNoProgress(){}
/* ---------- (v88) PAGES PER SHEET (hand-outs), (ww03) moved from tools-more.js: a job ----------
   Every page goes onto a new sheet as a form XObject (pdf-lib embedPages),
   so text stays sharp and selectable. Each page is shown as the reader
   sees it: its visible (crop) box and its /Rotate (pcVisualFrame). Comments
   and form fields are drawn from their own appearance into the page first,
   because a form XObject carries no annotations. */
const PC_NUP = {
  PAPER: { a4: [595.28, 841.89], letter: [612, 792] },
  MARGIN: 28.35,      // 10 mm around the sheet
  GAP: 14.17,         // 5 mm between pages
  GRID: { 2: [1, 2], 4: [2, 2], 6: [2, 3], 9: [3, 3] }, // columns x rows on a portrait sheet
  NOTES_ROWS: 3,
  LINE_GAP: 22.68,    // 8 mm between note lines
  LINE_WIDTH: 0.5,
  FRAME_WIDTH: 0.6,
  GREY: 0.62,
  ANNOT_HIDDEN: 2, ANNOT_NOVIEW: 32, // annotation flags (PDF 32000, 12.5.3)
  KEEP_KEYS: ['Type', 'Parent', 'Contents', 'Resources', 'MediaBox', 'CropBox', 'Rotate'],
  YIELD_EVERY: 25,    // pages between two looks at the Cancel button
  PREP_SHARE: 0.5     // part of the progress bar for preparing the pages
};
// (ww03) in the worker nothing needs to draw or see Cancel (the page ends the worker): no pauses there
function nupYield(){ return pcInBackground() ? Promise.resolve() : new Promise(function(r){ setTimeout(r, 0); }); }
/* Cells (bottom-left x, y, width, height), in reading order. */
function nupCells(W, H, cols, rows, byColumn){
  var cw = (W - 2 * PC_NUP.MARGIN - (cols - 1) * PC_NUP.GAP) / cols;
  var ch = (H - 2 * PC_NUP.MARGIN - (rows - 1) * PC_NUP.GAP) / rows;
  var cells = [];
  for(var k = 0; k < cols * rows; k++){
    var c = byColumn ? Math.floor(k / rows) : k % cols;
    var r = byColumn ? k % rows : Math.floor(k / cols);
    cells.push({ x: PC_NUP.MARGIN + c * (cw + PC_NUP.GAP), y: H - PC_NUP.MARGIN - (r + 1) * ch - r * PC_NUP.GAP, w: cw, h: ch });
  }
  return cells;
}
function nupFitScale(cell, aspect){ return Math.min(cell.w, cell.h * aspect); }
/* The sheet: size, orientation and cells. "auto" takes the orientation in
   which a page shaped like the first one is printed largest. */
function nupSheet(s, aspect){
  var P = PC_NUP.PAPER[s.paper];
  if(s.notes){
    var rows = nupCells(P[0], P[1], 1, PC_NUP.NOTES_ROWS, false);
    var half = (P[0] - 2 * PC_NUP.MARGIN - PC_NUP.GAP) / 2;
    return { W: P[0], H: P[1],
      cells: rows.map(function(r){ return { x: r.x, y: r.y, w: half, h: r.h }; }),
      notes: rows.map(function(r){ return { x: r.x + half + PC_NUP.GAP, y: r.y, w: half, h: r.h }; }) };
  }
  var g = PC_NUP.GRID[s.n];
  var portrait = { W: P[0], H: P[1], cells: nupCells(P[0], P[1], g[0], g[1], s.byColumn) };
  var landscape = { W: P[1], H: P[0], cells: nupCells(P[1], P[0], g[1], g[0], s.byColumn) };
  if(s.orient === 'portrait') return portrait;
  if(s.orient === 'landscape') return landscape;
  return nupFitScale(landscape.cells[0], aspect) > nupFitScale(portrait.cells[0], aspect) ? landscape : portrait;
}
/* Draws an embedded page into the box x, y, w, h (the page as SHOWN),
   turned by its /Rotate (clockwise in PDF, counter-clockwise in pdf-lib). */
function nupDrawPage(sheet, emb, rot, box){
  var s = (rot % 180 ? box.w / emb.height : box.w / emb.width);
  var a = -rot * Math.PI / 180, cos = Math.round(Math.cos(a)), sin = Math.round(Math.sin(a));
  var w = emb.width * s, h = emb.height * s;
  var xs = [0, w * cos, -h * sin, w * cos - h * sin], ys = [0, w * sin, h * cos, w * sin + h * cos];
  sheet.drawPage(emb, { x: box.x - Math.min.apply(null, xs), y: box.y - Math.min.apply(null, ys), xScale: s, yScale: s, rotate: PDFLib.degrees(-rot) });
}
function nupCenter(cell, vw, vh){
  var s = Math.min(cell.w / vw, cell.h / vh), w = vw * s, h = vh * s;
  return { x: cell.x + (cell.w - w) / 2, y: cell.y + (cell.h - h) / 2, w: w, h: h };
}
function nupNoteLines(sheet, box){
  var grey = PDFLib.rgb(PC_NUP.GREY, PC_NUP.GREY, PC_NUP.GREY);
  for(var y = box.y + box.h - PC_NUP.LINE_GAP; y >= box.y; y -= PC_NUP.LINE_GAP){
    sheet.drawLine({ start: { x: box.x, y: y }, end: { x: box.x + box.w, y: y }, thickness: PC_NUP.LINE_WIDTH, color: grey });
  }
}
/* An annotation's normal appearance stream (the one for its current state). */
function nupAppearanceRef(annot){
  var ap = annot.lookup(PDFLib.PDFName.of('AP'));
  if(!(ap instanceof PDFLib.PDFDict)) return null;
  var n = ap.get(PDFLib.PDFName.of('N'));
  var nObj = annot.context.lookup(n);
  if(nObj instanceof PDFLib.PDFDict && !(nObj instanceof PDFLib.PDFRawStream)){
    var as = annot.lookup(PDFLib.PDFName.of('AS'));
    n = as instanceof PDFLib.PDFName ? nObj.get(as) : null;
    nObj = n ? annot.context.lookup(n) : null;
  }
  if(!(nObj instanceof PDFLib.PDFRawStream || nObj instanceof PDFLib.PDFStream)) return null;
  return n instanceof PDFLib.PDFRef ? n : annot.context.register(nObj);
}
function nupNumbers(arr){
  if(!(arr instanceof PDFLib.PDFArray)) return null;
  var v = arr.asArray().map(function(x){ return x instanceof PDFLib.PDFNumber ? x.asNumber() : NaN; });
  return v.every(isFinite) ? v : null;
}
/* The matrix that maps an appearance onto its annotation rectangle
   (PDF 32000, 12.5.5): its BBox, transformed by its Matrix, fills Rect. */
function nupAppearanceMatrix(stream, rect){
  var bb = nupNumbers(stream.dict.lookup(PDFLib.PDFName.of('BBox')));
  var m = nupNumbers(stream.dict.lookup(PDFLib.PDFName.of('Matrix'))) || [1, 0, 0, 1, 0, 0];
  if(!bb || bb.length !== 4 || m.length !== 6) return null;
  var pts = [[bb[0], bb[1]], [bb[2], bb[1]], [bb[0], bb[3]], [bb[2], bb[3]]].map(function(p){ return [m[0] * p[0] + m[2] * p[1] + m[4], m[1] * p[0] + m[3] * p[1] + m[5]]; });
  var x0 = Math.min.apply(null, pts.map(function(p){ return p[0]; })), x1 = Math.max.apply(null, pts.map(function(p){ return p[0]; }));
  var y0 = Math.min.apply(null, pts.map(function(p){ return p[1]; })), y1 = Math.max.apply(null, pts.map(function(p){ return p[1]; }));
  var rx0 = Math.min(rect[0], rect[2]), rx1 = Math.max(rect[0], rect[2]), ry0 = Math.min(rect[1], rect[3]), ry1 = Math.max(rect[1], rect[3]);
  if(x1 - x0 <= 0 || y1 - y0 <= 0 || rx1 - rx0 <= 0 || ry1 - ry0 <= 0) return null;
  var sx = (rx1 - rx0) / (x1 - x0), sy = (ry1 - ry0) / (y1 - y0);
  return [sx, 0, 0, sy, rx0 - x0 * sx, ry0 - y0 * sy];
}
/* Draws the visible annotations of a page into its content, as a viewer
   shows them. Returns how many widgets (form fields) had no appearance. */
function nupFlattenAnnots(doc, page){
  var N = PDFLib.PDFName, ctx = doc.context;
  var annots = page.node.lookup(N.of('Annots'));
  if(!(annots instanceof PDFLib.PDFArray)) return 0;
  var ops = [], missing = 0;
  for(var i = 0; i < annots.size(); i++){
    var a = annots.lookup(i);
    if(!(a instanceof PDFLib.PDFDict)) continue;
    var sub = a.lookup(N.of('Subtype'));
    if(sub === N.of('Link') || sub === N.of('Popup')) continue;
    var f = a.lookup(N.of('F'));
    if(f instanceof PDFLib.PDFNumber && (f.asNumber() & (PC_NUP.ANNOT_HIDDEN | PC_NUP.ANNOT_NOVIEW))) continue;
    var ref = nupAppearanceRef(a), rect = nupNumbers(a.lookup(N.of('Rect')));
    // (v90) an invisible field (0×0, e.g. a certificate signature) has nothing to draw
    var invisible = rect && rect.length === 4 && (rect[2] - rect[0]) * (rect[3] - rect[1]) === 0;
    if(!ref || !rect){ if(sub === N.of('Widget') && nupHasValue(a) && !invisible) missing++; continue; }
    var stream = ctx.lookup(ref);
    var mtx = nupAppearanceMatrix(stream, rect);
    if(!mtx) continue;
    if(!stream.dict.get(N.of('Subtype'))) stream.dict.set(N.of('Subtype'), N.of('Form'));
    var name = page.node.newXObject('PcAnnot', ref);
    ops.push('q ' + mtx.map(function(v){ return +v.toFixed(5); }).join(' ') + ' cm /' + name.asString().slice(1) + ' Do Q');
  }
  if(ops.length){
    pcWrapContent(doc, page);
    page.node.addContentStream(ctx.register(ctx.stream(ops.join('\n'))));
  }
  return missing;
}
/* A filled-in field whose look a viewer draws itself (no look of its own,
   or /NeedAppearances true with an outdated look): pdf-lib draws it first.
   Returns false when a field could not be drawn (the note names the file). */
const PC_NUP_DRAWN_FIELDS = ['PDFTextField', 'PDFDropdown', 'PDFOptionList'];
function nupPrepareForm(doc){
  var acro = doc.catalog.lookup(PDFLib.PDFName.of('AcroForm'));
  if(!(acro instanceof PDFLib.PDFDict)) return true;
  var need = acro.lookup(PDFLib.PDFName.of('NeedAppearances'));
  var redrawAll = need instanceof PDFLib.PDFBool && need.asBoolean();
  var form, fields, font, ok = true;
  try{ form = doc.getForm(); fields = form.getFields(); }
  catch(e){ pcLogUnexpected(e); return false; } // (v111) the note says it; the cause goes to the console // a form pdf-lib can't read: its looks stay as they are, the note says so
  fields.forEach(function(f){
    try{
      var drawn = PC_NUP_DRAWN_FIELDS.some(function(t){ return PDFLib[t] && f instanceof PDFLib[t]; });
      if(!(redrawAll && drawn) && !f.needsAppearancesUpdate()) return;
      font = font || form.getDefaultFont();
      f.defaultUpdateAppearances(font);
    }catch(e){ pcLogUnexpected(e); ok = false; } // e.g. a value the standard font can't write: reported in the note
  });
  return ok;
}
/* A field's value can sit on the widget or on a parent field. */
function nupHasValue(annot){
  for(var d = annot, depth = 0; d instanceof PDFLib.PDFDict && depth < 32; d = d.lookup(PDFLib.PDFName.of('Parent')), depth++){
    var v = d.lookup(PDFLib.PDFName.of('V'));
    if(v) return !(v instanceof PDFLib.PDFName && v.asString() === '/Off');
  }
  return false;
}
/* Only what a page needs to be drawn goes along (no thumbnails, private
   editor data or the annotations already drawn into it). */
function nupStripPage(page){
  page.node.keys().forEach(function(k){ if(PC_NUP.KEEP_KEYS.indexOf(k.asString().slice(1)) < 0) page.node.delete(k); });
}
/* (v90) A page's transparency group (/Group: how its see-through parts are
   blended) goes onto the drawing of that page on the sheet. (/UserUnit, a
   page drawn at a larger scale, needs nothing: every page is fitted into
   its cell, and the unit does not change its shape.) */
async function nupKeepGroups(src, out, groups, embs){
  var copier = null;
  for(var k = 0; k < embs.length; k++){
    if(!(groups[k] instanceof PDFLib.PDFDict)) continue;
    copier = copier || PDFLib.PDFObjectCopier.for(src.context, out.context);
    await embs[k].embed(); // the drawing is made now instead of at save (pdf-lib does it only once)
    out.context.lookup(embs[k].ref).dict.set(PDFLib.PDFName.of('Group'), copier.copy(groups[k]));
  }
}
/* (ww03) Opens the file as loadPdfLib in tools-more.js did, with that file's
   own messages (msgs.password, msgs.invalid: the page's texts, sent along). */
/* A file for a tools-more.js job, opened exactly as loadPdfLib (tools-more.js) does,
   with that tool's own texts (msgs.password, msgs.invalid). */
async function pcOpenForMore(file, msgs){
  const bytes = new Uint8Array(await file.arrayBuffer());
  try{ return await openPdfForEdit(bytes); }
  catch(e){
    if(pcIsCancel(e)) throw e;
    if(e && e.userFacing) throw e; // e.g. an incomplete (cut-off) PDF: its own message
    pcThrowIfOutOfMemory(e); // a full memory is not a broken file
    pcLogUnexpected(e);
    const pw = !!e && e.name === 'PasswordException';
    throw Object.assign(pcUserError(pw ? msgs.password : msgs.invalid), pw ? { pw: true } : {}); // pw: the page asks for the password
  }
}
async function nupOnePdf(file, s, msgs, progress){
  const opened = await pcOpenForMore(file, msgs);
  var src = opened.doc;
  const certSigned = hasCertificateSignature(src); // before the pages are changed below (as loadPdfLib noted it)
  var pages = src.getPages();
  var missing = nupPrepareForm(src) ? 0 : 1;
  await src.flush(); // fonts pdf-lib added for field looks are only written now; the copy below needs them
  var frames = [], groups = [];
  for(var j = 0; j < pages.length; j++){
    if(j % PC_NUP.YIELD_EVERY === 0){ await nupYield(); pcCheckCancel(); progress(PC_NUP.PREP_SHARE * j / pages.length); }
    missing += nupFlattenAnnots(src, pages[j]);
    frames.push(pcVisualFrame(pages[j]));
    groups.push(pages[j].node.lookup(PDFLib.PDFName.of('Group')));
    nupStripPage(pages[j]);
  }
  var out = await PDFLib.PDFDocument.create({ updateMetadata: false });
  var title = (function(){ try{ return src.getTitle(); }catch(e){ return ''; } })();
  if(title) out.setTitle(title);
  out.setProducer('pdfcommunity.org (pdf-lib)');
  out.setCreationDate(new Date()); out.setModificationDate(new Date());
  var drawn = pages.map(function(p, i){ return p.node.Contents() ? i : -1; }).filter(function(i){ return i >= 0; });
  var embs = await out.embedPages(drawn.map(function(i){ return pages[i]; }), drawn.map(function(i){
    var b = frames[i].box; return { left: b.x, bottom: b.y, right: b.x + b.width, top: b.y + b.height };
  }));
  var embOf = {}; drawn.forEach(function(i, k){ embOf[i] = embs[k]; });
  await nupKeepGroups(src, out, drawn.map(function(i){ return groups[i]; }), embs);
  var layout = nupSheet(s, frames[0].vw / frames[0].vh);
  var grey = PDFLib.rgb(PC_NUP.GREY, PC_NUP.GREY, PC_NUP.GREY);
  var sheet = null;
  for(var i = 0; i < pages.length; i++){
    if(i % PC_NUP.YIELD_EVERY === 0) await nupYield(); // (v119) the page may draw (and see Cancel) on long files
    pcCheckCancel();
    var slot = i % layout.cells.length;
    if(slot === 0){
      sheet = out.addPage([layout.W, layout.H]);
      if(s.notes) layout.notes.forEach(function(b){ nupNoteLines(sheet, b); });
    }
    var box = nupCenter(layout.cells[slot], frames[i].vw, frames[i].vh);
    if(embOf[i]) nupDrawPage(sheet, embOf[i], frames[i].rot, box);
    if(s.frame || s.notes) sheet.drawRectangle({ x: box.x, y: box.y, width: box.w, height: box.h, borderColor: grey, borderWidth: PC_NUP.FRAME_WIDTH });
    progress(PC_NUP.PREP_SHARE + (1 - PC_NUP.PREP_SHARE) * (i + 1) / pages.length);
  }
  pcCheckCancel();
  var bytes = await pcSavePdf(out);
  var name = baseFileName(file.name) + (s.notes ? '-handout.pdf' : '-' + s.n + '-per-sheet.pdf');
  return { blob: new Blob([bytes], { type: 'application/pdf' }), filename: name, pages: pages.length, sheets: out.getPageCount(), formMissing: missing > 0,
    decrypted: !!opened.decrypted, certSigned: certSigned }; // (ww03) for the notes on the page
}
/* (ww03) Pages per sheet as a job (worker or page). opt: { s: settings, msgs }. */
function pcNupPdfFile(file, opt, onProgress){ return nupOnePdf(file, opt.s, opt.msgs, onProgress || pcNoProgress); }
/* (perf) Extract pages, Sign and Organize (tools-more.js) as jobs too: with a
   large file their pdf-lib work blocked the page 0.2-0.7 s. The page keeps
   reading the controls and writing the notes; flags tell it what to note. */
function pcPdfBlob(bytes){ return new Blob([bytes], { type: 'application/pdf' }); }
function pcMoreFlags(opened){
  return { decrypted: !!opened.decrypted, certSigned: hasCertificateSignature(opened.doc) };
}
/* opt: { spec (pcParsePageSpec), mode ('single'|'separate'), msgs } */
async function pcExtractPdfFile(file, opt){
  const opened = await pcOpenForMore(file, opt.msgs), donor = opened.doc;
  const flags = Object.assign(pcMoreFlags(opened), { xfaHybrid: pcXfaKind(donor) === 'hybrid' });
  const pages = pcResolvePageSpec(opt.spec, donor.getPageCount(), true);
  const base = baseFileName(file.name), results = [];
  // v47: pcAssemble keeps bookmarks, form fields, links and layers
  if(opt.mode !== 'separate'){
    const doc = await pcAssemble([{ doc: donor, indices: pages.map(p => p - 1) }], { part: true });
    results.push({ blob: pcPdfBlob(await pcSavePdf(doc)), filename: base + '-extracted.pdf' });
    return Object.assign(flags, { results });
  }
  for(const p of pages){
    pcCheckCancel();
    const one = await pcAssemble([{ doc: donor, indices: [p - 1] }], { part: true });
    // (v157) zero-padded: page-02 sorts before page-10 everywhere
    results.push({ blob: pcPdfBlob(await pcSavePdf(one)), filename: base + '-page-' + String(p).padStart(String(donor.getPageCount()).length, '0') + '.pdf' });
  }
  return Object.assign(flags, { results });
}
const PC_SIGN = {
  DATE_SIZE: 0.22,        // date text height as a fraction of the signature height...
  DATE_MIN_PT: 7, DATE_MAX_PT: 14,
  DATE_LINE: 1.3,         // the date's line height, in font sizes
  DATE_BASELINE: 1.05,    // the date's baseline below the picture, in font sizes
  DATE_GREY: 0.1
};
function pcClamp(v, lo, hi){ return Math.max(lo, Math.min(hi, v)); }
function pcSignDateSize(signatureHeightPt){ return pcClamp(signatureHeightPt * PC_SIGN.DATE_SIZE, PC_SIGN.DATE_MIN_PT, PC_SIGN.DATE_MAX_PT); }
/* A point in the page as it is SHOWN (u from the left, v from the top, in
   points, after its /Rotate) in the page's own coordinates. */
function pcShownToUser(box, rot, u, v){
  if(rot === 90) return { x: box.x + v, y: box.y + u };
  if(rot === 180) return { x: box.x + box.width - u, y: box.y + v };
  if(rot === 270) return { x: box.x + box.width - v, y: box.y + box.height - u };
  return { x: box.x + u, y: box.y + box.height - v };
}
/* at: the signature's place as fractions of the shown page { fx, fy, fw, aspect } */
function pcStampPage(page, png, font, dateText, at){
  const frame = pcVisualFrame(page); // (v89) visible box and a /Rotate viewers honour, as the preview shows them
  const box = frame.box, rot = frame.rot;
  const W = (rot === 90 || rot === 270) ? box.height : box.width, H = (rot === 90 || rot === 270) ? box.width : box.height;
  const w = at.fw * W, h = w * at.aspect;
  const size = pcSignDateSize(h), dateH = dateText ? size * PC_SIGN.DATE_LINE : 0;
  const u = pcClamp(at.fx * W, 0, Math.max(0, W - w)), v = pcClamp(at.fy * H, 0, Math.max(0, H - h - dateH));
  const corner = pcShownToUser(box, rot, u, v + h); // the picture's corner at the bottom left, as seen
  page.drawImage(png, { x: corner.x, y: corner.y, width: w, height: h, rotate: PDFLib.degrees(rot) });
  if(!dateText) return;
  const base = pcShownToUser(box, rot, u, v + h + size * PC_SIGN.DATE_BASELINE);
  page.drawText(dateText, { x: base.x, y: base.y, size, font, color: PDFLib.rgb(PC_SIGN.DATE_GREY, PC_SIGN.DATE_GREY, PC_SIGN.DATE_GREY), rotate: PDFLib.degrees(rot) });
}
/* opt: { png (bytes), dateText, all, page, at, msgs } */
async function pcSignPdfFile(file, opt){
  const opened = await pcOpenForMore(file, opt.msgs), doc = opened.doc;
  const flags = pcMoreFlags(opened);
  const png = await doc.embedPng(opt.png);
  const font = opt.dateText ? await doc.embedFont(PDFLib.StandardFonts.Helvetica) : null;
  const pages = doc.getPages();
  const targets = opt.all ? pages : [pages[Math.min(opt.page, pages.length) - 1]];
  targets.forEach(p => pcStampPage(p, png, font, opt.dateText, opt.at));
  doc.setModificationDate(new Date());
  // (v74) the field values are not touched, so their look is not redrawn
  // either: after a getForm() call pdf-lib would rebuild
  // every field's appearance on save, which changes how a filled-in form
  // looks and fails outright on rich-text fields ("not a valid PDF")
  const bytes = await pcSavePdf(doc, { updateFieldAppearances: false });
  return Object.assign(flags, { short: !opt.all && opt.page > pages.length, blob: pcPdfBlob(bytes), filename: baseFileName(file.name) + '-signed.pdf' });
}
const PC_ORG = { FULL: 360, A4: [595.28, 841.89], OPENED_SHARE: 0.3, BUILT_SHARE: 0.7 };
function pcOrgBlankSize(doc, plan, i){
  // a blank page gets the size of the page before it (or after it), as the reader sees it
  for(let d = 1; d < plan.length; d++){
    const near = [plan[i - d], plan[i + d]].filter(x => x && x.src)[0];
    if(near){ const f = pcVisualFrame(doc.getPage(near.src - 1)); return [f.vw, f.vh]; }
  }
  return PC_ORG.A4;
}
/* opt: { plan [{ src (1-based page, 0 = blank), turn }], total (pages the preview read), msgs } */
async function pcOrganizePdfFile(file, opt, onProgress){
  const progress = onProgress || pcNoProgress, plan = opt.plan;
  const opened = await pcOpenForMore(file, opt.msgs), donor = opened.doc;
  const flags = pcMoreFlags(opened);
  if(donor.getPageCount() !== opt.total) throw pcUserError(opt.msgs.changed); // pdf.js and pdf-lib read a different number of pages (damaged file)
  const sizes = plan.map((it, i) => (it.src ? null : pcOrgBlankSize(donor, plan, i)));
  const rots = plan.map(it => (it.src ? pcVisualFrame(donor.getPage(it.src - 1)).rot : 0));
  progress(PC_ORG.OPENED_SHARE);
  const out = await pcAssemble([{ doc: donor, indices: plan.filter(it => it.src).map(it => it.src - 1) }]);
  pcCheckCancel();
  plan.forEach((it, i) => { if(!it.src) out.insertPage(i, sizes[i]); });
  out.getPages().forEach((page, i) => page.setRotation(PDFLib.degrees((rots[i] + plan[i].turn) % PC_ORG.FULL)));
  progress(PC_ORG.BUILT_SHARE);
  pcCheckCancel();
  const bytes = await pcSavePdf(out);
  const info = out.__pcFormInfo; // for the notes pcAssembleNotes (app.js) writes on the page
  return Object.assign(flags, { blob: pcPdfBlob(bytes), filename: baseFileName(file.name) + '-organized.pdf',
    formRenamed: !!(info && info.renamedDonors.length), formXfa: !!(info && info.xfaDonors.length) });
}
/* The jobs the page can hand to the worker, by name. */
const PC_JOBS = { compressPdf: pcCompressPdfFile, compressOffice: pcCompressOfficeFile,
  mergePdf: pcMergePdfFiles, splitPdf: pcSplitPdfFile, zipFiles: pcZipFiles,
  rotatePdf: pcRotatePdfFile, numbersPdf: pcNumbersPdfFile, deletePdf: pcDeletePdfFile, // (v119)
  nupPdf: pcNupPdfFile, // (ww03)
  extractPdf: pcExtractPdfFile, signPdf: pcSignPdfFile, organizePdf: pcOrganizePdfFile }; // (perf)

/* TOOL FLATTEN (v147) */
/* Flatten PDF: form fields and comments become part of the page, so they can no
   longer be changed and look the same in every program. Each annotation's normal
   appearance (/AP /N) is drawn as a form XObject where a viewer shows it (PDF 32000
   12.5.5: BBox through /Matrix, then fitted onto /Rect), and the annotation goes.
   pdf-lib's form.flatten() is not used for the drawing: in 1.17 it ignores /Matrix,
   a BBox that doesn't start at 0,0, the Hidden flag and a page whose content leaves
   the transformation changed, and it throws on a widget with an empty /AP. pdf-lib
   only builds the appearances the file lacks (updateAppearances).
   What is drawn: an annotation shown on screen AND on paper. Hidden: removed (it is
   seen nowhere). Not printed or NoView (e.g. a "Print" button): left as it was, so
   neither the screen nor the paper changes; the page says so. Links, attachments
   and media stay as they are; a popup goes with its parent. */
const PC_FLAT = {
  HIDDEN: 2, PRINT: 4, NO_ROTATE: 16, NO_VIEW: 32, // annotation flags (/F)
  KEEP: new Set(['Link', 'FileAttachment', 'Sound', 'Movie', 'Screen', 'RichMedia', '3D']),
  PAGE_SHARE: 0.9, // the page loop's share of one file on the bar; saving takes the rest
  CANT_ENCODE: /cannot encode/i, // pdf-lib's message for a character the standard font lacks
};
function pcFlatNums(arr, n){
  const { PDFArray, PDFNumber } = PDFLib;
  if(!(arr instanceof PDFArray) || arr.size() !== n) return null;
  const out = [];
  for(let i = 0; i < n; i++){
    const v = arr.lookup(i);
    if(!(v instanceof PDFNumber)) return null;
    out.push(v.asNumber());
  }
  return out;
}
/* The normal appearance as an indirect form XObject, or null when there is none to draw. */
function pcFlatAppearance(ctx, annot){
  const { PDFDict, PDFStream, PDFRef, PDFName } = PDFLib;
  const ap = annot.lookup(pcN('AP'));
  if(!(ap instanceof PDFDict)) return null;
  let raw = ap.get(pcN('N'));
  let n = ctx.lookup(raw);
  if(n instanceof PDFDict){ // one appearance per state (check box, radio): the current one (/AS)
    const as = pcFlatState(annot, n);
    if(!as) return null;
    raw = n.get(as);
    n = ctx.lookup(raw);
  }
  if(!(n instanceof PDFStream)) return null;
  const bbox = pcFlatNums(n.dict.lookup(pcN('BBox')), 4);
  if(!bbox) return null;
  n.dict.set(pcN('Subtype'), pcN('Form')); // some writers leave it out; Do needs it
  return { ref: raw instanceof PDFRef ? raw : ctx.register(n), bbox, matrix: pcFlatNums(n.dict.lookup(pcN('Matrix')), 6) || PC_IDENTITY };
}
/* PDF 32000 12.5.5: the matrix that puts the appearance where a viewer shows it,
   or null when it covers no area. (v147) NoRotate on a rotated page: turned back
   around the rectangle's upper-left corner, so it stays upright for the reader. */
function pcFlatMatrix(app, rect, noRotateDeg){
  const [x0, y0, x1, y1] = app.bbox, m = app.matrix;
  const pts = [[x0, y0], [x1, y0], [x0, y1], [x1, y1]].map(([x, y]) => [m[0] * x + m[2] * y + m[4], m[1] * x + m[3] * y + m[5]]);
  const xs = pts.map(p => p[0]), ys = pts.map(p => p[1]);
  const bx = Math.min(...xs), by = Math.min(...ys), bw = Math.max(...xs) - bx, bh = Math.max(...ys) - by;
  if(!(bw > 0 && bh > 0)) return null;
  const rx = Math.min(rect[0], rect[2]), ry = Math.min(rect[1], rect[3]);
  const sx = Math.abs(rect[2] - rect[0]) / bw, sy = Math.abs(rect[3] - rect[1]) / bh;
  const a = [sx, 0, 0, sy, rx - bx * sx, ry - by * sy];
  if(!noRotateDeg) return a;
  const top = ry + Math.abs(rect[3] - rect[1]), rad = noRotateDeg * Math.PI / 180;
  const c = Math.cos(rad), s = Math.sin(rad);
  return pcMulMatrix(pcMulMatrix(a, [1, 0, 0, 1, -rx, -top]), [c, s, -s, c, rx, top]);
}
/* (v157) The state a check box or radio button shows: its /AS, or (when a
   writer left /AS out) the field's value /V as pdf.js and Acrobat use it, else Off. */
function pcFlatState(annot, states){
  const { PDFName } = PDFLib;
  const as = annot.get(pcN('AS'));
  if(as instanceof PDFName) return as;
  const v = pcFlatTerminalField(annot).lookup(pcN('V'));
  if(v instanceof PDFName && states.has(v)) return v;
  return states.has(pcN('Off')) ? pcN('Off') : null;
}
function pcFlatTerminalField(widget){
  const parent = widget.lookup(pcN('Parent'));
  return widget.has(pcN('T')) || !(parent instanceof PDFLib.PDFDict) ? widget : parent;
}
/* What happens to one annotation: 'keep', 'drop' (removed, not drawn) or 'draw'. */
function pcFlatAction(annot, type, opt, t){
  const { PDFNumber } = PDFLib;
  if(PC_FLAT.KEEP.has(type)) return 'keep';
  if(type === 'Widget' ? !opt.fields : !opt.annots) return 'keep';
  const f = annot.lookup(pcN('F'));
  const flags = f instanceof PDFNumber ? f.asNumber() : 0;
  if(flags & PC_FLAT.HIDDEN){ t.hidden++; return 'drop'; }
  if((flags & PC_FLAT.NO_VIEW) || !(flags & PC_FLAT.PRINT)){ t.screenOnly++; return 'keep'; }
  return 'draw';
}
function pcFlatOcName(page, oc){
  const { PDFDict } = PDFLib;
  const { Resources } = page.node.normalizedEntries();
  let props = Resources.lookup(pcN('Properties'));
  if(!(props instanceof PDFDict)){ props = page.doc.context.obj({}); Resources.set(pcN('Properties'), props); }
  const key = props.uniqueKey('PcFlatOC');
  props.set(key, oc);
  return key;
}
function pcFlatDrawOps(page, annot, app, rot){
  const L = PDFLib;
  const flags = annot.lookup(pcN('F'));
  const noRot = rot && (flags.asNumber() & PC_FLAT.NO_ROTATE) ? rot : 0;
  const mtx = pcFlatMatrix(app, pcFlatNums(annot.lookup(pcN('Rect')), 4) || [0, 0, 0, 0], noRot);
  if(!mtx) return [];
  const name = page.node.newXObject('PcFlat', app.ref);
  const draw = [L.pushGraphicsState(), L.concatTransformationMatrix(...mtx), L.drawObject(name), L.popGraphicsState()];
  const oc = annot.get(pcN('OC')); // on a layer: only where that layer is shown
  if(!oc) return draw;
  return [L.PDFOperator.of(L.PDFOperatorNames.BeginMarkedContentSequence, [pcN('OC'), pcFlatOcName(page, oc)]), ...draw, L.endMarkedContent()];
}
/* One page: draws, keeps or removes its annotations; `kept` collects the widgets that stay. */
function pcFlattenPage(doc, page, opt, failed, t, kept, fieldSet){
  const { PDFArray, PDFDict, PDFName } = PDFLib;
  const annots = page.node.lookup(pcN('Annots'));
  if(!(annots instanceof PDFArray) || !annots.size()) return;
  let rot = ((page.getRotation().angle % 360) + 360) % 360;
  if(rot % 90) rot = 0; // as pcVisualFrame
  const ops = [], keep = [], popups = [], gone = new Set();
  for(let i = 0; i < annots.size(); i++){
    const a = annots.lookup(i);
    if(!(a instanceof PDFDict)) continue; // a null or broken entry: viewers skip it too
    const st = a.lookup(pcN('Subtype')), type = st instanceof PDFName ? st.decodeText() : '';
    if(type === 'Popup'){ popups.push([annots.get(i), a]); continue; }
    let act = pcFlatAction(a, type, opt, t);
    // (v157) a field whose value couldn't be drawn again stays fillable, as the page says: its stored look may show an old value
    if(act === 'draw' && type === 'Widget' && failed instanceof Set && failed.has(pcFlatTerminalField(a))) act = 'keep';
    const app = act === 'draw' ? pcFlatAppearance(doc.context, a) : null;
    if(act === 'draw' && !app){
      // a widget without an appearance shows nothing (an empty signature field, the "off" state of a check box) ...
      const widgetFailed = type === 'Widget' && failed.has(pcFlatTerminalField(a));
      if(type !== 'Widget' || widgetFailed){ act = 'keep'; if(!widgetFailed) t.noAp++; } // ... a comment without one can't be drawn
      else act = 'drop';
    }
    if(act === 'keep'){ keep.push(annots.get(i)); if(type === 'Widget') kept.add(a); continue; }
    gone.add(a);
    if(act === 'drop') continue;
    ops.push(...pcFlatDrawOps(page, a, app, rot));
    if(type === 'Widget') fieldSet.add(pcFlatTerminalField(a)); else t.annots++;
  }
  // a popup goes with its parent; one whose parent stays, stays
  popups.forEach(([ref, p]) => { const parent = p.lookup(pcN('Parent')); if(parent instanceof PDFDict && !gone.has(parent)) keep.push(ref); });
  if(keep.length === annots.size()) return;
  if(ops.length){ pcWrapContent(doc, page); page.pushOperators(...ops); }
  if(keep.length) page.node.set(pcN('Annots'), doc.context.obj(keep)); else page.node.delete(pcN('Annots'));
}
/* Appearances pdf-lib can build for fields that lack one (or all text and choice
   fields when the form asks the viewer to build them: /NeedAppearances). A field
   whose value the standard font can't show stays fillable: in the returned set. */
function pcFlatBuildAppearances(doc, t){
  const L = PDFLib, failed = new Set();
  const acro = doc.catalog.lookup(pcN('AcroForm'));
  if(!(acro instanceof L.PDFDict)) return failed;
  // (pdf-lib's getForm drops an XFA part itself, with a console warning: done here first; the page notes it)
  acro.delete(pcN('XFA'));
  const need = acro.lookup(pcN('NeedAppearances')) === L.PDFBool.True;
  let font = null;
  const fontOnce = () => font || (font = doc.embedStandardFont(L.StandardFonts.Helvetica));
  let fields;
  try{ fields = doc.getForm().getFields(); }
  catch(e){ // a form pdf-lib can't read: every field without a look of its own stays, the page says so
    pcLogUnexpected(e);
    t.formError = true;
    return { has: () => true };
  }
  for(const field of fields){
    if(field instanceof L.PDFSignature || field instanceof L.PDFButton) continue; // nothing to build: drawn as they are
    const widgets = field.acroField.getWidgets();
    const toggle = field instanceof L.PDFCheckBox || field instanceof L.PDFRadioGroup;
    const lacks = widgets.some(w => { const n = w.getAppearances() && w.getAppearances().normal; return toggle ? !n : !(n instanceof L.PDFStream); });
    if(!lacks && (toggle || !need)) continue;
    try{ toggle ? field.defaultUpdateAppearances() : field.defaultUpdateAppearances(fontOnce()); }
    catch(e){
      if(!PC_FLAT.CANT_ENCODE.test(String(e && e.message))) pcLogUnexpected(e);
      failed.add(field.acroField.dict);
      t.failed.push(field.getName());
    }
  }
  return failed;
}
/* After the pages: the form keeps only the fields that still have a widget on a page.
   No field left: the whole form goes (with its usage rights). */
function pcFlatPruneFields(doc, kept){
  const L = PDFLib, ctx = doc.context, cat = doc.catalog;
  const acro = cat.lookup(pcN('AcroForm'));
  if(!(acro instanceof L.PDFDict)) return false;
  const seen = new Set();
  const live = (list, depth) => {
    const out = [];
    if(!(list instanceof L.PDFArray) || depth > PC_MAX_FIELD_DEPTH) return out;
    for(let i = 0; i < list.size(); i++){
      const node = list.lookup(i);
      if(!(node instanceof L.PDFDict) || seen.has(node)) continue;
      seen.add(node);
      const kids = node.lookup(pcN('Kids'));
      if(kids instanceof L.PDFArray){
        const k = live(kids, depth + 1);
        if(k.length){ node.set(pcN('Kids'), ctx.obj(k)); out.push(list.get(i)); }
      }else if(kept.has(node)) out.push(list.get(i));
    }
    return out;
  };
  const fields = live(acro.lookup(pcN('Fields')), 0);
  cat.delete(pcN('NeedsRendering')); // an XFA flag: the XFA part is gone (pcFlatBuildAppearances)
  if(!fields.length){ cat.delete(pcN('AcroForm')); cat.delete(pcN('Perms')); return; }
  acro.set(pcN('Fields'), ctx.obj(fields));
}
/* (v157) Only the form fields of an open document, with the same drawing as
   Flatten (Fill's "flatten the form afterwards" used pdf-lib's form.flatten,
   which throws on an empty signature field). Returns the names of the fields
   that stay fillable. The caller saves (pcSavePdf collects the garbage). */
function pcFlatFormFields(doc){
  const t = { annots: 0, hidden: 0, screenOnly: 0, noAp: 0, failed: [], formError: false };
  const opt = { fields: true, annots: false };
  const failed = pcFlatBuildAppearances(doc, t);
  const kept = new Set(), fieldSet = new Set();
  doc.getPages().forEach(page => pcFlattenPage(doc, page, opt, failed, t, kept, fieldSet));
  pcFlatPruneFields(doc, kept);
  return { fields: fieldSet.size, failed: t.failed, formError: t.formError };
}
async function pcFlattenPdfFile(file, opt, onProgress){
  const progress = onProgress || pcNoProgress;
  const { doc, decrypted, certSigned } = await openForTool(file, pcT('errPwUse'));
  const xfaGone = opt.fields && pcXfaKind(doc) === 'hybrid'; // flattened fields: the XFA version of the form goes
  const t = { annots: 0, hidden: 0, screenOnly: 0, noAp: 0, failed: [], formError: false };
  const failed = opt.fields ? pcFlatBuildAppearances(doc, t) : new Set();
  const kept = new Set(), fieldSet = new Set(), pages = doc.getPages();
  for(let i = 0; i < pages.length; i++){
    pcCheckCancel();
    await progress(PC_FLAT.PAGE_SHARE * i / pages.length);
    pcFlattenPage(doc, pages[i], opt, failed, t, kept, fieldSet);
  }
  if(opt.fields) pcFlatPruneFields(doc, kept);
  pcCollectGarbage(doc); // the removed fields and comments really leave the file
  await progress(PC_FLAT.PAGE_SHARE);
  doc.setModificationDate(new Date());
  // updateFieldAppearances: false, or pdf-lib tries the fields it couldn't build again
  const outBytes = await pcSavePdf(doc, { updateFieldAppearances: false });
  return { blob: new Blob([outBytes], { type: 'application/pdf' }), filename: baseFileName(file.name) + '-flat.pdf',
    decrypted, certSigned, name: file.name, xfaHybrid: xfaGone,
    fields: fieldSet.size, annots: t.annots, hidden: t.hidden, screenOnly: t.screenOnly, noAp: t.noAp, failedFields: t.failed, formError: t.formError };
}
PC_JOBS.flattenPdf = pcFlattenPdfFile;
/* /TOOL FLATTEN */
